import React, { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  Loader2, Plus, X, Save, RotateCcw, Send, Maximize2, Minimize2,
  Check, Trash2, Magnet, Undo2, Redo2,
} from 'lucide-react';
import FlashingDescriptionAdminDialog from './FlashingDescriptionAdminDialog';
import PenetrationEditPanel, { PenetrationInlineCreate } from './PenetrationEditPanel';


// Iter230b — constants + pure helpers extracted to ./annotator/constants.js
// (PIN_TYPES, TYPE_LOOKUP, RIDGE_CAP_TYPES, FLASHING_TYPES, AQUASEAL_DEFAULTS,
// genId, calcIronRidgePieces, etc.). The annotator's primary file now
// focuses on canvas + state + dialog rendering.
import {
  PIN_TYPES, OUTLET_DIAMETERS, OUTLET_SHAPES, RIDGE_CAP_TYPES,
  STD_RIDGE_CAP_MM, RIDGE_CAP_LOOKUP, IRON_RIDGE_MAX_M, IRON_RIDGE_LAP_M,
  calcIronRidgePieces, ridgeRoofKey, ridgeRoofLabel, getRidgeOptionsForRoof,
  defaultRidgeCapForRoof, FLASHING_TYPES, DOWNPIPE_STORIES, DOWNPIPE_MATERIALS,
  DOWNPIPE_BRACKETS, TYPE_LOOKUP, parseAquasealRange, AQUASEAL_DEFAULTS,
  unitForPin, LABEL_PRESETS, genId,
} from './annotator/constants';

// Iter230b — legacy pre-Iter229v helper components (BacktrayInlineAdd,
// PipeUsageFlashingBlock, LinkedFlashingBlock) extracted to their own
// file. Still mounted inside Valley/Barge/Outlet dialogs until those
// are migrated to the unified Penetration cascade.
import { BacktrayInlineAdd, PipeUsageFlashingBlock, LinkedFlashingBlock } from './annotator/LegacyFlashingBlocks';
import { PlanPitchedFields } from '@/components/PlanPitchedFields';
import { PITCHED_PIN_TYPES, toPitched } from '@/utils/pitch';

const isRidging = (t) => t === 'Ridge' || t === 'Hip';  // Ridge + Hip share caps and the Ridging total


export default function RoofDiagramAnnotator({ appraisalId, templateId, initialPins = [], onChange, slot, roofProfile, roofType = '', pinTypeLabelOverrides = {}, forceRidgeCapCount = false, onPushedToComponents, hidePush = false }) {
  const { api } = useAuth();
  const [pitchDeg, setPitchDeg] = useState(null);
  useEffect(() => {
    if (!appraisalId) return;
    api.get(`/appraisals/${appraisalId}/pitch`).then(r => setPitchDeg(r.data?.pitch_deg || null)).catch(() => {});
  }, [api, appraisalId]);

  const containerRef = useRef(null);
  // Resolve a pin-type's display label/colour, honouring per-slot overrides
  // (e.g. Roofing slot renders Spouting pins as "Perimeter" in black because
  // roofers don't do gutter work). Pin data itself is unchanged.
  const typeLabel = (t) => pinTypeLabelOverrides[t] || t;
  const typeColour = (t) => {
    if (pinTypeLabelOverrides[t]) return '#0F172A';   // forced black for renames
    return (TYPE_LOOKUP[t] || {}).color || '#475569';
  };
  // Iter211 — Ridge caps only exist on Concrete / Clay / Asbestos roofs.
  // Iron, Tin Tile, Butynol, Polycarbonate roofs use flashing instead of
  // discrete caps, so the operator should NOT see a cap-qty chip on those
  // Ridge pins. `forceRidgeCapCount` (set by the Concrete Restore slot)
  // overrides this — that slot is by definition for cap-bearing jobs.
  const showRidgeCapQty = (() => {
    if (forceRidgeCapCount) return true;
    const rt = String(roofType || '').trim().toLowerCase();
    if (!rt) return true;  // unknown roof type → fall back to legacy behaviour
    return rt.includes('concrete') || rt.includes('clay') || rt.includes('asbestos');
  })();
  // When `slot` is provided, we only edit pins tagged for THIS slot. Legacy
  // pins (no slot field) default to 'roof_plan'. The rest of the pin list
  // (other slots) is preserved verbatim on every save so trade-specific
  // annotations don't get wiped when another slot is being edited.
  const filterToSlot = (arr) => (slot
    ? (arr || []).filter(p => (p.slot || 'roof_plan') === slot)
    : (arr || []));
  const otherSlotPins = (arr) => (slot
    ? (arr || []).filter(p => (p.slot || 'roof_plan') !== slot)
    : []);
  const [pins, setPins] = useState(filterToSlot(initialPins));
  // Iter211 — Undo/Redo for annotated diagram edits.
  // A "snapshot" captures BOTH the pin array AND the appraisal-local
  // freehand strokes so a single user action that touches both (e.g.
  // tracing a stroke also seeds its pin) reverts atomically.
  // Snapshots are deep-cloned via JSON to avoid aliasing. Stack capped
  // at 30 entries to prevent unbounded growth. Both stacks are wiped
  // when the active slot or template changes — history belongs to a
  // particular canvas, not the editor instance.
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const [undoCount, setUndoCount] = useState(0);
  const [redoCount, setRedoCount] = useState(0);
  // Refs that always mirror current state so undo helpers can read the
  // freshest values without stale closures (React keyboard handlers
  // bind `pins` from an old render otherwise).
  const pinsRef = useRef(pins);
  const localStrokesRef = useRef([]);
  useEffect(() => { pinsRef.current = pins; }, [pins]);
  const cloneSnap = () => {
    try {
      return {
        pins: JSON.parse(JSON.stringify(pinsRef.current || [])),
        localStrokes: JSON.parse(JSON.stringify(localStrokesRef.current || [])),
      };
    } catch { return { pins: [], localStrokes: [] }; }
  };
  const pushUndoSnapshot = (clearRedo = true) => {
    undoStackRef.current.push(cloneSnap());
    if (undoStackRef.current.length > 30) undoStackRef.current.shift();
    setUndoCount(undoStackRef.current.length);
    if (clearRedo) {
      redoStackRef.current = [];
      setRedoCount(0);
    }
  };
  const pushRedoSnapshot = () => {
    redoStackRef.current.push(cloneSnap());
    if (redoStackRef.current.length > 30) redoStackRef.current.shift();
    setRedoCount(redoStackRef.current.length);
  };
  // Clear undo/redo whenever the active slot or template changes —
  // the history belongs to a particular canvas, not the editor.
  useEffect(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    setUndoCount(0);
    setRedoCount(0);
  }, [slot, templateId]);
  const [editingPin, setEditingPin] = useState(null);
  // Iter229v — inline "+ Create new Penetration" form open state +
  // tracker of the pin it was opened against. Render-phase reset
  // when the edited pin changes so the form never bleeds across
  // different Flashing pins (avoids set-state-in-effect).
  const [showInlinePenetrationCreate, setShowInlinePenetrationCreate] = useState(false);
  const [inlinePenetrationPinId, setInlinePenetrationPinId] = useState(null);
  if ((editingPin?.id || null) !== inlinePenetrationPinId) {
    setInlinePenetrationPinId(editingPin?.id || null);
    if (showInlinePenetrationCreate) setShowInlinePenetrationCreate(false);
  }
  // Iter229o — admin-editable Flashing Descriptions list. Loaded on
  // mount; refreshed when the inline admin pop-out makes a change.
  // The Pin.Flashing dialog reads `flashingDescriptions` for its
  // <select> options instead of the hardcoded list.
  const [flashingDescriptions, setFlashingDescriptions] = useState([]);
  const [showDescAdmin, setShowDescAdmin] = useState(false);
  const reloadFlashingDescriptions = useCallback(async () => {
    if (!api) return;
    try {
      const r = await api.get('/flashing-descriptions');
      setFlashingDescriptions(Array.isArray(r?.data) ? r.data : []);
    } catch (e) {
      // Non-fatal — fall back to the seeded default list if the API
      // is unavailable so the operator isn't blocked.
      console.warn('flashing-descriptions load failed', e);
    }
  }, [api]);
  useEffect(() => { reloadFlashingDescriptions(); }, [reloadFlashingDescriptions]);
  // Iter229s — when the operator opens a Ridge pin that doesn't yet
  // carry a `ridge_cap_type` (pinned before the Iter227 cascade
  // existed, or imported), auto-stamp the roof-type-appropriate
  // default ("RoundTop Std" on Iron, "Standard Concrete" on
  // concrete, etc.) so the dropdown shows the right value instead
  // of "— no cap (skip) —". The user can still pick a different
  // option; this just removes the "blank" first state.
  useEffect(() => {
    if (!editingPin) return;
    if (!isRidging(editingPin.type)) return;
    if (editingPin.ridge_cap_type) return;
    const fallback = defaultRidgeCapForRoof(roofType);
    if (!fallback) return;
    setEditingPin({ ...editingPin, ridge_cap_type: fallback });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingPin?.id, editingPin?.type, roofType]);
  const [savedFlash, setSavedFlash] = useState('');
  const [pushing, setPushing] = useState(false);
  const [pushFlash, setPushFlash] = useState(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dragging, setDragging] = useState(null); // pin id currently being dragged
  const dragMoved = useRef(false);
  // Last-used pin "template" — captured every time the operator saves a
  // pin. A double-click on an empty area of the diagram drops a new pin
  // pre-populated with these values (type, length, cap settings, etc.)
  // so the office can rattle through repetitive measurements without
  // re-picking the type/cap every time. Cleared by clicking "Reset".
  const [lastPinPreset, setLastPinPreset] = useState(null);
  // Pin currently flashing because the builder asked us to focus it
  // (via the `rtr:focus-pin` CustomEvent). Cleared 2.5s after focus.
  const [flashPinId, setFlashPinId] = useState(null);
  // Iter161 Phase B — Leak-pin drop mode. When the Remedial Repairs
  // builder dispatches `rtr:start-leak-pin-drop` with {leak_area_id,
  // label}, the annotator enters "single-shot" mode: the NEXT pointer
  // click on the diagram drops a LeakArea pin tagged with that meta,
  // emits `rtr:leak-pin-placed` so the builder can persist the
  // pin_id ↔ leak_area_id link, and exits the mode. ESC also exits.
  const [leakDropMode, setLeakDropMode] = useState(null);
  // Iter182 — vector strokes for the underlying template. Loaded once
  // per templateId; renders as invisible interactive overlays so the
  // operator can click on a hip / ridge / gutter / barge line and the
  // dropped pin auto-types to that segment's role. Also drives the
  // hover-highlight which signposts "this line is a Hip" etc.
  const [strokes, setStrokes] = useState([]);
  const [hoveredStroke, setHoveredStroke] = useState(null); // index into strokes
  // Iter237 — `imgNaturalAspect` RESTORED. Without it, the canvas
  // forces a fixed 4:3 aspect, but cropped images come out at the
  // roof's natural aspect (usually NOT 4:3). The PNG then letter-
  // boxes inside the 4:3 canvas while the SVG overlay stretches to
  // fill — re-creating the doubled-line bug. By matching the canvas
  // to the image's actual aspect, the PNG fills the canvas without
  // letterboxing and SVG strokes sit exactly on top.
  //
  // What WAS deleted: `canvasAspectOverride` (the manual stretch
  // drag-handle), `showPinLines` toggle, halo masking, stale-pin
  // wipe — those workarounds are still gone.
  const [imgNaturalAspect, setImgNaturalAspect] = useState(null);
  const [localStrokes, setLocalStrokes] = useState([]);
  useEffect(() => { localStrokesRef.current = localStrokes; }, [localStrokes]);
  const [drawMode, setDrawMode] = useState('pin'); // 'pin' | 'point' | 'straight' | 'polygon'
  const [defaultLineType, setDefaultLineType] = useState('ridge');
  const [activePoly, setActivePoly] = useState(null); // { points: [{x_pct,y_pct}] } during polygon build
  const [activeLineStart, setActiveLineStart] = useState(null); // {x_pct,y_pct} for straight mode
  const [cursorPct, setCursorPct] = useState(null); // {x_pct,y_pct} for live preview while drawing
  // Zoom & pan controls for "best fit". 1 = native template aspect.
  const [zoom, setZoom] = useState(1);
  // Iter182+ — guard against re-seeding the starter pin set on every
  // re-render. We only auto-generate pins the FIRST time a template
  // with strokes loads against an empty pin list for this appraisal.
  const starterSeededRef = useRef(false);
  useEffect(() => { starterSeededRef.current = false; setImgNaturalAspect(null); }, [templateId, appraisalId, slot]);
  useEffect(() => {
    if (!templateId) { setStrokes([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/roof-templates/${templateId}`);
        // Iter182++ — Trace-Template strokes are stored in image-pixel
        // coords (`pt.x`/`pt.y`); the annotator's geometry helpers
        // (polylineMidpoint, polylineLengthPct, snap, etc.) all work
        // in percent coords (`pt.x_pct`/`pt.y_pct`). Convert at load
        // time using the template's intrinsic image dimensions so
        // every downstream calculation Just Works™ regardless of the
        // template's natural pixel size. Legacy templates that already
        // ship % coords are passed through unchanged.
        const W = Math.max(1, Number(data?.intrinsic_width)  || Number(data?.image_width)  || Number(data?.width)  || 0);
        const H = Math.max(1, Number(data?.intrinsic_height) || Number(data?.image_height) || Number(data?.height) || 0);
        const raw = Array.isArray(data?.strokes) ? data.strokes : [];
        const normalised = raw.map((s) => {
          const pts = Array.isArray(s.points) ? s.points : [];
          // Already percent? (legacy / hand-authored). Detect via the
          // presence of x_pct OR a coordinate range firmly inside 0–100.
          const firstHasPct = pts[0] && (pts[0].x_pct !== undefined || pts[0].y_pct !== undefined);
          const maxAbs = pts.reduce((m, p) => Math.max(m, Math.abs(p.x || 0), Math.abs(p.y || 0)), 0);
          const isPercent = firstHasPct || (W <= 1 && H <= 1 && maxAbs <= 100);
          if (isPercent) {
            return {
              ...s,
              points: pts.map(p => ({ x_pct: p.x_pct ?? p.x, y_pct: p.y_pct ?? p.y })),
            };
          }
          // Pixel coords → convert to %. Falls back to dividing by the
          // largest observed coord when the template doesn't expose its
          // intrinsic dimensions (older save format).
          const fallbackW = W > 1 ? W : Math.max(1, ...pts.map(p => Math.abs(p.x || 0)), 1000);
          const fallbackH = H > 1 ? H : Math.max(1, ...pts.map(p => Math.abs(p.y || 0)), 750);
          return {
            ...s,
            points: pts.map(p => ({
              x_pct: ((p.x || 0) / fallbackW) * 100,
              y_pct: ((p.y || 0) / fallbackH) * 100,
            })),
          };
        });
        setStrokes(normalised);
      } catch { setStrokes([]); }
    })();
  }, [api, templateId]);

  // Iter184 — load appraisal-local strokes (drawn via the in-canvas tracer
  // tools). Persisted on the appraisal so other appraisals using the same
  // template are unaffected.
  useEffect(() => {
    if (!appraisalId) { setLocalStrokes([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/appraisals/${appraisalId}`);
        const raw = Array.isArray(data?.roof_diagram_strokes) ? data.roof_diagram_strokes : [];
        setLocalStrokes(raw);
      } catch { setLocalStrokes([]); }
    })();
  }, [api, appraisalId]);

  // Iter184 — ESC cancels any in-progress polygon / straight draw and
  // exits draw mode. Lets the operator bail without dropping a stray
  // stroke.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (activePoly) setActivePoly(null);
        if (activeLineStart) setActiveLineStart(null);
        if (drawMode !== 'pin') setDrawMode('pin');
      }
      // Iter211 — Ctrl+Z / ⌘+Z undoes the last pin/stroke change.
      // Ctrl+Shift+Z / ⌘+Shift+Z (or Ctrl+Y) re-applies the last undone
      // change. Ignored when focus is inside an input/textarea so the
      // operator's typing isn't intercepted.
      if ((e.key === 'z' || e.key === 'Z') && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        const ae = document.activeElement;
        const tag = (ae?.tagName || '').toLowerCase();
        const isEditable = tag === 'input' || tag === 'textarea' || ae?.isContentEditable;
        if (!isEditable && undoStackRef.current.length > 0) {
          e.preventDefault();
          handleUndo();
        }
      }
      if (((e.key === 'z' || e.key === 'Z') && (e.ctrlKey || e.metaKey) && e.shiftKey)
          || ((e.key === 'y' || e.key === 'Y') && (e.ctrlKey || e.metaKey))) {
        const ae = document.activeElement;
        const tag = (ae?.tagName || '').toLowerCase();
        const isEditable = tag === 'input' || tag === 'textarea' || ae?.isContentEditable;
        if (!isEditable && redoStackRef.current.length > 0) {
          e.preventDefault();
          handleRedo();
        }
      }
      // Enter closes the active polygon (≥ 2 vertices). Cuts down on
      // hunting for the first vertex when the operator's done.
      if (e.key === 'Enter' && drawMode === 'polygon' && activePoly && activePoly.points.length >= 2) {
        commitLocalStroke({
          type: 'polygon',
          lineType: defaultLineType,
          points: activePoly.points,
          closed: activePoly.points.length >= 3,
        });
        setActivePoly(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawMode, activePoly, activeLineStart, defaultLineType]);

  // Iter182 — tracer-line-type → annotator-pin-type map. Used when the
  // operator clicks on a saved segment so the freshly-dropped pin is
  // pre-typed correctly.
  const LINE_TO_PIN_TYPE = {
    gutter: 'Spouting',
    spouting: 'Spouting',
    ridge: 'Ridge',
    hip: 'Hip',
    valley: 'Valley',
    barge: 'Barge',
    flashing: 'Flashing',
    face: 'Face',
    face_m2: 'Face',
    length: 'Length',
    perimeter: 'Spouting',
    downpipe: 'Spouting',
    outlet: 'Outlet',
    pipe: 'Penetration',
    flue: 'Penetration',
    penetration: 'Penetration',
  };
  const pinTypeFromLine = (lt) => LINE_TO_PIN_TYPE[(lt || '').toLowerCase()] || null;

  // Distance from point (px,py) to line segment (ax,ay)-(bx,by) in % units.
  const pointToSegDist = (px, py, ax, ay, bx, by) => {
    const dx = bx - ax, dy = by - ay;
    const len2 = dx*dx + dy*dy;
    if (len2 === 0) return Math.hypot(px - ax, py - ay);
    let t = ((px - ax) * dx + (py - ay) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
  };

  // Iter182+ — total polyline length in % units, used to surface the
  // segment's relative length on the auto-generated starter pins.
  const polylineLengthPct = (pts, closed) => {
    if (!pts || pts.length < 2) return 0;
    let total = 0;
    for (let i = 0; i < pts.length - 1; i++) {
      total += Math.hypot(pts[i+1].x_pct - pts[i].x_pct, pts[i+1].y_pct - pts[i].y_pct);
    }
    if (closed) total += Math.hypot(pts[0].x_pct - pts[pts.length-1].x_pct, pts[0].y_pct - pts[pts.length-1].y_pct);
    return total;
  };

  // Iter182+ — midpoint of a polyline (50% along its arc length). Used
  // to anchor the auto-generated starter pin sensibly on each segment.
  const polylineMidpoint = (pts, closed) => {
    if (!pts || !pts.length) return { x: 50, y: 50 };
    if (pts.length === 1) return { x: pts[0].x_pct, y: pts[0].y_pct };
    const total = polylineLengthPct(pts, closed);
    if (total <= 0) return { x: pts[0].x_pct, y: pts[0].y_pct };
    const target = total / 2;
    let acc = 0;
    const segCount = closed ? pts.length : pts.length - 1;
    for (let i = 0; i < segCount; i++) {
      const a = pts[i], b = pts[(i + 1) % pts.length];
      const seg = Math.hypot(b.x_pct - a.x_pct, b.y_pct - a.y_pct);
      if (acc + seg >= target) {
        const t = (target - acc) / (seg || 1);
        return { x: a.x_pct + t * (b.x_pct - a.x_pct), y: a.y_pct + t * (b.y_pct - a.y_pct) };
      }
      acc += seg;
    }
    return { x: pts[pts.length-1].x_pct, y: pts[pts.length-1].y_pct };
  };

  // Iter182 — find the closest segment under the cursor (within 2.5%
  // threshold). Walks every stroke's polyline. Returns
  // { strokeIdx, segIdx, snapX, snapY, lineType } or null.
  const findStrokeUnder = (xPct, yPct, threshold = 2.5) => {
    let best = null;
    for (let si = 0; si < strokes.length; si++) {
      const s = strokes[si];
      const pts = s?.points || [];
      if (s?.type === 'point' && pts.length === 1) {
        const d = Math.hypot(xPct - pts[0].x_pct, yPct - pts[0].y_pct);
        if (d < threshold && (!best || d < best.dist)) {
          best = { strokeIdx: si, segIdx: 0, snapX: pts[0].x_pct, snapY: pts[0].y_pct, dist: d, lineType: s.lineType };
        }
        continue;
      }
      const n = pts.length;
      const last = s?.closed ? n : n - 1;
      for (let i = 0; i < last; i++) {
        const a = pts[i], b = pts[(i + 1) % n];
        if (!a || !b) continue;
        const d = pointToSegDist(xPct, yPct, a.x_pct, a.y_pct, b.x_pct, b.y_pct);
        if (d < threshold && (!best || d < best.dist)) {
          // Project the click onto the segment to snap.
          const dx = b.x_pct - a.x_pct, dy = b.y_pct - a.y_pct;
          const len2 = dx*dx + dy*dy;
          let t = len2 ? ((xPct - a.x_pct) * dx + (yPct - a.y_pct) * dy) / len2 : 0;
          t = Math.max(0, Math.min(1, t));
          best = {
            strokeIdx: si, segIdx: i,
            snapX: a.x_pct + t * dx, snapY: a.y_pct + t * dy,
            dist: d, lineType: s.lineType,
            a, b,
          };
        }
      }
    }
    return best;
  };
  useEffect(() => {
    const handler = (evt) => {
      const { pinId, pinType } = evt.detail || {};
      if (!pinId) return;
      // Match by computed REF code (the same per-type counter the
      // table uses). We rebuild the index on every event because pins
      // may have been added/removed since mount.
      const REF_PREFIX = { Ridge: 'R', Valley: 'V', Hip: 'H', Barge: 'B', Spouting: 'S', Outlet: 'O', Flashing: 'F', Face: 'FA', Penetration: 'PN', Length: 'L', LeakArea: 'LA' };
      const counters = {};
      const slotPins = pins.filter(p => (p.slot || 'roof_plan') === 'roof_plan');
      const targetIdx = slotPins.findIndex(p => {
        const t = (p.type || '').trim();
        const prefix = REF_PREFIX[t] || (t || 'X')[0].toUpperCase();
        counters[prefix] = (counters[prefix] || 0) + 1;
        return `${prefix}${counters[prefix]}` === pinId;
      });
      if (targetIdx < 0) return;
      const target = slotPins[targetIdx];
      setFlashPinId(target.id);
      // Scroll the container into view so the highlighted pin is visible.
      if (containerRef.current) containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => setFlashPinId(null), 2500);
    };
    window.addEventListener('rtr:focus-pin', handler);
    return () => window.removeEventListener('rtr:focus-pin', handler);
  }, [pins]);

  // Iter161 Phase B — Listen for the Remedial Repairs builder's
  // request to enter "drop a leak pin for area X" mode.
  useEffect(() => {
    const startHandler = (evt) => {
      const { leak_area_id, label, area_label } = evt.detail || {};
      if (!leak_area_id) return;
      setLeakDropMode({ leak_area_id, label: label || area_label || '', area_label: area_label || label || '' });
      // Make sure the operator can see the diagram
      if (containerRef.current) containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    const cancelHandler = () => setLeakDropMode(null);
    const escHandler = (e) => { if (e.key === 'Escape') setLeakDropMode(null); };
    window.addEventListener('rtr:start-leak-pin-drop', startHandler);
    window.addEventListener('rtr:cancel-leak-pin-drop', cancelHandler);
    window.addEventListener('keydown', escHandler);
    return () => {
      window.removeEventListener('rtr:start-leak-pin-drop', startHandler);
      window.removeEventListener('rtr:cancel-leak-pin-drop', cancelHandler);
      window.removeEventListener('keydown', escHandler);
    };
  }, []);


  // Aquaseal type catalogue (Dektite + Lead-based) sourced from the
  // component_rate_tables. Loaded once per mount so the Pipe/Flue edit
  // dialog can render a "Pick a Dektite" dropdown + auto-suggest one
  // that covers the pin's diameter.
  const [aquasealTypes, setAquasealTypes] = useState([]);
  // Backtray flashing width lookup. The lookup is editable from the
  // edit-dialog itself when the operator hits a profile × pipe combo
  // we haven't measured yet.
  const [backtrayRows, setBacktrayRows] = useState([]);
  const reloadBacktrayRows = useCallback(async () => {
    try {
      const { data } = await api.get('/backtray-widths');
      setBacktrayRows(Array.isArray(data) ? data : []);
    } catch { /* table empty / endpoint missing → soft-fail */ }
  }, [api]);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get('/component-rate-tables/aquaseal/type');
        if (mounted) setAquasealTypes(Array.isArray(data) ? data : []);
      } catch { /* soft-fail */ }
      reloadBacktrayRows();
    })();
    return () => { mounted = false; };
  }, [api, reloadBacktrayRows]);

  // Resync from the parent ONLY when the persisted content actually
  // differs from our local state (compared by serialised payload).
  // The legacy useEffect resynced every time `initialPins` changed by
  // reference — and because the parent passed `data.roof_diagram_annotations || []`
  // a fresh empty array was created on every render, wiping locally-added
  // pins on the next parent rerender. Bug 3a.
  //
  // Also auto-migrates legacy `Downpipe` → `Outlet` on load (in-memory
  // only; persists once the user makes any other save). Same treatment
  // for legacy `Face` LINE pins (with x_pct_end set) → `Length` after
  // the 2026-02-17 Face / Length split.
  useEffect(() => {
    if (editingPin) return;
    const migrated = (initialPins || []).map(p => {
      if (p.type === 'Downpipe') return { ...p, type: 'Outlet' };
      // Legacy Face line pins were used for linear measurements; the
      // 2026-02-17 split moves those to `Length` so Face stays purely
      // area (m²) and Length stays purely linear (m).
      const isLine = p.x_pct_end !== undefined && p.y_pct_end !== undefined;
      if (p.type === 'Face' && isLine) return { ...p, type: 'Length' };
      return p;
    });
    const visible = filterToSlot(migrated);
    const incoming = JSON.stringify(visible);
    const current  = JSON.stringify(pins);
    if (incoming !== current) setPins(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPins, editingPin, slot]);

  const save = async (nextPins, opts = {}) => {
    // Iter211 — snapshot the PRE-edit state (current pins + strokes)
    // on the undo stack BEFORE we mutate. Skipped when `opts.fromUndo`
    // is set (so undo'ing the undo wouldn't push itself back on) and
    // when `opts.skipUndoSnapshot` is set (used by chained internal
    // helpers like seedPinFromLocalStroke / deleteLocalStroke whose
    // OUTER saveLocalStrokes call already pushed one snapshot).
    if (!opts.fromUndo && !opts.fromRedo && !opts.skipUndoSnapshot) {
      pushUndoSnapshot();
    }
    // Stamp every pin we're editing with the current slot so per-slot
    // filtering works on next load. Then merge back with the pins from
    // OTHER slots before persisting.
    const stamped = slot
      ? nextPins.map(p => ({ ...p, slot: p.slot || slot }))
      : nextPins;
    const merged = slot ? [...otherSlotPins(initialPins), ...stamped] : stamped;
    setPins(stamped);
    onChange?.(merged);
    try {
      await api.patch(`/appraisals/${appraisalId}/roof-diagram-annotations`, { annotations: merged });
      onChange?.(merged);
      setSavedFlash('Saved ✓');
      setTimeout(() => setSavedFlash(''), 1800);
    } catch (e) {
      setSavedFlash('Save failed');
      setTimeout(() => setSavedFlash(''), 2500);
    }
  };

  // Iter211 — apply a previously captured snapshot to BOTH pins and
  // localStrokes, persisting both via the dedicated endpoints. Used by
  // handleUndo and handleRedo so a single user action reverts/replays
  // atomically (e.g. tracing a stroke + auto-seeded pin → one undo).
  const applySnapshot = async (snap) => {
    if (!snap) return;
    const snapPins = Array.isArray(snap.pins) ? snap.pins : [];
    const snapStrokes = Array.isArray(snap.localStrokes) ? snap.localStrokes : [];
    const stamped = slot
      ? snapPins.map(p => ({ ...p, slot: p.slot || slot }))
      : snapPins;
    const merged = slot ? [...otherSlotPins(initialPins), ...stamped] : stamped;
    setPins(stamped);
    setLocalStrokes(snapStrokes);
    onChange?.(merged);
    try {
      await Promise.all([
        api.patch(`/appraisals/${appraisalId}/roof-diagram-annotations`, { annotations: merged }),
        api.patch(`/appraisals/${appraisalId}/roof-diagram-strokes`, { strokes: snapStrokes }),
      ]);
      onChange?.(merged);
    } catch (_) {
      setSavedFlash('Revert save failed');
      setTimeout(() => setSavedFlash(''), 2500);
    }
  };

  const handleUndo = () => {
    if (!undoStackRef.current.length) return;
    pushRedoSnapshot();
    const prev = undoStackRef.current.pop();
    setUndoCount(undoStackRef.current.length);
    applySnapshot(prev);
    setSavedFlash(`Undone — ${undoStackRef.current.length} step${undoStackRef.current.length === 1 ? '' : 's'} left`);
    setTimeout(() => setSavedFlash(''), 2000);
  };

  const handleRedo = () => {
    if (!redoStackRef.current.length) return;
    // Capture current state on undo stack WITHOUT clearing redo, so
    // an undo-after-redo chain works the way users expect.
    pushUndoSnapshot(false);
    const next = redoStackRef.current.pop();
    setRedoCount(redoStackRef.current.length);
    applySnapshot(next);
    setSavedFlash(`Redone — ${redoStackRef.current.length} step${redoStackRef.current.length === 1 ? '' : 's'} left`);
    setTimeout(() => setSavedFlash(''), 2000);
  };

  // Iter182+ — auto-generate a starter pin set the moment a template
  // with strokes is opened against an empty pin list. One pin per
  // tagged stroke, dropped at the segment's midpoint with the matching
  // pin type pre-filled and the polyline's % length stored as
  // `segment_length_pct` (a hint the operator can convert to metres in
  // the edit dialog). Skipped when the operator already has pins on
  // this slot so we never overwrite real work.
  //
  // Iter182++ — STALENESS GUARD: when the operator swaps the property's
  // main template, the existing `pins` array still references the OLD
  // template's segment_idx (which no longer matches the new strokes)
  // AND its pin chips render at the OLD percent coords (which no longer
  // map onto the NEW lines). The classic symptom: "extra lines / labels
  // floating in odd places". Detect this by checking whether ANY pin is
  // tagged `auto_starter` with a `segment_idx` that's now out-of-range
  // for the new strokes array, OR every auto_starter pin has all-zero
  // length_m AND the geometry simply doesn't line up. When stale, wipe
  // the auto_starter pins and re-seed against the new template.
  // Manually-placed pins (no `auto_starter` flag) are PRESERVED — the
  // operator's hand-drawn additions survive a template change.
  useEffect(() => {
    if (!strokes.length) return;
    if (!appraisalId || !templateId) return;
    // Iter237 — staleness wipe removed. With the server-side smart-
    // crop ensuring PNG and SVG occupy the same rectangle, template
    // swaps no longer cause pins to drift to wrong positions. If
    // operator-introduced staleness ever resurfaces, surface it as
    // a manual "Reset starter pins" action rather than auto-wiping.
    if (starterSeededRef.current) return;
    if (pins.some(p => p.auto_starter)) return;  // already seeded once
    starterSeededRef.current = true;
    const starter = [];
    for (let si = 0; si < strokes.length; si++) {
      const s = strokes[si];
      const pts = s?.points || [];
      if (!pts.length) continue;
      const pinType = pinTypeFromLine(s.lineType);
      if (!pinType) continue;
      const colour = TYPE_LOOKUP[pinType]?.color || '#94A3B8';
      if (s.type === 'point' || pts.length < 2) {
        starter.push({
          id: genId(), x_pct: pts[0].x_pct, y_pct: pts[0].y_pct,
          type: pinType,
          // Iter182++ — inherit any length/diameter the operator already
          // typed during the trace so the chip shows real numbers, not "—".
          length_m: Number(s.length_m) || Number(s.area_m2) || 0,
          ...(s.lineType === 'face_m2' && Number(s.area_m2) > 0 ? { face_area_override: true } : {}),
          outlet_diameter_mm: s.diameter_mm || undefined,
          label: '', color: colour,
          auto_starter: true,
          segment_idx: si,
          // Iter183 — stamp the template these pins were seeded from
          // so a future template swap can detect & wipe them cleanly.
          template_id: templateId,
        });
        continue;
      }
      const mid = polylineMidpoint(pts, !!s.closed);
      const lenPct = polylineLengthPct(pts, !!s.closed);
      const first = pts[0], last = pts[pts.length - 1];
      starter.push({
        id: genId(),
        x_pct: first.x_pct, y_pct: first.y_pct,
        x_pct_end: last.x_pct, y_pct_end: last.y_pct,
        // Iter182++ — `x_pct_label/y_pct_label` is the chip position.
        // Defaulted to the segment midpoint; the operator can later
        // drag the chip to declutter while the underlying line stays
        // glued to the segment (see `segment_idx` below).
        x_pct_label: mid.x, y_pct_label: mid.y,
        type: pinType,
        // Iter182++ — inherit length/area from the originating stroke
        // so chips show "Sp1 · 18.60 m" right from the open, not "Sp1 · —".
        length_m: Number(s.length_m) || Number(s.area_m2) || 0,
        label: '', color: colour,
        segment_length_pct: Math.round(lenPct * 10) / 10,
        auto_starter: true,
        // Iter182++ — bind this pin to the underlying tagged segment
        // so the editor renders the pin's line as the FULL polyline
        // (not just a straight 2-point line) and the drag handler
        // only moves the tag, never the endpoints.
        segment_idx: si,
        // Iter183 — stamp the template these pins were seeded from
        // so a future template swap can detect & wipe them cleanly.
        template_id: templateId,
      });
    }
    if (!starter.length) return;
    // Iter182++ — Seed every chip a few %-pts perpendicular to its
    // line, pushed OUTWARD relative to the rough centroid of all
    // anchors, then run a few overlap-relaxation passes so labels
    // never sit on top of each other. Mirrors the algorithm in the
    // Trace Template canvas's "Auto-arrange" button.
    const lineLbls = starter.filter(p => p.segment_idx !== undefined && p.x_pct_end !== undefined);
    if (lineLbls.length >= 2) {
      const cx = lineLbls.reduce((a, p) => a + ((p.x_pct + p.x_pct_end) / 2), 0) / lineLbls.length;
      const cy = lineLbls.reduce((a, p) => a + ((p.y_pct + p.y_pct_end) / 2), 0) / lineLbls.length;
      const OFFSET_PCT = 3.5; // ~3.5% of canvas as the perpendicular offset
      lineLbls.forEach((p) => {
        const dx = (p.x_pct_end - p.x_pct);
        const dy = (p.y_pct_end - p.y_pct);
        const len = Math.hypot(dx, dy) || 1;
        let nx = -dy / len, ny = dx / len;
        const mx = ((p.x_pct + p.x_pct_end) / 2) - cx;
        const my = ((p.y_pct + p.y_pct_end) / 2) - cy;
        if (nx * mx + ny * my < 0) { nx = -nx; ny = -ny; }
        p.x_pct_label = Math.max(0, Math.min(100, p.x_pct_label + nx * OFFSET_PCT));
        p.y_pct_label = Math.max(0, Math.min(100, p.y_pct_label + ny * OFFSET_PCT));
      });
      // Overlap-relax — pretend each chip is a 9×5 %-pt rectangle and
      // push pairs apart along their connecting vector. 16 passes cap.
      const HALF_W = 4.5, HALF_H = 2.6;
      for (let pass = 0; pass < 16; pass++) {
        let nudged = false;
        for (let i = 0; i < lineLbls.length; i++) {
          for (let j = i + 1; j < lineLbls.length; j++) {
            const A = lineLbls[i], B = lineLbls[j];
            const ox = Math.abs(A.x_pct_label - B.x_pct_label);
            const oy = Math.abs(A.y_pct_label - B.y_pct_label);
            if (ox < HALF_W * 2 && oy < HALF_H * 2) {
              let vx = (A.x_pct_label - B.x_pct_label);
              let vy = (A.y_pct_label - B.y_pct_label);
              if (Math.hypot(vx, vy) < 0.001) { vx = 1; vy = 0; }
              const mag = Math.hypot(vx, vy);
              vx /= mag; vy /= mag;
              const step = Math.max(HALF_W * 2 - ox, HALF_H * 2 - oy) * 0.55 + 0.2;
              A.x_pct_label = Math.max(0, Math.min(100, A.x_pct_label + vx * step));
              A.y_pct_label = Math.max(0, Math.min(100, A.y_pct_label + vy * step));
              B.x_pct_label = Math.max(0, Math.min(100, B.x_pct_label - vx * step));
              B.y_pct_label = Math.max(0, Math.min(100, B.y_pct_label - vy * step));
              nudged = true;
            }
          }
        }
        if (!nudged) break;
      }
    }
    setSavedFlash(`Pre-populated ${starter.length} starter pins · type the dimensions`);
    setTimeout(() => setSavedFlash(''), 4000);
    // Iter182++ — Append (not replace). If the operator had manual
    // pins on the diagram before a template swap, preserve them.
    const manualPins = pins.filter(p => !p.auto_starter);
    save([...manualPins, ...starter]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strokes, pins.length, appraisalId, templateId, slot]);

  // ── Iter241 — Pin → segment auto-rebind sweep REMOVED ──────────────
  // History: added in Iter239 to fix "pins highlight wrong lines on
  // hover". The actual root cause turned out to be the smart-crop
  // image vs stroke coordinate mismatch (fixed in Iter240). With that
  // resolved, the sweep has no legitimate work to do — and on every
  // page load it AUTOMATICALLY re-binds pins whose `type` doesn't
  // match their stroke's `lineType` to the nearest matching-type
  // stroke, which broke the operator's manual workflow of changing a
  // pin's type via the edit dialog (e.g. flipping a Ridge to a
  // Spouting). The operator's binding is now trusted as-is.

  // Pointer-down on the diagram canvas. Two outcomes based on drag distance:
  //   • Released within ~8 px of start → drop a SINGLE-POINT pin (legacy click)
  //   • Released after a drag ≥ 8 px    → drop a LINE pin (start + end coords)
  // The line lets the operator click-and-drag to mark the actual run of a
  // ridge/valley/gutter/face area on the diagram. The visual end coords are
  // stored separately from `length_m` — the user still types the real-world
  // metres so the printout matches what they measured on site.
  const [drawing, setDrawing] = useState(null);  // { x1, y1, x2, y2 } while dragging

  // Iter184 — persist appraisal-local strokes via the dedicated endpoint
  // (does NOT touch the underlying roof_template).
  // Iter211 — pushes an undo snapshot before mutation unless
  // `opts.skipUndoSnapshot` is set (used during undo/redo replay).
  const saveLocalStrokes = async (next, opts = {}) => {
    if (!opts.fromUndo && !opts.fromRedo && !opts.skipUndoSnapshot) {
      pushUndoSnapshot();
    }
    setLocalStrokes(next);
    try {
      await api.patch(`/appraisals/${appraisalId}/roof-diagram-strokes`, { strokes: next });
    } catch (_) {
      setSavedFlash('Stroke save failed');
      setTimeout(() => setSavedFlash(''), 2500);
    }
  };

  // Iter184 — Auto-seed a pin from a newly-committed local stroke so the
  // operator's tag flows straight into the appraisal annotation list
  // (mirrors the behaviour of the template-stroke seeder). The pin
  // carries `local_stroke_id` so the staleness guard skips it on
  // template swap.
  const seedPinFromLocalStroke = (s) => {
    const pinType = pinTypeFromLine(s.lineType);
    if (!pinType) return;  // unsupported line type → no pin
    const colour = TYPE_LOOKUP[pinType]?.color || '#94A3B8';
    const pts = s.points || [];
    if (!pts.length) return;
    let newPin;
    if (s.type === 'point' || pts.length < 2) {
      newPin = {
        id: genId(),
        x_pct: pts[0].x_pct, y_pct: pts[0].y_pct,
        type: pinType, length_m: 0, label: '', color: colour,
        auto_starter: true, local_stroke_id: s.id,
      };
    } else {
      const mid = polylineMidpoint(pts, !!s.closed);
      const first = pts[0], last = pts[pts.length - 1];
      newPin = {
        id: genId(),
        x_pct: first.x_pct, y_pct: first.y_pct,
        x_pct_end: last.x_pct, y_pct_end: last.y_pct,
        x_pct_label: mid.x, y_pct_label: mid.y,
        type: pinType, length_m: 0, label: '', color: colour,
        auto_starter: true, local_stroke_id: s.id,
      };
    }
    // Iter211 — atomic with the outer commitLocalStroke: the
    // saveLocalStrokes call already pushed an undo snapshot for THIS
    // user action, so suppress pushing a second one here.
    save([...pins, newPin], { skipUndoSnapshot: true });
  };

  // Iter184 — append a freshly-traced stroke to the appraisal's local
  // strokes, persist, and auto-seed its pin. Called by every tracer
  // mode (point / straight / polygon).
  const commitLocalStroke = (stroke) => {
    const sid = genId();
    const withId = { ...stroke, id: sid };
    const next = [...localStrokes, withId];
    saveLocalStrokes(next);
    seedPinFromLocalStroke(withId);
    setSavedFlash(`Traced ${stroke.lineType || 'stroke'} · ${stroke.type === 'point' ? 'point' : (stroke.points?.length || 0) + ' vertices'} ✓`);
    setTimeout(() => setSavedFlash(''), 2000);
  };

  // Iter184 — remove a local stroke + its auto-seeded pin (matched by
  // `local_stroke_id`). Manual edits to the pin survive — only the
  // freshly auto-seeded pin is dropped.
  const deleteLocalStroke = (strokeId) => {
    const nextStrokes = localStrokes.filter(s => s.id !== strokeId);
    saveLocalStrokes(nextStrokes);
    const nextPins = pins.filter(p => !(p.local_stroke_id === strokeId && p.auto_starter));
    // Iter211 — atomic with the outer saveLocalStrokes snapshot.
    if (nextPins.length !== pins.length) save(nextPins, { skipUndoSnapshot: true });
  };

  const handleDiagramPointerDown = (e) => {
    if (e.button !== 0) return;
    if (dragging || dragMoved.current) {
      dragMoved.current = false;
      return;
    }
    // Don't start a line draw when the user is interacting with a pin marker
    if (e.target.closest('[data-testid^="annot-pin-"]')) return;
    // Iter184 — same guard for clicks on the local-stroke delete chips.
    if (e.target.closest('[data-testid^="annot-local-stroke-"]')) return;
    const rect = containerRef.current.getBoundingClientRect();
    const startX = ((e.clientX - rect.left) / rect.width) * 100;
    const startY = ((e.clientY - rect.top) / rect.height) * 100;

    // Iter161 Phase B — leak-pin drop mode short-circuits the normal
    // pointer-down handling. We drop a LeakArea POSITION pin at the
    // click coordinates, emit `rtr:leak-pin-placed` so the Remedial
    // Repairs builder can persist pin_id ↔ leak_area_id, and exit.
    if (leakDropMode) {
      const newPin = {
        id: genId(),
        x_pct: startX, y_pct: startY,
        type: 'LeakArea', length_m: 0,
        label: leakDropMode.label || '',
        color: TYPE_LOOKUP['LeakArea'].color,
        leak_area_id: leakDropMode.leak_area_id,
      };
      const nextPins = [...pins, newPin];
      save(nextPins);
      try {
        window.dispatchEvent(new CustomEvent('rtr:leak-pin-placed', {
          detail: { leak_area_id: leakDropMode.leak_area_id, pin_id: newPin.id, x_pct: startX, y_pct: startY },
        }));
      } catch (_) { /* ignore */ }
      setLeakDropMode(null);
      return;
    }

    // Iter229x — "+ Pen + Btray" dual-drop. Creates a Penetration
    // pin at the click + a linked Backtray flashing pin offset ~2%
    // (clamped 2-98) in a single user action. Mode stays active so
    // the operator can chain-drop multiple installs without re-
    // selecting the tool.
    if (drawMode === 'penbtray') {
      const offset = (v) => Math.min(98, Math.max(2, (parseFloat(v) || 50) + 2));
      const penId = genId();
      const penPin = {
        id: penId,
        x_pct: startX, y_pct: startY,
        type: 'Penetration',
        penetration_kind: 'pipe',
        outlet_diameter_mm: 32,
        length_m: 0,
        label: '',
        color: TYPE_LOOKUP['Penetration']?.color,
      };
      const btrayPin = {
        id: genId(),
        x_pct: offset(startX), y_pct: offset(startY),
        type: 'Flashing',
        flashing_type: 'Backtray',
        flashing_description: 'Pipe Backtray',
        length_m: 0,
        linked_pipe_pin_id: penId,
        has_penetration: 'yes',
        label: '',
        color: TYPE_LOOKUP['Flashing']?.color,
      };
      save([...pins, penPin, btrayPin]).then(() => {
        // Iter229x — flash AFTER save() so we overwrite its
        // internal 'Saved ✓' indicator with the dual-drop-specific
        // confirmation. Without the .then chain our flash was being
        // immediately overwritten by save()'s own setSavedFlash.
        setSavedFlash('Dropped Penetration + Backtray ✓');
        setTimeout(() => setSavedFlash(''), 2200);
      });
      return;
    }

    // Iter184 — Tracer draw modes. Routes pointer-down through the
    // tracer state machine instead of the legacy pin-drop path when
    // `drawMode !== 'pin'`. Point = drop immediately; Straight =
    // 2-click line; Polygon = multi-click polyline + close on near-
    // first-vertex.
    if (drawMode !== 'pin') {
      if (drawMode === 'point') {
        commitLocalStroke({
          type: 'point',
          lineType: defaultLineType,
          points: [{ x_pct: startX, y_pct: startY }],
        });
        return;
      }
      if (drawMode === 'straight') {
        if (!activeLineStart) {
          setActiveLineStart({ x_pct: startX, y_pct: startY });
        } else {
          commitLocalStroke({
            type: 'polygon',
            lineType: defaultLineType,
            points: [activeLineStart, { x_pct: startX, y_pct: startY }],
            closed: false,
          });
          setActiveLineStart(null);
        }
        return;
      }
      if (drawMode === 'polygon') {
        if (!activePoly) {
          setActivePoly({ points: [{ x_pct: startX, y_pct: startY }] });
          return;
        }
        const first = activePoly.points[0];
        const dist = Math.hypot(startX - first.x_pct, startY - first.y_pct);
        if (activePoly.points.length >= 2 && dist < 2.5) {
          // Close polygon on click near first vertex.
          commitLocalStroke({
            type: 'polygon',
            lineType: defaultLineType,
            points: activePoly.points,
            closed: activePoly.points.length >= 3,
          });
          setActivePoly(null);
          return;
        }
        setActivePoly({ points: [...activePoly.points, { x_pct: startX, y_pct: startY }] });
        return;
      }
    }

    setDrawing({ x1: startX, y1: startY, x2: startX, y2: startY });
    const startClientX = e.clientX;
    const startClientY = e.clientY;
    let moved = false;

    const onMove = (ev) => {
      const dx = Math.abs(ev.clientX - startClientX);
      const dy = Math.abs(ev.clientY - startClientY);
      if (dx > 4 || dy > 4) moved = true;
      const nx = ((ev.clientX - rect.left) / rect.width) * 100;
      const ny = ((ev.clientY - rect.top) / rect.height) * 100;
      setDrawing({ x1: startX, y1: startY, x2: Math.max(0, Math.min(100, nx)), y2: Math.max(0, Math.min(100, ny)) });
    };
    const onUp = (ev) => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      const endX = ((ev.clientX - rect.left) / rect.width) * 100;
      const endY = ((ev.clientY - rect.top) / rect.height) * 100;
      const dragPx = Math.hypot(ev.clientX - startClientX, ev.clientY - startClientY);
      setDrawing(null);
      // Iter182 — hit-test the click against the saved template
      // segments. If the operator clicked ON a tagged segment we
      // pre-type the new pin from that segment's lineType AND snap
      // the pin to the segment.
      const hit = findStrokeUnder(startX, startY);
      const hitPinType = hit ? pinTypeFromLine(hit.lineType) : null;
      const snapStart = hit ? { x: hit.snapX, y: hit.snapY } : { x: startX, y: startY };
      const hitEnd = (moved && dragPx >= 8) ? findStrokeUnder(endX, endY) : null;
      const snapEnd = hitEnd ? { x: hitEnd.snapX, y: hitEnd.snapY } : { x: endX, y: endY };
      // Edge-proximity check uses the START point — same heuristic as before
      const edgeProx = Math.min(startX, startY, 100 - startX, 100 - startY);
      const defaultType = hitPinType || (edgeProx <= 12 ? 'Spouting' : 'Ridge');
      if (moved && dragPx >= 8) {
        // LINE pin — drag-to-measure. Default type is taken from the
        // tagged segment if the click landed on one (Iter182), else
        // falls back to "Length" so existing behaviour is preserved.
        const lineDefaultType = hitPinType || 'Length';
        const newPin = {
          id: genId(),
          x_pct: snapStart.x, y_pct: snapStart.y,
          x_pct_end: Math.max(0, Math.min(100, snapEnd.x)),
          y_pct_end: Math.max(0, Math.min(100, snapEnd.y)),
          type: lineDefaultType, length_m: 0, label: '',
          color: TYPE_LOOKUP[lineDefaultType].color,
        };
        // Iter227 — Ridge pins land with the roof-type-appropriate cap
        // pre-stamped so the dialog opens already cascaded (Iron →
        // RoundTop Std, Concrete → Standard Concrete, …).
        if (isRidging(lineDefaultType)) {
          const cap = defaultRidgeCapForRoof(roofType);
          if (cap) newPin.ridge_cap_type = cap;
        }
        const nextPins = [...pins, newPin];
        save(nextPins);
        setEditingPin({ ...newPin, _isFresh: true });
      } else {
        // SINGLE-POINT pin (legacy click path)
        const newPin = {
          id: genId(),
          x_pct: snapStart.x, y_pct: snapStart.y,
          type: defaultType, length_m: 0, label: '',
          color: TYPE_LOOKUP[defaultType].color,
        };
        if (isRidging(defaultType)) {
          const cap = defaultRidgeCapForRoof(roofType);
          if (cap) newPin.ridge_cap_type = cap;
        }
        const nextPins = [...pins, newPin];
        save(nextPins);
        setEditingPin({ ...newPin, _isFresh: true });
      }
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  };

  const handleEditSave = (updated) => {
    // For new Ridge pins that don't yet have a cap type set, default to
    // the roof-type-appropriate option (RoundTop Std on Iron, Standard
    // Concrete on tile etc.) so the canvas surfaces the right pricing
    // signal immediately. Operator can still pick a different cap type.
    if (isRidging(updated.type) && !updated.ridge_cap_type) {
      const fallback = defaultRidgeCapForRoof(roofType);
      if (fallback) updated = { ...updated, ridge_cap_type: fallback };
    }
    const next = pins.map(p => p.id === updated.id ? { ...updated, color: TYPE_LOOKUP[updated.type]?.color || updated.color } : p);
    save(next);
    // Capture a clean template (no id, no position) of what the operator
    // just typed so a subsequent double-click can replay it.
    const preset = { ...updated };
    delete preset.id; delete preset.x_pct; delete preset.y_pct;
    delete preset.x_pct_end; delete preset.y_pct_end;
    delete preset._isFresh;
    setLastPinPreset(preset);
    setEditingPin(null);
  };

  // Double-click → drop a new pin pre-filled from the last-saved pin's
  // settings. Skips the edit dialog entirely so power-users can sweep
  // through multiple repeated measurements (e.g. tagging every cap on
  // a long ridge at the same length / cap type). If there's no preset
  // yet (no prior pin saved this session), we fall back to opening the
  // edit dialog like a single-click does.
  const handleDiagramDoubleClick = (e) => {
    if (e.target.closest('[data-testid^="annot-pin-"]')) return;
    if (!containerRef.current) return;
    // Iter184 — Polygon tracer: double-click closes the polygon (closed
    // when ≥ 3 vertices, open polyline otherwise).
    if (drawMode === 'polygon' && activePoly && activePoly.points.length >= 2) {
      e.preventDefault();
      commitLocalStroke({
        type: 'polygon',
        lineType: defaultLineType,
        points: activePoly.points,
        closed: activePoly.points.length >= 3,
      });
      setActivePoly(null);
      return;
    }
    // Iter184 — Other tracer modes (point / straight) suppress the
    // legacy "quick-drop from last preset" behaviour while active.
    if (drawMode !== 'pin') { e.preventDefault(); return; }
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    // The FIRST mousedown of a double-click already triggered
    // handleDiagramPointerDown, which created a fresh-default pin and
    // opened the edit dialog. We detect that state and convert that
    // fresh pin into the preset-applied one instead of creating a
    // second pin (which would leave behind a stray 0m placeholder).
    if (editingPin && editingPin._isFresh) {
      const fresh = editingPin;
      setEditingPin(null);
      if (!lastPinPreset) {
        // Nothing to apply — just leave the fresh-default pin as-is.
        return;
      }
      const applied = {
        ...lastPinPreset,
        id: fresh.id,
        x_pct: fresh.x_pct,
        y_pct: fresh.y_pct,
        color: TYPE_LOOKUP[lastPinPreset.type]?.color || lastPinPreset.color,
      };
      const next = pins.map(p => p.id === fresh.id ? applied : p);
      save(next);
      setSavedFlash(`Quick-drop · ${applied.type} ${applied.length_m || ''}${unitForPin(applied)} ✓`);
      setTimeout(() => setSavedFlash(''), 1800);
      return;
    }
    if (!lastPinPreset) {
      const newPin = {
        id: genId(), x_pct: x, y_pct: y,
        type: 'Ridge', length_m: 0, label: '',
        color: TYPE_LOOKUP['Ridge'].color,
      };
      save([...pins, newPin]);
      setEditingPin({ ...newPin, _isFresh: true });
      return;
    }
    const newPin = {
      ...lastPinPreset,
      id: genId(),
      x_pct: x, y_pct: y,
      color: TYPE_LOOKUP[lastPinPreset.type]?.color || lastPinPreset.color,
    };
    save([...pins, newPin]);
    setSavedFlash(`Quick-drop · ${newPin.type} ${newPin.length_m || ''}${unitForPin(newPin)} ✓`);
    setTimeout(() => setSavedFlash(''), 1800);
  };

  const handleEditDelete = (id) => {
    const next = pins.filter(p => p.id !== id);
    save(next);
    setEditingPin(null);
  };

  const handlePinPointerDown = (e, pin) => {
    e.stopPropagation();
    setDragging(pin.id);
    dragMoved.current = false;
    const rect = containerRef.current.getBoundingClientRect();
    // Iter182++ — anchor the drag offset to the pin's CHIP position
    // (label-coord when set, else legacy midpoint / point) so a drag
    // moves the chip directly under the cursor.
    let anchorX, anchorY;
    if (pin.x_pct_label !== undefined && pin.y_pct_label !== undefined) {
      anchorX = pin.x_pct_label; anchorY = pin.y_pct_label;
    } else if (pin.x_pct_end !== undefined && pin.y_pct_end !== undefined) {
      anchorX = (pin.x_pct + pin.x_pct_end) / 2;
      anchorY = (pin.y_pct + pin.y_pct_end) / 2;
    } else {
      anchorX = pin.x_pct; anchorY = pin.y_pct;
    }
    const offsetX = e.clientX - (rect.left + (anchorX / 100) * rect.width);
    const offsetY = e.clientY - (rect.top + (anchorY / 100) * rect.height);

    const onMove = (ev) => {
      const dx = Math.abs(ev.clientX - e.clientX);
      const dy = Math.abs(ev.clientY - e.clientY);
      if (dx > 3 || dy > 3) dragMoved.current = true;
      const nx = ((ev.clientX - offsetX - rect.left) / rect.width) * 100;
      const ny = ((ev.clientY - offsetY - rect.top) / rect.height) * 100;
      setPins(prev => prev.map(p => {
        if (p.id !== pin.id) return p;
        const newX = Math.max(0, Math.min(100, nx));
        const newY = Math.max(0, Math.min(100, ny));
        // Iter182++ — segment-bound pins (auto-starter, or manually
        // attached) keep their endpoints locked to the underlying
        // segment polyline. Dragging moves ONLY the label/tag.
        if (p.segment_idx !== undefined && p.segment_idx !== null) {
          return { ...p, x_pct_label: newX, y_pct_label: newY };
        }
        // For LEGACY line pins (no segment binding), translate the
        // whole line: move both endpoints by the same delta so the
        // line keeps its shape.
        if (p.x_pct_end !== undefined && p.y_pct_end !== undefined) {
          const oldMidX = (p.x_pct + p.x_pct_end) / 2;
          const oldMidY = (p.y_pct + p.y_pct_end) / 2;
          const deltaX = newX - oldMidX;
          const deltaY = newY - oldMidY;
          return {
            ...p,
            x_pct:     Math.max(0, Math.min(100, p.x_pct + deltaX)),
            y_pct:     Math.max(0, Math.min(100, p.y_pct + deltaY)),
            x_pct_end: Math.max(0, Math.min(100, p.x_pct_end + deltaX)),
            y_pct_end: Math.max(0, Math.min(100, p.y_pct_end + deltaY)),
          };
        }
        return { ...p, x_pct: newX, y_pct: newY };
      }));
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      setDragging(null);
      if (dragMoved.current) {
        // Persist after drag end (we used the latest pins array via the setter)
        setPins(latest => { save(latest); return latest; });
      } else {
        // No drag → treat as click → open editor
        setEditingPin(pin);
      }
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  };

  const resetAll = async () => {
    if (!window.confirm('Remove ALL annotations from this diagram?')) return;
    await save([]);
  };

  // Iter182++ — Auto-arrange. Re-runs the perpendicular-outward seed
  // + overlap-relaxation algorithm on the CURRENT pin set. The seed
  // skips any pin whose chip has been manually dragged > 1pt away
  // from the recommended position (we treat their drag as an explicit
  // override). Anything still stacked is unstacked.
  const autoArrangeChips = async () => {
    if (pins.length === 0) return;
    const next = pins.map(p => ({ ...p }));
    const lineLbls = next.filter(p => p.segment_idx !== undefined && p.x_pct_end !== undefined);
    if (lineLbls.length === 0) return;
    const cx = lineLbls.reduce((a, p) => a + ((p.x_pct + p.x_pct_end) / 2), 0) / lineLbls.length;
    const cy = lineLbls.reduce((a, p) => a + ((p.y_pct + p.y_pct_end) / 2), 0) / lineLbls.length;
    const OFFSET_PCT = 3.5;
    lineLbls.forEach((p) => {
      const dx = (p.x_pct_end - p.x_pct);
      const dy = (p.y_pct_end - p.y_pct);
      const len = Math.hypot(dx, dy) || 1;
      let nx = -dy / len, ny = dx / len;
      const mx = ((p.x_pct + p.x_pct_end) / 2) - cx;
      const my = ((p.y_pct + p.y_pct_end) / 2) - cy;
      if (nx * mx + ny * my < 0) { nx = -nx; ny = -ny; }
      const baseMidX = (p.x_pct + p.x_pct_end) / 2;
      const baseMidY = (p.y_pct + p.y_pct_end) / 2;
      p.x_pct_label = Math.max(0, Math.min(100, baseMidX + nx * OFFSET_PCT));
      p.y_pct_label = Math.max(0, Math.min(100, baseMidY + ny * OFFSET_PCT));
    });
    const HALF_W = 4.5, HALF_H = 2.6;
    for (let pass = 0; pass < 16; pass++) {
      let nudged = false;
      for (let i = 0; i < lineLbls.length; i++) {
        for (let j = i + 1; j < lineLbls.length; j++) {
          const A = lineLbls[i], B = lineLbls[j];
          const ox = Math.abs(A.x_pct_label - B.x_pct_label);
          const oy = Math.abs(A.y_pct_label - B.y_pct_label);
          if (ox < HALF_W * 2 && oy < HALF_H * 2) {
            let vx = (A.x_pct_label - B.x_pct_label);
            let vy = (A.y_pct_label - B.y_pct_label);
            if (Math.hypot(vx, vy) < 0.001) { vx = 1; vy = 0; }
            const mag = Math.hypot(vx, vy); vx /= mag; vy /= mag;
            const step = Math.max(HALF_W * 2 - ox, HALF_H * 2 - oy) * 0.55 + 0.2;
            A.x_pct_label = Math.max(0, Math.min(100, A.x_pct_label + vx * step));
            A.y_pct_label = Math.max(0, Math.min(100, A.y_pct_label + vy * step));
            B.x_pct_label = Math.max(0, Math.min(100, B.x_pct_label - vx * step));
            B.y_pct_label = Math.max(0, Math.min(100, B.y_pct_label - vy * step));
            nudged = true;
          }
        }
      }
      if (!nudged) break;
    }
    await save(next);
  };

  const pushToComponents = async () => {
    if (!pins.some(p => (parseFloat(p.length_m) || 0) > 0)) {
      setPushFlash({ ok: false, msg: 'No pins with a length > 0 yet.' });
      setTimeout(() => setPushFlash(null), 3000);
      return;
    }
    if (!window.confirm('Push these measurements to the Components tab? This replaces any previous diagram-imported lines (operator-created lines are kept).')) return;
    setPushing(true);
    try {
      const { data } = await api.post(`/appraisals/${appraisalId}/roof-diagram-annotations/push-to-components`);
      setPushFlash({ ok: true, msg: `Pushed ${data.lines_created} line${data.lines_created !== 1 ? 's' : ''} — ${Object.entries(data.rollup || {}).map(([k, v]) => `${k}: ${v.length}m`).join(', ')}` });
      // Iter229z — notify parent so the Components tab below
      // auto-refreshes. Without this the operator sees stale line
      // items until they switch tabs and come back (A2508
      // walkthrough finding).
      if (typeof onPushedToComponents === 'function') {
        try { onPushedToComponents(data); } catch (_) { /* swallow */ }
      }
    } catch (e) {
      setPushFlash({ ok: false, msg: e?.response?.data?.detail || 'Push failed' });
    }
    setPushing(false);
    setTimeout(() => setPushFlash(null), 5500);
  };

  // Group pins by type for the live totals strip. POSITION pins (Outlet
  // / Pipe / Flue) have no linear length — bucket the count and the
  // *distinct diameters* so the totals chip reads e.g. "Pipe: 32/50 mm
  // × 2" rather than "Pipe: 0.0m × 2". Ridge pins that carry a cap type
  // also accumulate a ridge-cap qty so the "Ridge" subtotal chip reads
  // "6.3m · 15c × 1" — visible without opening the dedicated rollup
  // card. Face vs Length is now a clean split (Face = area m²,
  // Length = drag-drawn line m) so no special-cased "Face (length)"
  // bucket is needed.
  const totals = pins.reduce((acc, p) => {
    const isPos  = !!(TYPE_LOOKUP[p.type] || {}).position;
    const key = p.type;
    if (!acc[key]) {
      acc[key] = {
        count: 0,
        length: 0,
        unit: unitForPin(p),
        color: (TYPE_LOOKUP[p.type] || {}).color || '#94a3b8',
        position: isPos,
        diameters: new Set(),
        cap_qty: 0,
      };
    }
    acc[key].count += 1;
    acc[key].length += parseFloat(p.length_m) || 0;
    if (isPos && p.outlet_diameter_mm) acc[key].diameters.add(p.outlet_diameter_mm);
    // Ridge cap qty rollup into the subtotal chip. When `forceRidgeCapCount`
    // is set by the parent (e.g. the Concrete Restore slot), default
    // any Ridge pin without an explicit `ridge_cap_type` to Standard
    // Concrete (420 mm) so the operator always sees the cap quantity
    // for ordering — concrete restoration jobs ALWAYS replace ridge caps.
    const effectiveRidgeCapType = (isRidging(p.type)
        ? (p.ridge_cap_type || (forceRidgeCapCount ? 'Standard Concrete' : ''))
        : '');
    if (isRidging(p.type) && effectiveRidgeCapType) {
      const cap = RIDGE_CAP_LOOKUP[effectiveRidgeCapType];
      if (cap) {
        const capM = cap.length_mm / 1000;
        const explicit = parseInt(p.ridge_cap_count, 10);
        const len = parseFloat(p.length_m) || 0;
        const qty = Number.isFinite(explicit) && explicit > 0
          ? explicit
          : (len > 0 ? Math.ceil(len / capM) : 0);
        acc[key].cap_qty += qty;
        // Std-420 equivalent — quoting unit for legacy rate tables.
        const lenForStd = len > 0 ? len : (explicit > 0 ? explicit * capM : 0);
        if (lenForStd > 0) {
          acc[key].cap_std420 = (acc[key].cap_std420 || 0) + Math.ceil(lenForStd / (STD_RIDGE_CAP_MM / 1000));
        }
      }
    }
    return acc;
  }, {});

  // Downpipe rollup — only Outlet pins flagged `downpipe_install`. Material
  // tally is per diameter: pipe metres + 2 bends + 1 × 500 mm junction.
  // Used for the Guttering Tradespack quote.
  const downpipeRollup = (() => {
    const byDia = {};
    let totalPipeM = 0;
    let totalBends = 0;
    let totalJunctions = 0;
    let count = 0;
    pins.forEach(p => {
      if (p.type !== 'Outlet' || !p.downpipe_install) return;
      const stories = parseInt(p.downpipe_stories, 10) || 1;
      const preset = (DOWNPIPE_STORIES.find(s => s.value === stories) || {}).default_m || 3;
      const pipeM = parseFloat(p.downpipe_pipe_m) || preset;
      const dia = parseInt(p.outlet_diameter_mm, 10) || 80;
      if (!byDia[dia]) byDia[dia] = { count: 0, pipe_m: 0, bends: 0, junctions: 0 };
      byDia[dia].count += 1;
      byDia[dia].pipe_m += pipeM;
      byDia[dia].bends += 2;
      byDia[dia].junctions += 1;
      count += 1;
      totalPipeM += pipeM;
      totalBends += 2;
      totalJunctions += 1;
    });
    return { count, totalPipeM, totalBends, totalJunctions, byDia };
  })();

  // Aquaseal/Dektite material rollup — grouped by Aquaseal type. Sums
  // rivets + silicone (as %-of-tube). Surfaces under the Pipe/Flue rollup
  // for the Roofing trade quote.
  const aquasealRollup = (() => {
    const byType = {};
    let totalRivets = 0;
    let totalSilicone = 0;
    let totalCount = 0;
    pins.forEach(p => {
      if (!p.aquaseal_name) return;
      if (!byType[p.aquaseal_name]) byType[p.aquaseal_name] = { count: 0, rivets: 0, silicone: 0 };
      byType[p.aquaseal_name].count += 1;
      byType[p.aquaseal_name].rivets += parseInt(p.rivets_qty, 10) || 0;
      byType[p.aquaseal_name].silicone += parseFloat(p.silicone_pct) || 0;
      totalCount    += 1;
      totalRivets   += parseInt(p.rivets_qty, 10) || 0;
      totalSilicone += parseFloat(p.silicone_pct) || 0;
    });
    return { byType, totalCount, totalRivets, totalSiliconeTubes: +(totalSilicone / 100).toFixed(2) };
  })();

  // Backtray rollup — grouped by width_mm + summed length per pin.
  const backtrayRollup = (() => {
    const byWidth = {};
    let totalLen = 0;
    let totalCount = 0;
    pins.forEach(p => {
      if (!p.backtray_length_m || !(p.backtray_width_mm || 0)) return;
      const w = parseInt(p.backtray_width_mm, 10);
      const len = parseFloat(p.backtray_length_m) || 0;
      if (!byWidth[w]) byWidth[w] = { count: 0, length: 0 };
      byWidth[w].count += 1;
      byWidth[w].length += len;
      totalLen += len;
      totalCount += 1;
    });
    return { byWidth, totalLen, totalCount };
  })();

  // Ridge-cap rollup — grouped by cap type (Standard Concrete / Old Concrete
  // / Old Ross / Clay) for the Concrete Restore quote. Each cap entry
  // counts the auto-derived qty if the user only entered a ridge length.
  // Also rolls up the equivalent qty at the STD 420 mm quoting unit so
  // the office can compare against legacy concrete-tile rate tables.
  const ridgeCapRollup = (() => {
    const byType = {};
    let totalStd420 = 0;
    let totalLen    = 0;
    pins.forEach(p => {
      if (!isRidging(p.type)) return;
      // Concrete Restore slot defaults blank ridges to Standard Concrete
      // 420 mm so the operator sees the cap order quantity for every
      // ridge measurement without having to set ridge_cap_type explicitly.
      const capType = p.ridge_cap_type || (forceRidgeCapCount ? 'Standard Concrete' : '');
      if (!capType) return;
      const cap = RIDGE_CAP_LOOKUP[capType];
      if (!cap) return;
      const capM = cap.length_mm / 1000;
      const explicit = parseInt(p.ridge_cap_count, 10);
      const lenM = parseFloat(p.length_m) || 0;
      const qty = Number.isFinite(explicit) && explicit > 0 ? explicit : (lenM > 0 ? Math.ceil(lenM / capM) : 0);
      // Resolve a usable length for the std-420 conversion: prefer
      // operator-entered, else fall back to the derived length from
      // the cap count × actual cap length.
      const lenForStd = lenM > 0 ? lenM : (explicit > 0 ? explicit * capM : 0);
      const std420   = lenForStd > 0 ? Math.ceil(lenForStd / (STD_RIDGE_CAP_MM / 1000)) : 0;
      if (!byType[capType]) byType[capType] = { length_mm: cap.length_mm, qty: 0, total_m: 0, std420: 0 };
      byType[capType].qty += qty;
      byType[capType].total_m += lenForStd;
      byType[capType].std420 += std420;
      totalStd420 += std420;
      totalLen    += lenForStd;
    });
    return { byType, totalStd420, totalLen };
  })();

  // Reference codes — R1, R2, R3 (Ridge), V1, V2 (Valley), S1, S2 (Spouting), …
  // Derived purely from insertion order so adding/removing earlier pins
  // doesn't re-shuffle codes that the office may have already noted down.
  // Explicit prefix map — Flashing, Face & Flue all start with 'F' so the
  // single-letter heuristic would collide. Pipe & Outlet keep their natural
  // letters; the multi-prefix types pick the next-best mnemonic.
  const REF_PREFIX = {
    Ridge: 'R', Valley: 'V', Hip: 'H', Barge: 'B',
    Spouting: 'S', Outlet: 'O',
    Flashing: 'F', Face: 'FA', Penetration: 'PN',
    Length: 'L', LeakArea: 'LA',
  };
  const refMap = (() => {
    const m = {};
    const counters = {};
    pins.forEach(p => {
      const prefix = REF_PREFIX[p.type] || (p.type || 'X').charAt(0).toUpperCase();
      counters[prefix] = (counters[prefix] || 0) + 1;
      m[p.id] = `${prefix}${counters[prefix]}`;
    });
    return m;
  })();

  // Distinct existing lengths grouped by type — used by the edit dialog
  // to surface "copy this length" chips (user often has 3 ridges all
  // the same length, valley == 2 small ridges, etc.). Per type, list
  // distinct lengths > 0 along with one example ref so the chip reads
  // "Same as R1 · 6.2 m".
  const lengthsByType = pins.reduce((acc, p) => {
    const len = parseFloat(p.length_m) || 0;
    if (len <= 0) return acc;
    const t = p.type || 'Ridge';
    if (!acc[t]) acc[t] = [];
    const existing = acc[t].find(e => Math.abs(e.length - len) < 0.001);
    if (!existing) acc[t].push({ length: len, ref: refMap[p.id] });
    return acc;
  }, {});

  if (!templateId) {
    return (
      <div data-testid="roof-annotator-empty" className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
        Pick a roof design template above first — once selected, you can annotate it here.
      </div>
    );
  }

  return (
    <div data-testid="roof-diagram-annotator" className="space-y-2">
      {/* Iter211 — Toolbox accordion. Defaults to expanded; click the
          summary bar to collapse and reclaim screen space on small
          canvases. The summary line stays visible while collapsed so
          the operator can always see current mode + undo/redo counts
          at a glance. */}
      <details className="bg-slate-50 border border-slate-200 rounded-md" open data-testid="annot-toolbox">
        <summary className="cursor-pointer px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-600 hover:bg-slate-100 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            🧰 Annotator Tools
            <span className="text-slate-400 font-normal normal-case tracking-normal">
              · Mode: <span className="text-slate-700 font-semibold">{
                drawMode === 'pin' ? 'Pin (default)'
                : drawMode === 'penbtray' ? 'Pen + Btray dual-drop'
                : `${drawMode[0].toUpperCase()}${drawMode.slice(1)} → ${defaultLineType || 'pick type'}`
              }</span>
              {undoCount > 0 && <> · {undoCount} undo</>}
              {redoCount > 0 && <> · {redoCount} redo</>}
            </span>
          </span>
          <span className="text-slate-400 font-normal normal-case text-[10px]">click to toggle</span>
        </summary>
        <div className="px-3 py-2 border-t border-slate-200 bg-white">
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="font-bold text-slate-700">Annotate the diagram:</span>
        {/* Iter185++ — Dedicated quick-tap buttons for the four POSITION
            pin types (Pipe / Flue / Outlet / Face). Switches the canvas
            into Point mode AND pre-selects the lineType so the next
            click drops a typed pin without going through the dropdown.
            Iter211 — Face added per operator request so the same row
            covers the four quick-drop position types.  */}
        <div data-testid="annot-quick-point" className="flex items-center border border-slate-200 rounded overflow-hidden text-[11px] font-semibold">
          {[
            { key: 'penetration', label: '+ Penetration', colour: TYPE_LOOKUP.Penetration.color },
            { key: 'outlet',      label: '+ Outlet',      colour: TYPE_LOOKUP.Outlet.color },
            { key: 'face',        label: '+ Face',        colour: TYPE_LOOKUP.Face.color },
          ].map((m, i) => {
            const active = drawMode === 'point' && defaultLineType === m.key;
            return (
              <button
                key={m.key}
                data-testid={`annot-quick-${m.key}`}
                onClick={() => {
                  setDrawMode('point');
                  setDefaultLineType(m.key);
                  setActivePoly(null);
                  setActiveLineStart(null);
                }}
                className={`px-2 py-1 ${i > 0 ? 'border-l border-slate-200' : ''} ${active ? 'text-white' : 'bg-white hover:bg-slate-100'}`}
                style={{ background: active ? m.colour : undefined, color: active ? '#fff' : m.colour }}
                title={`Switch to Point mode & drop a ${m.label.replace('+ ', '')} pin on the next click`}
              >{m.label}</button>
            );
          })}
          {/* Iter229x — "+ Pen + Btray" shortcut. Switches the canvas
              into a dual-drop mode where the next click drops BOTH a
              Penetration pin AND a Backtray flashing pin (offset
              ~2%) pre-linked. Saves 3 clicks per typical install. */}
          {(() => {
            const active = drawMode === 'penbtray';
            const colour = TYPE_LOOKUP.Penetration.color;
            return (
              <button
                key="penbtray"
                data-testid="annot-quick-penbtray"
                onClick={() => {
                  setDrawMode('penbtray');
                  setActivePoly(null);
                  setActiveLineStart(null);
                }}
                className={`px-2 py-1 border-l border-slate-200 ${active ? 'text-white' : 'bg-white hover:bg-slate-100'}`}
                style={{ background: active ? colour : undefined, color: active ? '#fff' : colour }}
                title="Switch to dual-drop mode: next click drops a Penetration + a linked Backtray flashing in one action"
              >+ Pen + Btray</button>
            );
          })()}
        </div>
        {/* Iter184 — Tracer draw modes. Switches the canvas click-handler
            into stroke-tracing mode (point / straight / polygon) so the
            operator can add extra lines on top of the template WITHOUT
            altering the underlying roof_template (changes are stored
            per-appraisal). Selecting Pin returns to the legacy click-
            to-drop-a-pin behaviour.
            Iter211 — moved to sit immediately after the quick-pin-type
            group per operator request, so all eight related drawing
            buttons read as one cluster. */}
        <div data-testid="annot-draw-mode" className="flex items-center border border-slate-200 rounded overflow-hidden">
          {[
            { key: 'pin',      label: 'Pin'      },
            { key: 'point',    label: 'Point'    },
            { key: 'straight', label: 'Straight' },
            { key: 'polygon',  label: 'Polygon'  },
          ].map((m, i) => (
            <button
              key={m.key}
              data-testid={`annot-draw-mode-${m.key}`}
              onClick={() => { setDrawMode(m.key); setActivePoly(null); setActiveLineStart(null); }}
              className={`px-2 py-1 text-[11px] font-semibold ${i > 0 ? 'border-l border-slate-200' : ''} ${drawMode === m.key ? 'bg-[#D35400] text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
              title={
                m.key === 'pin'      ? 'Default: click drops a pin, drag draws a length-line pin'
                : m.key === 'point'  ? 'Click anywhere to drop a single-point trace (e.g. flue / outlet)'
                : m.key === 'straight' ? 'Click START then END to draw a straight tagged line'
                : 'Click each vertex; close by clicking the first vertex, double-click, or Enter'
              }
            >{m.label}</button>
          ))}
        </div>
        {drawMode !== 'pin' && (
          <select
            data-testid="annot-default-line-type"
            value={defaultLineType}
            onChange={(e) => setDefaultLineType(e.target.value)}
            className="text-[11px] border border-slate-200 rounded px-1.5 py-1 font-semibold"
            style={{ color: typeColour(pinTypeFromLine(defaultLineType) || 'Ridge') }}
            title="Pin type assigned to the next traced stroke"
          >
            <option value="ridge">Ridge</option>
            <option value="hip">Hip</option>
            <option value="valley">Valley</option>
            <option value="barge">Barge</option>
            <option value="gutter">Gutter</option>
            <option value="flashing">Flashing</option>
            <option value="face">Face (m²)</option>
            <option value="length">Length (m)</option>
            <option value="outlet">Outlet</option>
            <option value="penetration">Penetration</option>
          </select>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          {savedFlash && <span data-testid="annot-saved-flash" className="text-emerald-700 font-semibold flex items-center gap-1"><Check size={11} /> {savedFlash}</span>}
          {!savedFlash && (
            <span data-testid="annot-autosave-status" className="text-emerald-700 font-semibold flex items-center gap-1 opacity-70" title="Every pin change is automatically saved to the server. Manual save button is available below for peace of mind.">
              <Check size={11} /> Auto-saved
            </span>
          )}
          <button
            data-testid="annot-undo"
            onClick={handleUndo}
            disabled={undoCount === 0}
            title={undoCount > 0
              ? `Undo last change (${undoCount} step${undoCount === 1 ? '' : 's'} available). Shortcut: Ctrl+Z / ⌘+Z`
              : 'Nothing to undo on this diagram.'}
            className="flex items-center gap-1 px-2 py-1 border border-slate-300 text-slate-600 bg-white rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Undo2 size={11} /> Undo
            {undoCount > 0 && (
              <span className="text-[9px] font-mono-num bg-slate-200 text-slate-700 px-1 rounded">{undoCount}</span>
            )}
          </button>
          <button
            data-testid="annot-redo"
            onClick={handleRedo}
            disabled={redoCount === 0}
            title={redoCount > 0
              ? `Redo last undone change (${redoCount} step${redoCount === 1 ? '' : 's'} available). Shortcut: Ctrl+Shift+Z / ⌘+Shift+Z (or Ctrl+Y)`
              : 'Nothing to redo on this diagram.'}
            className="flex items-center gap-1 px-2 py-1 border border-slate-300 text-slate-600 bg-white rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Redo2 size={11} /> Redo
            {redoCount > 0 && (
              <span className="text-[9px] font-mono-num bg-slate-200 text-slate-700 px-1 rounded">{redoCount}</span>
            )}
          </button>
          <button data-testid="annot-save-all" onClick={() => save(pins)} disabled={pins.length === 0} className="flex items-center gap-1 px-2 py-1 border border-[#D35400] text-[#D35400] rounded hover:bg-[#FBE9D8] disabled:opacity-40" title="Force-save every pin on this diagram. Diagram already auto-saves on every change.">
            <Save size={11} /> Save
          </button>
          <button data-testid="annot-expand" onClick={() => setExpanded(v => !v)} className="p-1 rounded hover:bg-slate-100" title={expanded ? 'Compact view' : 'Expand'}>
            {expanded ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
          </button>
          {/* Iter182 — zoom toolbar. "Fit" resets back to 1×. */}
          <div data-testid="annot-zoom" className="flex items-center border border-slate-200 rounded overflow-hidden ml-1" title="Resize the template canvas — useful for off-axis or small roofs">
            <button onClick={() => setZoom(z => Math.max(0.5, +(z - 0.1).toFixed(2)))} className="px-1.5 py-1 hover:bg-slate-100 text-slate-600" data-testid="annot-zoom-out">−</button>
            <button onClick={() => setZoom(1)} className="px-2 py-1 text-[10px] font-semibold text-slate-600 border-x border-slate-200" data-testid="annot-zoom-fit">{Math.round(zoom * 100)}%</button>
            <button onClick={() => setZoom(z => Math.min(2.5, +(z + 0.1).toFixed(2)))} className="px-1.5 py-1 hover:bg-slate-100 text-slate-600" data-testid="annot-zoom-in">+</button>
          </div>
          <button data-testid="annot-reset" onClick={resetAll} disabled={pins.length === 0} className="flex items-center gap-1 px-2 py-1 border border-slate-200 text-slate-600 rounded hover:bg-slate-50 disabled:opacity-40">
            <RotateCcw size={11} /> Reset
          </button>
          {/* Iter182++ — Auto-arrange. Same algorithm the starter
              seeder uses: push every chip a few %-pts perpendicular
              to its line outward from the centroid, then relax
              overlaps. Cheap escape hatch when the operator's
              shuffled chips around and wants a clean re-layout. */}
          <button data-testid="annot-auto-arrange" onClick={autoArrangeChips} disabled={pins.length === 0} className="flex items-center gap-1 px-2 py-1 border border-sky-300 text-sky-700 rounded hover:bg-sky-50 disabled:opacity-40" title="Push every pin chip just off its line and pull them apart so nothing overlaps">
            <Magnet size={11} /> Auto-arrange
          </button>
          {/* Iter237 — "Lines off / on" toggle, canvas-aspect reset, and
              push-to-template buttons all removed. The server-side
              smart-crop in /backend/routes/roof_templates.py now stores
              tight roof images, so SVG strokes overlay the PNG lines
              1:1 by default — no duplicate-line problem to toggle around,
              and no manual canvas stretching to reset. */}
          {!hidePush && <button data-testid="annot-push" title="Sync all diagram pins into the Components form (Sheet, Ridge, Barge, Flashing, etc.). Preserves your manual edits on re-sync." onClick={pushToComponents} disabled={pushing} className="flex items-center gap-1 px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-semibold disabled:opacity-40">
            {pushing ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            Sync to Components
          </button>}
        </div>
        </div>
        </div>
      </details>

      {/* Iter211 — moved off the toolbar row so the 8 quick-buttons stay
          together regardless of viewport width. */}
      <p className="text-[11px] text-slate-500 leading-snug">
        {strokes.length ? `Hover the template lines — coloured highlight = tagged segment. Click a segment to pre-type the pin. ` : ''}Click anywhere to drop a pin · drag pins to reposition · click an existing pin to edit/delete.
      </p>

      {pushFlash && (
        <div data-testid="annot-push-flash" className={`px-3 py-2 rounded text-xs font-semibold ${pushFlash.ok ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'}`}>
          {pushFlash.msg}
        </div>
      )}

      {/* Iter161 Phase B — Leak-pin drop-mode banner. Active when the
          Remedial Repairs builder asked us to place a pin for one of
          its leak areas. Click anywhere on the diagram to drop it;
          ESC or "Cancel" exits. */}
      {leakDropMode && (
        <div
          data-testid="annot-leak-drop-banner"
          className="px-3 py-2 rounded text-xs font-semibold bg-rose-50 text-rose-800 ring-1 ring-rose-300 flex items-center justify-between gap-2"
        >
          <span>
            <span className="inline-block mr-1.5">⚠</span>
            Click anywhere on the diagram to drop a leak pin for <strong>{leakDropMode.area_label || 'this area'}</strong> (ESC to cancel).
          </span>
          <button
            data-testid="annot-leak-drop-cancel"
            onClick={() => setLeakDropMode(null)}
            className="text-rose-700 hover:text-rose-900 underline"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Side-by-side: diagram (~3/5) + pin list (~2/5) — gives the
          diagram more breathing room and shrinks the pin list. */}
      <div className={`grid gap-3 ${expanded ? '' : 'md:grid-cols-5'}`}>
      {/* Diagram + pins — 3/5 width by default, full width when expanded */}
      <div className={expanded ? '' : 'md:col-span-3'}>
      {/* Outer wrapper has padding so pin labels positioned on the outer
          perimeter (Spouting / Gutter) have room to render without being
          clipped. Inner container is where the click coords are measured.
          Padding bumped to p-10 (40px) per user request for more notes
          breathing room around the diagram. */}
      <div className={`relative bg-white border border-slate-200 rounded-md p-10 ${expanded ? 'min-h-[60vh]' : ''}`}>
      <div
        ref={containerRef}
        data-testid="annot-canvas"
        onPointerDown={handleDiagramPointerDown}
        onDoubleClick={handleDiagramDoubleClick}
        onPointerMove={(e) => {
          // Iter184 — track the cursor in % coords while a tracer is
          // active, so the in-progress preview (rubber-band line for
          // straight mode, next-vertex preview for polygon) can render.
          if (drawMode !== 'pin' && containerRef.current) {
            const r = containerRef.current.getBoundingClientRect();
            setCursorPct({
              x_pct: ((e.clientX - r.left) / r.width) * 100,
              y_pct: ((e.clientY - r.top) / r.height) * 100,
            });
          }
          // Iter182 — hover highlight for tagged segments. Skip while
          // mid-drag-draw or interacting with a pin.
          if (drawing || dragging) return;
          if (!strokes.length || !containerRef.current) return;
          const r = containerRef.current.getBoundingClientRect();
          const x = ((e.clientX - r.left) / r.width) * 100;
          const y = ((e.clientY - r.top) / r.height) * 100;
          const hit = findStrokeUnder(x, y, 2.5);
          setHoveredStroke(hit ? hit.strokeIdx : null);
        }}
        onPointerLeave={() => { setHoveredStroke(null); setCursorPct(null); }}
        className={`relative cursor-crosshair select-none transition-transform origin-top-left ${expanded ? 'min-h-[55vh]' : ''}`}
        style={{ aspectRatio: expanded ? undefined : (imgNaturalAspect ? `${imgNaturalAspect}` : '4 / 3'), transform: zoom !== 1 ? `scale(${zoom})` : undefined, transformOrigin: 'top left' }}
      >
        <img
          src={`/api/roof-templates/${templateId}/image`}
          alt="Roof design"
          onLoad={(e) => {
            setImgLoaded(true);
            // Iter237 — capture the image's natural aspect so the
            // canvas matches it exactly. PNG fills the canvas with
            // ZERO letterboxing → SVG strokes overlay 1:1 → no
            // doubled lines.
            const t = e.target;
            if (t && t.naturalWidth > 0 && t.naturalHeight > 0) {
              setImgNaturalAspect(t.naturalWidth / t.naturalHeight);
            }
          }}
          className="absolute inset-0 w-full h-full object-contain bg-slate-50"
          draggable={false}
        />
        {!imgLoaded && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-xs">
            <Loader2 size={14} className="animate-spin mr-1.5" /> Loading diagram…
          </div>
        )}

        {/* Iter182 — invisible-by-default segment overlay. Each saved
            stroke (hip/ridge/gutter/barge/etc.) gets an SVG line
            element that lights up when the cursor is over it. A real
            click on the line is handled by the parent onPointerDown
            (we hit-test the stroke under the cursor and pre-type the
            new pin); this overlay is just the visual feedback. */}
        {strokes.length > 0 && (
          <svg
            data-testid="annot-segment-overlay"
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            {strokes.map((s, si) => {
              const pts = s?.points || [];
              if (!pts.length) return null;
              const ltKey = (s.lineType || '').toLowerCase();
              const pinType = LINE_TO_PIN_TYPE[ltKey];
              const colour = (TYPE_LOOKUP[pinType] || {}).color || '#94A3B8';
              const isHovered = hoveredStroke === si;
              // Iter182+ — when another stroke of the SAME lineType is
              // being hovered, dim-highlight this one too so the
              // operator instantly sees how many of each type are on
              // the roof (e.g. hover one Hip → all 4 Hips light up).
              const hoveredLT = hoveredStroke !== null
                ? (strokes[hoveredStroke]?.lineType || '').toLowerCase() : null;
              const isPeer = !isHovered && hoveredLT && hoveredLT === ltKey;
              if (s.type === 'point') {
                return (
                  <circle key={`seg-${si}`} cx={pts[0].x_pct} cy={pts[0].y_pct}
                    r={isHovered ? 1.4 : isPeer ? 1.1 : 0.8}
                    fill={colour}
                    fillOpacity={isHovered ? 0.95 : isPeer ? 0.55 : 0.4}
                    stroke="#fff" strokeWidth={isHovered ? 0.4 : 0} />
                );
              }
              const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x_pct},${p.y_pct}`).join(' ') + (s.closed ? ' Z' : '');
              return (
                <path
                  key={`seg-${si}`}
                  d={d}
                  fill="none"
                  stroke={colour}
                  strokeWidth={isHovered ? 0.9 : isPeer ? 0.55 : 0}
                  strokeOpacity={isHovered ? 0.9 : isPeer ? 0.45 : 0}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray={isPeer ? '0.8,0.6' : undefined}
                />
              );
            })}
          </svg>
        )}

        {/* Iter184 — Local strokes layer. Always rendered with a
            visible coloured stroke so the operator clearly sees the
            extra lines they've drawn on top of the template. Each
            stroke gets a tiny ✕ chip the operator can click to remove
            it (also wipes the auto-seeded pin). Rendered ABOVE the
            invisible segment overlay so the operator can interact
            with their own work without fighting the template hit-
            box. */}
        {localStrokes.length > 0 && (
          <svg
            data-testid="annot-local-stroke-overlay"
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            style={{ pointerEvents: 'none' }}
          >
            {localStrokes.map((s) => {
              const pts = s?.points || [];
              if (!pts.length) return null;
              const pinType = pinTypeFromLine(s.lineType);
              const colour = (TYPE_LOOKUP[pinType] || {}).color || '#475569';
              if (s.type === 'point' || pts.length < 2) {
                return (
                  <circle
                    key={`ls-${s.id}`}
                    cx={pts[0].x_pct} cy={pts[0].y_pct}
                    r={1.4}
                    fill={colour} fillOpacity={0.9}
                    stroke="#fff" strokeWidth={0.4}
                  />
                );
              }
              const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x_pct},${p.y_pct}`).join(' ') + (s.closed ? ' Z' : '');
              return (
                <path
                  key={`ls-${s.id}`}
                  d={d}
                  fill={s.closed ? colour : 'none'}
                  fillOpacity={s.closed ? 0.10 : 0}
                  stroke={colour}
                  strokeWidth={0.7}
                  strokeOpacity={0.95}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              );
            })}
          </svg>
        )}

        {/* Iter184 — per-local-stroke ✕ delete chip, rendered as plain
            DOM so it can capture clicks (SVG layer above is pointer-
            events: none). Positioned at the stroke's midpoint. */}
        {localStrokes.length > 0 && drawMode === 'pin' && localStrokes.map((s) => {
          const pts = s?.points || [];
          if (!pts.length) return null;
          let cx = pts[0].x_pct, cy = pts[0].y_pct;
          if (pts.length >= 2) {
            const mid = polylineMidpoint(pts, !!s.closed);
            cx = mid.x; cy = mid.y;
          }
          return (
            <button
              key={`ls-del-${s.id}`}
              data-testid={`annot-local-stroke-delete-${s.id}`}
              onClick={(e) => { e.stopPropagation(); deleteLocalStroke(s.id); }}
              className="absolute w-4 h-4 -ml-2 -mt-2 rounded-full bg-white text-slate-500 hover:text-rose-600 hover:bg-rose-50 border border-slate-300 hover:border-rose-400 shadow text-[10px] flex items-center justify-center z-30"
              style={{ left: `${cx}%`, top: `${cy}%` }}
              title={`Delete this traced ${s.lineType || 'stroke'}`}
            ><X size={10} /></button>
          );
        })}

        {/* Iter184 — In-progress polygon preview. Renders the vertices
            placed so far + a dashed rubber-band to the cursor. Click
            near the first vertex (small ring) to close the polygon. */}
        {drawMode === 'polygon' && activePoly && (
          <svg
            data-testid="annot-tracer-preview-polygon"
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            {(() => {
              const colour = typeColour(pinTypeFromLine(defaultLineType) || 'Ridge');
              const pts = activePoly.points;
              const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x_pct},${p.y_pct}`).join(' ');
              const last = pts[pts.length - 1];
              return (
                <>
                  <path d={d} fill="none" stroke={colour} strokeWidth={0.7} strokeOpacity={0.95} strokeLinecap="round" strokeLinejoin="round" />
                  {cursorPct && (
                    <line x1={last.x_pct} y1={last.y_pct} x2={cursorPct.x_pct} y2={cursorPct.y_pct} stroke={colour} strokeWidth={0.5} strokeOpacity={0.65} strokeDasharray="0.8,0.6" />
                  )}
                  {pts.map((p, i) => (
                    <circle key={`apv-${i}`} cx={p.x_pct} cy={p.y_pct} r={i === 0 ? 1.4 : 0.9} fill="#fff" stroke={colour} strokeWidth={0.5} />
                  ))}
                </>
              );
            })()}
          </svg>
        )}

        {/* Iter184 — In-progress straight-line preview. Renders a
            dashed rubber-band from the click START to the current
            cursor position. */}
        {drawMode === 'straight' && activeLineStart && cursorPct && (
          <svg
            data-testid="annot-tracer-preview-straight"
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            {(() => {
              const colour = typeColour(pinTypeFromLine(defaultLineType) || 'Ridge');
              return (
                <>
                  <line x1={activeLineStart.x_pct} y1={activeLineStart.y_pct} x2={cursorPct.x_pct} y2={cursorPct.y_pct} stroke={colour} strokeWidth={0.6} strokeDasharray="0.8,0.6" strokeOpacity={0.85} />
                  <circle cx={activeLineStart.x_pct} cy={activeLineStart.y_pct} r={1.1} fill="#fff" stroke={colour} strokeWidth={0.5} />
                </>
              );
            })()}
          </svg>
        )}

        {/* Iter184 — Drop-cursor crosshair for Point mode, plus an
            instruction banner so the operator knows the canvas is in
            tracer mode (and how to leave it). */}
        {drawMode !== 'pin' && (
          <div
            data-testid="annot-tracer-banner"
            className="absolute top-1 right-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded text-white shadow pointer-events-none z-30"
            style={{ backgroundColor: typeColour(pinTypeFromLine(defaultLineType) || 'Ridge') }}
          >
            Tracer: {drawMode}
            {drawMode === 'polygon' && activePoly ? ` · ${activePoly.points.length} pts · Enter to close` : ''}
            {drawMode === 'straight' && activeLineStart ? ' · click END' : ''}
            {' · ESC to exit'}
          </div>
        )}

        {/* Iter237 — drag-to-resize handle removed. Server-side
            smart-crop now ensures the PNG and SVG occupy the same
            rectangle, so manual canvas stretching is no longer
            useful. The persisted `roof_diagram_canvas.aspect` field
            on the appraisal is ignored by the renderer — left in
            the DB as harmless legacy data. */}

        {/* Iter182 — hover label: when a tagged segment is highlighted
            show its line type ("Hip" / "Ridge" / "Gutter" / ...) AND
            the total count of same-type strokes on this template so
            the operator sees the take-off at a glance. */}
        {hoveredStroke !== null && strokes[hoveredStroke]?.lineType && (() => {
          const ltKey = (strokes[hoveredStroke].lineType || '').toLowerCase();
          const pinType = LINE_TO_PIN_TYPE[ltKey];
          const colour = (TYPE_LOOKUP[pinType] || {}).color || '#475569';
          const label = pinType || strokes[hoveredStroke].lineType;
          const peerCount = strokes.filter(s => (s.lineType || '').toLowerCase() === ltKey).length;
          return (
            <div
              data-testid="annot-segment-hover-label"
              className="absolute top-1 left-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded text-white shadow-lg pointer-events-none z-30"
              style={{ backgroundColor: colour }}
            >
              Click → {label}{peerCount > 1 ? ` · ${peerCount} on this roof` : ''}
            </div>
          );
        })()}

        {/* SVG overlay — persisted lines (drag-drawn pins) + in-progress
            preview while the user is dragging a new line. preserveAspectRatio
            "none" so coords map 1:1 to the parent's % grid. Arrowheads at
            both ends so the operator can clearly see start/end of each run. */}
        <svg
          data-testid="annot-line-overlay"
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <defs>
            {/* Double-headed dimension arrow — `context-stroke` fills the
                arrow head with whatever colour the line is drawn in, so
                one marker definition serves every pin type. */}
            <marker id="ad-arr-end" viewBox="0 0 10 10" refX="9" refY="5"
                    markerWidth="3" markerHeight="3" orient="auto-start-reverse"
                    markerUnits="strokeWidth">
              <path d="M0,0 L10,5 L0,10 Z" fill="context-stroke" />
            </marker>
          </defs>
          {pins.filter(p => p.x_pct_end !== undefined && p.y_pct_end !== undefined).map(p => {
            const t = TYPE_LOOKUP[p.type] || TYPE_LOOKUP['Ridge'];
            // Iter182++ — when this pin is bound to a tagged segment,
            // render the FULL polyline (multi-vertex) so kinked hips
            // / dog-legs / closed gutters look correct. Falls back to
            // a straight 2-point line for unbound (operator-drawn) pins.
            const seg = (p.segment_idx !== undefined && p.segment_idx !== null) ? strokes[p.segment_idx] : null;
            if (seg && Array.isArray(seg.points) && seg.points.length >= 2) {
              const pts = seg.points;
              const d = pts.map((pt, i) => `${i === 0 ? 'M' : 'L'}${pt.x_pct},${pt.y_pct}`).join(' ') + (seg.closed ? ' Z' : '');
              // Dashed connector from the polyline midpoint to the
              // free-moving label chip position (drawn ON TOP of the
              // image so it's always visible).
              const mid = polylineMidpoint(pts, !!seg.closed);
              const lx = p.x_pct_label !== undefined ? p.x_pct_label : mid.x;
              const ly = p.y_pct_label !== undefined ? p.y_pct_label : mid.y;
              const connectorVisible = Math.hypot(lx - mid.x, ly - mid.y) > 1.5;
              // Iter237c — Skip the SVG polyline render for auto_starter
              // pins. The template PNG already shows the line baked in
              // (in the original traced colour); drawing an SVG copy on
              // top creates the "doubled-line" effect — two parallel
              // strokes in slightly different widths/colours peeking
              // around each other. Operator-drawn (non auto_starter)
              // pins ALWAYS render their line because no PNG copy
              // exists for those.
              const hidePolyline = !!p.auto_starter;
              return (
                <g key={`line-${p.id}`}>
                  {!hidePolyline && (
                    <path
                      d={d}
                      fill="none"
                      stroke={t.color}
                      strokeWidth="0.42"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      opacity="0.95"
                    />
                  )}
                  {connectorVisible && (
                    <line
                      x1={mid.x} y1={mid.y}
                      x2={lx}    y2={ly}
                      stroke={t.color}
                      strokeWidth="0.18"
                      strokeDasharray="0.7,0.6"
                      opacity="0.85"
                    />
                  )}
                </g>
              );
            }
            return (
              <line
                key={`line-${p.id}`}
                x1={p.x_pct} y1={p.y_pct}
                x2={p.x_pct_end} y2={p.y_pct_end}
                stroke={t.color}
                strokeWidth="0.35"
                strokeLinecap="butt"
                opacity="0.95"
                markerStart="url(#ad-arr-end)"
                markerEnd="url(#ad-arr-end)"
              />
            );
          })}
          {drawing && (
            <line
              data-testid="annot-line-preview"
              x1={drawing.x1} y1={drawing.y1}
              x2={drawing.x2} y2={drawing.y2}
              stroke="#D35400"
              strokeWidth="0.3"
              strokeDasharray="2 1"
              strokeLinecap="butt"
              markerStart="url(#ad-arr-end)"
              markerEnd="url(#ad-arr-end)"
            />
          )}
          {/* Iter242 — Visual link between paired Penetration ↔ Flashing
              pins. For every Flashing pin that carries
              `linked_pipe_pin_id`, draw a thin dashed line from its
              label position to the linked Penetration's pin position.
              Helps the operator confirm at a glance which pipe each
              backtray is covering without opening the edit dialog. */}
          {pins.filter(f => f.type === 'Flashing' && f.linked_pipe_pin_id).map(f => {
            const pen = pins.find(q => q.id === f.linked_pipe_pin_id && q.type === 'Penetration');
            if (!pen) return null;
            const fx = f.x_pct_label !== undefined ? f.x_pct_label : f.x_pct;
            const fy = f.y_pct_label !== undefined ? f.y_pct_label : f.y_pct;
            const px = pen.x_pct;
            const py = pen.y_pct;
            if (fx === undefined || fy === undefined || px === undefined || py === undefined) return null;
            return (
              <line
                key={`pen-flash-link-${f.id}`}
                data-testid={`annot-pen-flash-link-${f.id}`}
                x1={fx} y1={fy}
                x2={px} y2={py}
                stroke="#0EA5E9"
                strokeWidth="0.35"
                strokeDasharray="1.2 0.8"
                strokeLinecap="round"
                opacity="0.7"
              />
            );
          })}
        </svg>

        {pins.map(pin => {
          const t = TYPE_LOOKUP[pin.type] || TYPE_LOOKUP['Ridge'];
          const isDragging = dragging === pin.id;
          const ref = refMap[pin.id] || '';
          const isLine = pin.x_pct_end !== undefined && pin.y_pct_end !== undefined;
          const isOutlet = t.position;
          // Iter236 — Visual link badges. Two flavours:
          //   1. Flashing pin (Backtray) carries `linked_pipe_pin_id`
          //      pointing at a Penetration → show "→ PN3" badge and
          //      a tiny Ø chip when a Dektite is resolved.
          //   2. Penetration pin → scan all pins for the reciprocal
          //      Flashing back-link so we can show "← F2".
          // Cheap O(n) scan acceptable here (≤30 pins typically).
          const linkedPenRef = (pin.type === 'Flashing' && pin.linked_pipe_pin_id)
            ? (refMap[pin.linked_pipe_pin_id] || '')
            : '';
          const dektiteName = pin.linked_dektite_name || '';
          const backlinkFlashings = isOutlet
            ? pins.filter(o => o.type === 'Flashing' && o.linked_pipe_pin_id === pin.id)
            : [];
          // Iter182++ — chip position priority: explicit label coord
          // > segment midpoint (when segment-bound) > line midpoint >
          // point coord. Lets the operator drag the tag freely while
          // the underlying line stays glued to the segment.
          let chipX, chipY;
          if (pin.x_pct_label !== undefined && pin.y_pct_label !== undefined) {
            chipX = pin.x_pct_label; chipY = pin.y_pct_label;
          } else if (pin.segment_idx !== undefined && pin.segment_idx !== null
                     && strokes[pin.segment_idx]?.points?.length >= 2) {
            const seg = strokes[pin.segment_idx];
            const mid = polylineMidpoint(seg.points, !!seg.closed);
            chipX = mid.x; chipY = mid.y;
          } else if (isLine) {
            chipX = (pin.x_pct + pin.x_pct_end) / 2;
            chipY = (pin.y_pct + pin.y_pct_end) / 2;
          } else {
            chipX = pin.x_pct; chipY = pin.y_pct;
          }
          // Diameter readout — used by all POSITION pins (Outlet/Pipe/Flue).
          // Outlet additionally has a shape (round vs square). Pipe/Flue
          // are always round so render with the ⌀ glyph.
          const diaMm = pin.outlet_diameter_mm || t.defaultDia || 80;
          const outletLabel = !isOutlet
            ? null
            : pin.type === 'Outlet'
              ? `${diaMm}${(pin.outlet_shape || 'Round').charAt(0) === 'S' ? '□' : '⌀'}`
              : `⌀${diaMm}`;
          return (
            <button
              key={pin.id}
              data-testid={`annot-pin-${pin.id}`}
              onPointerDown={(e) => handlePinPointerDown(e, pin)}
              onClick={(e) => { e.stopPropagation(); }}
              type="button"
              className={`absolute -translate-x-1/2 -translate-y-full transform group ${isDragging ? 'cursor-grabbing z-30' : 'cursor-grab z-20 hover:z-30'} ${flashPinId === pin.id ? 'pin-flash z-40' : ''}`}
              style={{ left: `${chipX}%`, top: `${chipY}%` }}
              title={isOutlet
                ? `${ref} · ${typeLabel(pin.type)} · ${diaMm}mm${pin.type === 'Outlet' ? ` ${pin.outlet_shape || 'Round'}` : ''}${pin.label ? ` · ${pin.label}` : ''}${backlinkFlashings.length ? ` · linked from ${backlinkFlashings.map(f => refMap[f.id] || '').filter(Boolean).join(', ')}` : ''}`
                : `${ref} · ${typeLabel(pin.type)} · ${pin.length_m || 0}${t.unit || 'm'}${pin.label ? ` · ${pin.label}` : ''}${isLine ? ' · line' : ''}${pin.type === 'Barge' && pin.barge_role === 'starter' ? ' · STARTER barge' : ''}${linkedPenRef ? ` · linked to ${linkedPenRef}` : ''}${dektiteName ? ` · ${dektiteName}` : ''}`}
            >
              {isOutlet ? (
                /* Position pin — coloured circle / square marker with a small
                   readout chip above. Pipe & Flue are always circular; Outlet
                   honours its round/square preference. Outlet pins flagged
                   for a new downpipe get an extra ⇩ badge so the office can
                   spot them at a glance. */
                <div className="flex flex-col items-center -mb-0.5">
                  <span className="text-[9px] font-bold text-slate-800 bg-white/90 px-1 rounded shadow-sm whitespace-nowrap">
                    {ref} {outletLabel}
                    {pin.downpipe_install && (
                      <span
                        data-testid={`annot-pin-${pin.id}-downpipe-badge`}
                        className="ml-0.5 inline-flex items-center justify-center bg-[#15803D] text-white rounded-sm px-0.5 text-[8px] font-bold align-middle"
                        title={`New downpipe · ${pin.downpipe_stories || 1}-storey`}
                      >⇩{pin.downpipe_stories || 1}</span>
                    )}
                    {/* Iter236 — back-link badge. If a Flashing pin
                        references this Penetration, surface it on
                        the chip so the operator sees the relationship
                        without opening the edit panel. */}
                    {backlinkFlashings.length > 0 && (
                      <span
                        data-testid={`annot-pin-${pin.id}-flashing-backlink`}
                        className="ml-0.5 inline-flex items-center bg-amber-500 text-white rounded-sm px-0.5 text-[8px] font-bold align-middle"
                        title={`Linked from ${backlinkFlashings.map(f => refMap[f.id] || '').filter(Boolean).join(', ')}`}
                      >← {(refMap[backlinkFlashings[0].id] || '').replace(/^F/, 'F')}</span>
                    )}
                  </span>
                  <span
                    className="block w-4 h-4 rounded-full ring-2 ring-white shadow mt-0.5"
                    style={{ background: typeColour(pin.type), borderRadius: pin.type === 'Outlet' && (pin.outlet_shape || 'Round').charAt(0) === 'S' ? '2px' : '50%' }}
                  />
                </div>
              ) : (
                <>
                  <div
                    className="flex items-center gap-1 px-1.5 py-0.5 rounded-md text-white text-[10px] font-bold shadow-md whitespace-nowrap"
                    style={{ background: typeColour(pin.type) }}
                  >
                    {/* Iter236 — Starter-barge visual indicator. Only
                        starter barges get a marker (▶); finish barges
                        render plain. Drives quick visual scanning of
                        a roof's barge sequence. */}
                    {pin.type === 'Barge' && pin.barge_role === 'starter' && (
                      <span
                        data-testid={`annot-pin-${pin.id}-starter-badge`}
                        className="inline-flex items-center justify-center bg-white/30 rounded-sm px-1 text-[9px] font-extrabold"
                        title="Starter barge"
                      >▶</span>
                    )}
                    <span className="font-mono-num">{ref}</span>
                    <span className="font-mono-num opacity-90">{pin.length_m ? `${pin.length_m}${unitForPin(pin)}${pin.length_source === 'plan' && pin.plan_length_m ? ` · plan ${pin.plan_length_m}` : ''}` : '—'}</span>
                    {/* Iter236 — Flashing-side link badges. When a
                        Backtray (or other Flashing pin) carries a
                        linked_pipe_pin_id, show the resolved ref
                        (→ PN3) plus a tiny Ø chip when a Dektite was
                        auto-resolved against that pen pin. Helps
                        operators see the cascade at a glance. */}
                    {linkedPenRef && (
                      <span
                        data-testid={`annot-pin-${pin.id}-pen-link`}
                        className="inline-flex items-center bg-white/30 rounded-sm px-1 text-[9px] font-bold"
                        title={`Linked to Penetration ${linkedPenRef}${dektiteName ? ` · ${dektiteName}` : ''}`}
                      >→ {linkedPenRef}</span>
                    )}
                    {dektiteName && pin.outlet_diameter_mm && (
                      <span
                        data-testid={`annot-pin-${pin.id}-dektite-chip`}
                        className="inline-flex items-center bg-emerald-100 text-emerald-900 ring-1 ring-emerald-300 rounded-sm px-1 text-[9px] font-bold"
                        title={`Linked Dektite: ${dektiteName}`}
                      >⌀{pin.outlet_diameter_mm}</span>
                    )}
                    {/* Ridge pins flagged with a cap type show the derived
                        cap qty inline — e.g. "15 caps". Surfaced on the
                        canvas so the Concrete Restore diagram reads as a
                        complete take-off without flipping to the rail.
                        Iter211 — hidden when the roof material doesn't
                        use discrete caps (Iron / Tin Tile / Butynol /
                        Polycarbonate). */}
                    {isRidging(pin.type) && pin.ridge_cap_type && showRidgeCapQty && (() => {
                      const cap = RIDGE_CAP_LOOKUP[pin.ridge_cap_type];
                      if (!cap) return null;
                      const capM = cap.length_mm / 1000;
                      const explicit = parseInt(pin.ridge_cap_count, 10);
                      const len = parseFloat(pin.length_m) || 0;
                      const qty = Number.isFinite(explicit) && explicit > 0
                        ? explicit
                        : (len > 0 ? Math.ceil(len / capM) : 0);
                      if (!qty) return null;
                      return (
                        <span
                          data-testid={`annot-pin-${pin.id}-cap-qty`}
                          className="font-mono-num bg-white/30 px-1 rounded text-[9px]"
                          title={`${qty} × ${pin.ridge_cap_type} (${cap.length_mm} mm)`}
                        >{qty}c</span>
                      );
                    })()}
                  </div>
                  <span
                    className="block w-2.5 h-2.5 rounded-full mx-auto -mt-px ring-2 ring-white shadow"
                    style={{ background: t.color }}
                  />
                </>
              )}
            </button>
          );
        })}
      </div>
      </div>
      {/* Line-colour key for the underlying roof template — colour swatches
          match the pin chip colours so the office can map key → pin at a
          glance. Spouting/Gutter is shown in green even though the template
          line is drawn black; we colour-code by SEMANTIC type, not by ink. */}
      <div className="mt-2 flex items-center gap-3 flex-wrap text-[10px] text-slate-600 px-1">
        <span className="font-bold uppercase tracking-wider text-slate-500 text-[9px]">Key:</span>
        <span className="inline-flex items-center gap-1.5">
          <svg width="22" height="6"><line x1="0" y1="3" x2="22" y2="3" stroke="#15803D" strokeWidth="3" /></svg>
          <span>Outside = <b className="text-[#15803D]">Gutter / Spouting</b></span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <svg width="22" height="6"><line x1="0" y1="3" x2="22" y2="3" stroke="#1E40AF" strokeWidth="3" /></svg>
          <span>Internal Blue = <b className="text-[#1E40AF]">Ridge</b></span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <svg width="22" height="6"><line x1="0" y1="3" x2="22" y2="3" stroke="#9F1239" strokeWidth="3" strokeDasharray="4 2" /></svg>
          <span>Dotted Red = <b className="text-[#9F1239]">Valley</b></span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <svg width="22" height="6"><line x1="0" y1="3" x2="22" y2="3" stroke="#A16207" strokeWidth="3" /></svg>
          <span>Brown = <b className="text-[#A16207]">Barge</b></span>
        </span>
      </div>
      </div>

      {/* Right pane — pin list (visible only in side-by-side mode) */}
      {!expanded && (
      <div data-testid="annot-pin-list" className="md:col-span-2 bg-slate-50 rounded-md border border-slate-200 p-2 space-y-1 max-h-[calc(75vw*0.75/3)] md:max-h-none md:overflow-auto">
        {/* Take-off Summary tile removed in iter85 — duplicated the
            sub-totals grid below. The `annot-subtotals` grid is the
            single source of truth for pin-data totals. */}

        {pins.length > 0 && (
          <div data-testid="annot-subtotals" className="mb-2 grid grid-cols-2 gap-1 px-1">
            {Object.entries(totals).map(([type, t]) => {
              const diaList = t.position ? Array.from(t.diameters || []).sort((a,b)=>a-b) : null;
              return (
                <div key={type} data-testid={`annot-subtotal-${type}`}
                     className="flex items-center gap-1.5 bg-white rounded ring-1 ring-slate-200 px-1.5 py-1 text-[10px]">
                  <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: typeColour(type) }} />
                  <span className="font-semibold text-slate-700 flex-1 truncate" style={{ color: typeColour(type) }}>{typeLabel(type)}</span>
                  {t.position ? (
                    <span className="font-mono-num font-bold text-slate-900 truncate" title={diaList?.join(', ') + ' mm'}>
                      {diaList && diaList.length ? `${diaList.join('/')} mm` : '—'}
                    </span>
                  ) : (
                    <span className="font-mono-num font-bold text-slate-900">{t.length.toFixed(1)}{t.unit}</span>
                  )}
                  {t.cap_qty > 0 && showRidgeCapQty && (
                    <span
                      className="font-mono-num font-bold text-amber-800 bg-amber-100 ring-1 ring-amber-200 rounded px-1 text-[9px]"
                      title={`${t.cap_qty} ridge cap${t.cap_qty === 1 ? '' : 's'}`}
                    >{t.cap_qty}c</span>
                  )}
                  {/* Outlet sub-total: "+N DP" chip showing how many of
                      this row's outlets include a new-downpipe install.
                      Hidden when no outlets in this group ship a DP. */}
                  {type === 'Outlet' && downpipeRollup.count > 0 && (
                    <span
                      data-testid="annot-subtotal-outlet-dp"
                      className="font-mono-num font-bold text-emerald-800 bg-emerald-100 ring-1 ring-emerald-200 rounded px-1 text-[9px]"
                      title={`${downpipeRollup.count} of these outlets include a new-downpipe install`}
                    >+{downpipeRollup.count} DP</span>
                  )}
                  {t.cap_std420 > 0 && t.cap_std420 !== t.cap_qty && showRidgeCapQty && (
                    <span
                      className="font-mono-num font-bold text-indigo-700 bg-indigo-100 ring-1 ring-indigo-200 rounded px-1 text-[9px]"
                      title={`${t.cap_std420} caps at the std ${STD_RIDGE_CAP_MM} mm quoting unit`}
                    >{t.cap_std420}@{STD_RIDGE_CAP_MM}</span>
                  )}
                  <span className="font-mono-num text-slate-400">×{t.count}</span>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex items-center justify-between mb-1 px-1">
          <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Pins ({pins.length})</h4>
          {pins.length > 0 && (
            <span className="text-[10px] text-slate-400">Click a row to edit</span>
          )}
        </div>
        {pins.length === 0 && (
          <div className="text-xs text-slate-400 italic px-2 py-3 text-center">Click anywhere on the diagram to drop your first pin.</div>
        )}
        {/* Iter183 — Sort the side-panel pin list ALPHABETICALLY by the
            displayed type label (Barge, Box-gutter, Flashing, Gutter,
            Hip, Ridge, Spouting, Valley, …) then by pin number ascending.
            Uses typeLabel() so any per-trade label overrides (e.g.
            "Eave Gutter") drive the ordering too. */}
        {[...pins].sort((a, b) => {
          const la = (typeLabel(a.type) || '').toLowerCase();
          const lb = (typeLabel(b.type) || '').toLowerCase();
          if (la !== lb) return la < lb ? -1 : 1;
          // Same label → sort by the trailing number in refMap (Sp1 < Sp2)
          const ra = refMap[a.id] || '';
          const rb = refMap[b.id] || '';
          const na = parseInt(ra.replace(/\D+/g, ''), 10) || 0;
          const nb = parseInt(rb.replace(/\D+/g, ''), 10) || 0;
          return na - nb;
        }).map(pin => {
          const t = TYPE_LOOKUP[pin.type] || TYPE_LOOKUP['Ridge'];
          const isPos = !!t.position;
          return (
            <div
              key={pin.id}
              data-testid={`annot-pin-row-${pin.id}`}
              onClick={() => setEditingPin(pin)}
              className="flex items-center gap-2 px-2 py-1.5 bg-white rounded ring-1 ring-slate-200 hover:ring-[#D35400] cursor-pointer text-xs"
            >
              <span
                className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                style={{ background: t.color }}
              />
              <span className="font-mono-num font-bold text-slate-900 w-8 shrink-0">{refMap[pin.id]}</span>
              <span className="font-semibold text-slate-700 w-16 shrink-0" style={{ color: typeColour(pin.type) }}>{typeLabel(pin.type)}</span>
              <span className="font-mono-num text-slate-900 w-20 shrink-0 text-right">
                {isPos
                  ? `⌀${pin.outlet_diameter_mm || t.defaultDia || '?'}mm${pin.type === 'Outlet' ? ` ${(pin.outlet_shape || 'R').charAt(0)}` : ''}`
                  : `${pin.length_m || 0}${unitForPin(pin)}`
                }
              </span>
              {/* "+ DP" chip — flags Outlet pins that include a new-downpipe
                  install. Surfaces here on the pin rail so the office can
                  scan which outlets need a downpipe without opening each
                  one's editor. Tooltip shows the configured length + stories. */}
              {pin.type === 'Outlet' && pin.downpipe_install && (() => {
                const stories = parseInt(pin.downpipe_stories, 10) || 1;
                const preset = (DOWNPIPE_STORIES.find(s => s.value === stories) || {}).default_m || 3;
                const pipeM = parseFloat(pin.downpipe_pipe_m) || preset;
                return (
                  <span
                    data-testid={`annot-pin-row-${pin.id}-downpipe`}
                    className="text-[10px] font-mono-num font-bold bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200 rounded px-1 shrink-0"
                    title={`Install new downpipe — ${stories} storey · ${pipeM} m pipe`}
                  >+DP {pipeM}m</span>
                );
              })()}
              {/* Flashing sub-type chip on the rail row */}
              {pin.type === 'Flashing' && pin.flashing_type && (
                <span
                  data-testid={`annot-pin-row-${pin.id}-flashing`}
                  className="text-[10px] font-mono-num font-semibold bg-orange-100 text-orange-800 ring-1 ring-orange-200 rounded px-1 shrink-0"
                  title={`Flashing kind: ${pin.flashing_type}`}
                >{pin.flashing_type}</span>
              )}
              {/* Ridge-cap qty inline on the row when a cap type is set,
                  OR when the parent forces ridge-cap counts (Concrete
                  Restore slot). Shows the actual cap count + the std-420
                  quoting unit when the cap type differs from 420 mm.
                  Iter211 — hidden when the roof material doesn't use
                  discrete caps (Iron / Tin Tile / Butynol / Polycarbonate). */}
              {isRidging(pin.type) && (pin.ridge_cap_type || forceRidgeCapCount) && showRidgeCapQty && (() => {
                const effectiveCapType = pin.ridge_cap_type || (forceRidgeCapCount ? 'Standard Concrete' : '');
                const cap = RIDGE_CAP_LOOKUP[effectiveCapType];
                if (!cap) return null;
                const capM = cap.length_mm / 1000;
                const explicit = parseInt(pin.ridge_cap_count, 10);
                const len = parseFloat(pin.length_m) || 0;
                const qty = Number.isFinite(explicit) && explicit > 0
                  ? explicit
                  : (len > 0 ? Math.ceil(len / capM) : 0);
                if (!qty) return null;
                const lenForStd = len > 0 ? len : (explicit > 0 ? explicit * capM : 0);
                const std420 = lenForStd > 0 ? Math.ceil(lenForStd / (STD_RIDGE_CAP_MM / 1000)) : 0;
                const isInferred = !pin.ridge_cap_type && forceRidgeCapCount;
                return (
                  <>
                    <span
                      data-testid={`annot-pin-row-${pin.id}-cap`}
                      className={`text-[10px] font-mono-num font-semibold rounded px-1 shrink-0 ${isInferred ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200 italic' : 'bg-amber-100 text-amber-800 ring-1 ring-amber-200'}`}
                      title={`${qty} × ${effectiveCapType} (${cap.length_mm} mm)${isInferred ? ' — inferred from Concrete Restore slot' : ''}`}
                    >{qty}c</span>
                    {cap.length_mm !== STD_RIDGE_CAP_MM && std420 > 0 && (
                      <span
                        data-testid={`annot-pin-row-${pin.id}-cap-std`}
                        className="text-[10px] font-mono-num font-semibold bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200 rounded px-1 shrink-0"
                        title={`Equivalent count at ${STD_RIDGE_CAP_MM} mm std quoting unit`}
                      >{std420}@{STD_RIDGE_CAP_MM}</span>
                    )}
                  </>
                );
              })()}
              {pin.label && <span className="text-slate-500 truncate flex-1 italic">{pin.label}</span>}
              {!pin.label && <span className="text-slate-300 flex-1">—</span>}
            </div>
          );
        })}
      </div>
      )}
      </div>

      {/* Live totals strip */}
      <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
        <span className="text-slate-500 font-semibold uppercase tracking-wider text-[10px] mr-1">Totals:</span>
        {Object.keys(totals).length === 0 && <span className="text-slate-300 italic">No pins yet</span>}
        {Object.entries(totals).map(([type, t]) => {
          const diaList = t.position ? Array.from(t.diameters || []).sort((a,b)=>a-b) : null;
          const value = t.position
            ? (diaList && diaList.length ? `${diaList.join('/')} mm` : '—')
            : `${t.length.toFixed(1)}${t.unit}`;
          return (
            <span key={type} className="px-2 py-0.5 rounded-full text-white font-semibold font-mono-num" style={{ background: t.color }}>
              {type}: {value} × {t.count}
            </span>
          );
        })}
      </div>

      {/* Downpipe material rollup — grouped by diameter (80 vs 65 mm). Each
          flagged Outlet adds 2 bends + 1 × 500 mm junction + the configured
          pipe length. Useful as a contractor-ready BOM right under the
          diagram. */}
      {downpipeRollup.count > 0 && (
        <div data-testid="annot-downpipe-rollup" className="rounded-md ring-1 ring-emerald-200 bg-emerald-50/60 p-2 text-[11px] space-y-1">
          <div className="flex items-center justify-between">
            <strong className="uppercase tracking-wider text-[10px] text-emerald-700">New Downpipes</strong>
            <span className="text-[10px] text-emerald-700">
              {downpipeRollup.count} outlet{downpipeRollup.count === 1 ? '' : 's'} · {downpipeRollup.totalPipeM.toFixed(1)} m pipe · {downpipeRollup.totalBends} bends · {downpipeRollup.totalJunctions} × 500 mm junction
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(downpipeRollup.byDia).map(([dia, agg]) => (
              <span key={dia} data-testid={`annot-downpipe-dia-${dia}`} className="px-2 py-0.5 rounded ring-1 ring-emerald-300 bg-white font-mono-num">
                ⌀{dia} mm — {agg.count} run{agg.count === 1 ? '' : 's'} · {agg.pipe_m.toFixed(1)} m pipe · {agg.bends} bends · {agg.junctions} junction{agg.junctions === 1 ? '' : 's'}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Aquaseal / Dektite material rollup — surfaced for the Roofing
          trade. Lists each Aquaseal product picked + total rivets +
          tubes-of-silicone (rolled up from %-per-pin). */}
      {aquasealRollup.totalCount > 0 && (
        <div data-testid="annot-aquaseal-rollup" className="rounded-md ring-1 ring-sky-200 bg-sky-50/60 p-2 text-[11px] space-y-1">
          <div className="flex items-center justify-between">
            <strong className="uppercase tracking-wider text-[10px] text-sky-800">Dektites</strong>
            <span className="text-[10px] text-sky-800">
              {aquasealRollup.totalCount} penetration{aquasealRollup.totalCount === 1 ? '' : 's'} · {aquasealRollup.totalRivets} rivets · ≈ {aquasealRollup.totalSiliconeTubes} tube{aquasealRollup.totalSiliconeTubes === 1 ? '' : 's'} silicone
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(aquasealRollup.byType).map(([name, agg]) => (
              <span key={name} data-testid={`annot-aquaseal-type-${name.replace(/\W+/g,'-')}`} className="px-2 py-0.5 rounded ring-1 ring-sky-300 bg-white font-mono-num">
                {name} — {agg.count} · {agg.rivets} rivets · {agg.silicone.toFixed(0)}% tube
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Backtray flashing rollup — grouped by width band (mm). Length
          totals are per width so the order desk can quote sheets straight
          off the diagram. */}
      {backtrayRollup.totalCount > 0 && (
        <div data-testid="annot-backtray-rollup" className="rounded-md ring-1 ring-orange-200 bg-orange-50/60 p-2 text-[11px] space-y-1">
          <div className="flex items-center justify-between">
            <strong className="uppercase tracking-wider text-[10px] text-orange-800">Backtray flashings</strong>
            <span className="text-[10px] text-orange-800">
              {backtrayRollup.totalCount} tray{backtrayRollup.totalCount === 1 ? '' : 's'} · {backtrayRollup.totalLen.toFixed(1)} m total
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(backtrayRollup.byWidth).map(([w, agg]) => (
              <span key={w} data-testid={`annot-backtray-width-${w}`} className="px-2 py-0.5 rounded ring-1 ring-orange-300 bg-white font-mono-num">
                {w} mm wide — {agg.count} tray{agg.count === 1 ? '' : 's'} · {agg.length.toFixed(1)} m
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Ridge-cap rollup — grouped by cap type for concrete-tile quoting.
          Each entry shows total caps × cap length and the rolled-up ridge
          metres (helpful sanity check before ordering materials). The
          "std 420 mm" column on each chip + the summary at the top show
          the quoting unit count even when larger caps are installed. */}
      {Object.keys(ridgeCapRollup.byType).length > 0 && (
        <div data-testid="annot-ridgecap-rollup" className="rounded-md ring-1 ring-amber-200 bg-amber-50/60 p-2 text-[11px] space-y-1">
          <div className="flex items-center justify-between">
            <strong className="uppercase tracking-wider text-[10px] text-amber-800">Ridge Caps (Concrete Restore)</strong>
            <span className="text-[10px] text-amber-800 flex items-center gap-2">
              <span className="font-mono-num">{ridgeCapRollup.totalLen.toFixed(1)} m</span>
              <span>·</span>
              <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-mono-num">
                {ridgeCapRollup.totalStd420} std @ {STD_RIDGE_CAP_MM} mm
              </span>
              <span className="opacity-70">— verify on-site</span>
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(ridgeCapRollup.byType).map(([type, agg]) => (
              <span key={type} data-testid={`annot-ridgecap-type-${type}`} className="px-2 py-0.5 rounded ring-1 ring-amber-300 bg-white font-mono-num">
                {type} ({agg.length_mm} mm) — {agg.qty} caps · {agg.total_m.toFixed(1)} m
                {agg.length_mm !== STD_RIDGE_CAP_MM && (
                  <span className="ml-1 text-indigo-700"> · {agg.std420} @ {STD_RIDGE_CAP_MM} mm std</span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Inline edit dialog */}
      {editingPin && (
        <div data-testid="annot-edit-dialog" className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditingPin(null)}>
          <div
            className="bg-white rounded-xl w-full max-w-sm"
            onClick={e => e.stopPropagation()}
            onKeyDown={(e) => {
              // Enter on any input/select inside the dialog saves + closes.
              // Shift+Enter and Enter inside <textarea> remain newline-safe.
              if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && !e.shiftKey) {
                e.preventDefault();
                handleEditSave(editingPin);
              }
              if (e.key === 'Escape') setEditingPin(null);
            }}
          >
            <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <h3 className="text-sm font-bold flex items-center gap-2">
                Pin · {editingPin.type}
                {/* Iter211 — surface the pin ref (e.g. F1 / R3 / FA2) in
                    the dialog header so the operator can confirm WHICH
                    pin they're editing. The ref comes from refMap which
                    is keyed by pin.id. Skipped while the pin is fresh
                    (no ref allocated yet). */}
                {(() => {
                  const ref = refMap?.[editingPin.id];
                  if (!ref) return null;
                  return (
                    <span
                      data-testid="annot-edit-pin-ref"
                      className="px-1.5 py-0.5 rounded font-mono-num text-[11px] font-bold text-slate-700 bg-white border border-slate-300"
                      title={`Pin reference ${ref}`}
                    >{ref}</span>
                  );
                })()}
              </h3>
              <button onClick={() => setEditingPin(null)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </header>
            <div className="p-4 space-y-3 text-xs">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Type</label>
                <select
                  data-testid="annot-edit-type"
                  value={editingPin.type}
                  onChange={e => {
                    const type = e.target.value;
                    const rePitch = editingPin.length_source === 'plan' && Number(editingPin.plan_length_m) > 0
                      && PITCHED_PIN_TYPES.includes(type) ? toPitched(Number(editingPin.plan_length_m), type, pitchDeg) : null;
                    setEditingPin({ ...editingPin, type, ...(rePitch ? { length_m: rePitch } : {}) });
                  }}
                  className="w-full px-3 py-2 border border-slate-200 rounded text-sm bg-white"
                >
                  {PIN_TYPES.map(t => (
                    <option key={t.value} value={t.value}>
                      {typeLabel(t.value)}{t.unit ? ` (${t.unit})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              {/* Flashing sub-type — only relevant when the pin is a
                  Flashing. Captures WHAT KIND of flashing (Backtray /
                  Apron / Head / Skirt / Change of Pitch / Cowling /
                  Gutter Flashing / Barge Tray) so the office knows
                  which flashing to fabricate or order.
                  Iter229o — restructured into a 2-col layout pairing:
                    Row 1: [Flashing Kind] + [Label]
                    Row 2: [Flashing Description] + [Length (m)]  (when Backtray)
                            or [Length (m)] full-width (other kinds).
                  The generic Label + Length blocks lower in the
                  dialog skip themselves for Flashing pins so we
                  don't render duplicates. */}
              {editingPin.type === 'Flashing' && (
                <div className="space-y-2">
                  {/* Row 1 — Flashing Kind paired with Label */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Flashing kind</label>
                      <select
                        data-testid="annot-edit-flashing-type"
                        value={editingPin.flashing_type || ''}
                        onChange={e => setEditingPin({ ...editingPin, flashing_type: e.target.value || undefined })}
                        className="w-full px-3 py-2 border border-slate-200 rounded text-sm bg-white"
                      >
                        <option value="">— pick a flashing kind —</option>
                        {FLASHING_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Label</label>
                      <select
                        data-testid="annot-edit-label-preset"
                        value={LABEL_PRESETS.includes(editingPin.label) ? editingPin.label : (editingPin.label ? '__OTHER__' : '')}
                        onChange={e => {
                          const v = e.target.value;
                          if (v === '__OTHER__') {
                            setEditingPin({ ...editingPin, label: editingPin.label && !LABEL_PRESETS.includes(editingPin.label) ? editingPin.label : ' ' });
                          } else {
                            setEditingPin({ ...editingPin, label: v });
                          }
                        }}
                        className="w-full px-3 py-2 border border-slate-200 rounded text-sm bg-white"
                      >
                        <option value="">— Pick a side —</option>
                        {LABEL_PRESETS.map(p => <option key={p} value={p}>{p}</option>)}
                        <option value="__OTHER__">Other / custom…</option>
                      </select>
                      {(editingPin.label && !LABEL_PRESETS.includes(editingPin.label)) && (
                        <input
                          data-testid="annot-edit-label"
                          value={editingPin.label.trim() === '' ? '' : editingPin.label}
                          onChange={e => setEditingPin({ ...editingPin, label: e.target.value })}
                          onFocus={(e) => e.target.select()}
                          className="w-full mt-1 px-3 py-2 border border-slate-200 rounded text-sm"
                          placeholder='e.g. "Above bay window"'
                        />
                      )}
                    </div>
                  </div>
                  {/* Row 2 — Flashing Description (Backtray only) + Length (m).
                      Iter229o — Description loaded from
                      `flashing_descriptions` collection (admin-editable
                      via the ⚙ pop-out). When the Kind is not Backtray
                      the Length input takes the full row. */}
                  <div className={editingPin.flashing_type === 'Backtray' ? 'grid grid-cols-2 gap-2' : ''}>
                    {editingPin.flashing_type === 'Backtray' && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">
                            Flashing Description <span className="text-slate-400 font-normal normal-case">— drives picker</span>
                          </label>
                          <button
                            type="button"
                            data-testid="annot-edit-flashing-description-admin"
                            onClick={() => setShowDescAdmin(true)}
                            title="Edit Flashing Description list (admin)"
                            className="text-[10px] font-semibold text-slate-500 hover:text-slate-800 bg-slate-50 hover:bg-slate-100 ring-1 ring-slate-200 rounded px-1.5 py-0.5"
                          >⚙ Edit list</button>
                        </div>
                        <select
                          data-testid="annot-edit-flashing-description"
                          value={editingPin.flashing_description || ''}
                          onChange={e => {
                            const newDesc = e.target.value || undefined;
                            setEditingPin({ ...editingPin, flashing_description: newDesc });
                            // Iter232 — Description-driven Pipe Ø cascade.
                            // When the picked description carries an
                            // effective_diameter_mm (explicit override
                            // OR parsed from the label), AND this
                            // flashing is linked to a Penetration pin
                            // that has no Ø set yet, push the Ø onto
                            // the Penetration so the operator doesn't
                            // have to re-enter it. We only auto-fill
                            // when the existing Ø is unset (≤0) — never
                            // overwrite an operator-entered value.
                            if (!newDesc) return;
                            const match = (flashingDescriptions || []).find(d => d.label === newDesc);
                            const eff = match?.effective_diameter_mm;
                            const linkedPenId = editingPin.linked_pipe_pin_id;
                            if (eff && linkedPenId) {
                              const linkedPen = pins.find(p => p.id === linkedPenId);
                              if (linkedPen && !(Number(linkedPen.outlet_diameter_mm) > 0)) {
                                save(pins.map(p => p.id === linkedPenId
                                  ? { ...p, outlet_diameter_mm: eff }
                                  : p
                                )).then?.(() => {
                                  setSavedFlash(`Ø ${eff} mm cascaded → linked Penetration`);
                                  setTimeout(() => setSavedFlash(''), 2200);
                                });
                              }
                            }
                          }}
                          className="w-full px-3 py-2 border border-slate-200 rounded text-sm bg-white"
                        >
                          <option value="">— Pick a description —</option>
                          {(() => {
                            const cascading = flashingDescriptions.filter(d => (d.group || 'simple') === 'cascading');
                            const simple    = flashingDescriptions.filter(d => (d.group || 'simple') === 'simple');
                            // Fallback to a baseline list when the API
                            // hasn't responded yet so the operator
                            // never sees an empty dropdown on first
                            // open.
                            if (cascading.length === 0 && simple.length === 0) {
                              return (
                                <>
                                  <optgroup label="Cascading (carries fittings)">
                                    <option value="Pipe Backtray">Pipe Backtray</option>
                                    <option value="Flue Backtray">Flue Backtray</option>
                                  </optgroup>
                                  <optgroup label="Simple cover (no fittings)">
                                    <option value="Barge Backtray">Barge Backtray</option>
                                    <option value="Std Backtray">Std Backtray</option>
                                  </optgroup>
                                </>
                              );
                            }
                            return (
                              <>
                                {cascading.length > 0 && (
                                  <optgroup label="Cascading (carries fittings)">
                                    {cascading.map(d => <option key={d._id} value={d.label}>{d.label}</option>)}
                                  </optgroup>
                                )}
                                {simple.length > 0 && (
                                  <optgroup label="Simple cover (no fittings)">
                                    {simple.map(d => <option key={d._id} value={d.label}>{d.label}</option>)}
                                  </optgroup>
                                )}
                              </>
                            );
                          })()}
                        </select>
                        {/* Iter232 — Description-driven Pipe Ø chip.
                            Surfaces the parsed/explicit diameter from
                            the admin catalogue so the operator sees
                            why their linked Penetration's Ø was auto-
                            filled (or what value would have cascaded
                            had they linked one). */}
                        {(() => {
                          const desc = editingPin.flashing_description;
                          if (!desc) return null;
                          const match = (flashingDescriptions || []).find(d => d.label === desc);
                          const eff = match?.effective_diameter_mm;
                          if (!eff) return null;
                          const explicit = match?.diameter_mm;
                          return (
                            <div
                              data-testid="annot-edit-flashing-description-dia-chip"
                              className="mt-1 text-[10px] text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded px-1.5 py-0.5 inline-block font-mono-num"
                              title={explicit ? 'Admin-set override' : 'Parsed from description text'}
                            >
                              → Ø {eff} mm · auto from {explicit ? 'admin override' : 'description'}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    {PITCHED_PIN_TYPES.includes(editingPin.type) ? (
                      <PlanPitchedFields
                        kind={editingPin.type} pitchDeg={pitchDeg} testId="annot-edit-length"
                        plan={editingPin.plan_length_m} pitched={editingPin.length_m} source={editingPin.length_source}
                        autoFocusPitched
                        onChange={({ plan, pitched, source }) => setEditingPin({
                          ...editingPin, length_m: pitched, plan_length_m: plan, length_source: source, pitch_deg_used: pitchDeg,
                        })}
                      />
                    ) : (
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                        Length (m)
                      </label>
                      <input
                        type="number" min={0} step={0.1}
                        data-testid="annot-edit-length"
                        value={editingPin.length_m === 0 || editingPin.length_m === '0' ? '' : editingPin.length_m}
                        onFocus={(e) => e.target.select()}
                        onChange={e => setEditingPin({ ...editingPin, length_m: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded text-sm font-mono-num"
                        placeholder="e.g. 5.2"
                      />
                    </div>
                    )}
                  </div>
                  {/* Iter229q — Backtray ⟷ Pipe/Flue bidirectional link.
                      A "Pipe Backtray" / "Flue Backtray" flashing
                      always sits over a specific Pipe or Flue
                      penetration. Picking the target pin here writes
                      `linked_pipe_pin_id` on this flashing and on
                      save the materials/order rollup can pair them.
                      "+ Create new …" drops a fresh Pipe/Flue pin at
                      the same diagram position (operator usually pins
                      both within a few millimetres of each other)
                      and pre-links it. */}
                  {editingPin.flashing_type === 'Backtray' && (() => {
                    // Iter229u — "Has penetration?" toggle for Backtray
                    // flashings. Auto-default to Yes when the
                    // description points at a Pipe/Flue Backtray; else
                    // No. Operator can override at any time.
                    const autoYes = editingPin.flashing_description === 'Pipe Backtray'
                                 || editingPin.flashing_description === 'Flue Backtray';
                    const hasPen = editingPin.has_penetration === 'yes'
                                || (editingPin.has_penetration === undefined && autoYes);
                    // When description hints flue → seed kind=flue. Else pipe.
                    const seedKind = editingPin.flashing_description === 'Flue Backtray' ? 'flue' : 'pipe';
                    const candidates = pins.filter(p => p.type === 'Penetration');
                    const linkedId = editingPin.linked_pipe_pin_id || '';
                    const linked = candidates.find(p => p.id === linkedId) || null;
                    const refLabel = (p) => {
                      const refMatch = /^([A-Z]+\d+)/.exec(p.label || '') || [];
                      const idx = pins.filter(x => x.type === 'Penetration').findIndex(x => x.id === p.id);
                      const tag = `PN${idx + 1}`;
                      const dia = p.outlet_diameter_mm ? `⌀${p.outlet_diameter_mm}` : '';
                      const usage = p.pipe_usage ? ` ${p.pipe_usage.replace(/_/g, ' ')}` : '';
                      const kindBadge = p.penetration_kind === 'flue' ? '[Flue]' : '[Pipe]';
                      return `${refMatch[1] || tag} ${kindBadge} · ${dia}${usage}`.trim();
                    };
                    // Iter229y — A2508 walkthrough caught the bug where
                    // two Backtray flashings could silently claim the
                    // same Penetration. Build a `penetrationId →
                    // [otherFlashings]` map (excluding THIS pin) so
                    // we can warn in the dropdown labels + show an
                    // on-load conflict banner + atomically detach
                    // when the operator reassigns.
                    const allBacktrays = pins.filter(p =>
                      p.type === 'Flashing' && p.flashing_type === 'Backtray'
                    );
                    const flashRefLabel = (p) => {
                      const refMatch = /^([A-Z]+\d+)/.exec(p.label || '') || [];
                      const idx = pins.filter(x => x.type === 'Flashing').findIndex(x => x.id === p.id);
                      return refMatch[1] || `F${idx + 1}`;
                    };
                    const otherFlashingsByPenId = {};
                    allBacktrays.forEach(b => {
                      if (b.id === editingPin.id) return;
                      if (!b.linked_pipe_pin_id) return;
                      const list = otherFlashingsByPenId[b.linked_pipe_pin_id] || [];
                      list.push(b);
                      otherFlashingsByPenId[b.linked_pipe_pin_id] = list;
                    });
                    const conflictingFlashings = linkedId
                      ? (otherFlashingsByPenId[linkedId] || [])
                      : [];
                    const handlePickPenetration = (penId) => {
                      if (!penId) {
                        setEditingPin({ ...editingPin, linked_pipe_pin_id: undefined });
                        return;
                      }
                      const conflicts = otherFlashingsByPenId[penId] || [];
                      if (conflicts.length > 0) {
                        // Atomically detach the other flashings'
                        // links so this Penetration is uniquely
                        // claimed by THIS flashing. Persisted via
                        // save(); editingPin's link is then set
                        // locally so the modal reflects the change.
                        const detachIds = new Set(conflicts.map(b => b.id));
                        const nextPins = pins.map(p =>
                          detachIds.has(p.id)
                            ? { ...p, linked_pipe_pin_id: undefined }
                            : p
                        );
                        const names = conflicts.map(flashRefLabel).join(', ');
                        save(nextPins).then(() => {
                          // Flash AFTER save() so the 'Saved ✓'
                          // indicator doesn't overwrite our intent.
                          setSavedFlash(`Detached ${names} from this Penetration — exclusive to this flashing now`);
                          setTimeout(() => setSavedFlash(''), 3000);
                        });
                      }
                      setEditingPin({ ...editingPin, linked_pipe_pin_id: penId });
                    };
                    const resolveConflictKeepThis = () => {
                      const detachIds = new Set(conflictingFlashings.map(b => b.id));
                      const nextPins = pins.map(p =>
                        detachIds.has(p.id)
                          ? { ...p, linked_pipe_pin_id: undefined }
                          : p
                      );
                      const names = conflictingFlashings.map(flashRefLabel).join(', ');
                      save(nextPins).then(() => {
                        setSavedFlash(`Detached ${names} — this flashing now exclusively linked`);
                        setTimeout(() => setSavedFlash(''), 3000);
                      });
                    };
                    const createAndLink = (cascadeFields = {}) => {
                      // Iter229v — offset by ~2% so the new Penetration
                      // doesn't sit directly under the Backtray pin and
                      // is visible on the diagram immediately.
                      const offset = (v) => Math.min(98, Math.max(2, (parseFloat(v) || 50) + 2));
                      const newPin = {
                        id: genId(),
                        type: 'Penetration',
                        penetration_kind: seedKind,
                        x_pct: offset(editingPin.x_pct),
                        y_pct: offset(editingPin.y_pct),
                        outlet_diameter_mm: seedKind === 'flue' ? 160 : 32,
                        color: TYPE_LOOKUP['Penetration']?.color,
                        ...cascadeFields,
                      };
                      save([...pins, newPin]);
                      setEditingPin({ ...editingPin, linked_pipe_pin_id: newPin.id, has_penetration: 'yes' });
                      setShowInlinePenetrationCreate(false);
                    };
                    return (
                      <div className="bg-emerald-50 ring-1 ring-emerald-200 rounded p-2 space-y-1.5 text-xs" data-testid="annot-edit-backtray-link">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 flex-1">
                            Has penetration?
                            <span className="text-emerald-600 font-normal normal-case ml-1">— does this Backtray cover a Pipe / Flue?</span>
                          </span>
                          <div className="flex gap-1" data-testid="annot-edit-backtray-has-penetration">
                            {['yes', 'no'].map(v => (
                              <label
                                key={v}
                                data-testid={`annot-edit-backtray-has-penetration-${v}`}
                                className={`cursor-pointer px-2 py-0.5 rounded ring-1 text-[10px] font-semibold ${
                                  (hasPen ? 'yes' : 'no') === v
                                    ? 'bg-emerald-600 text-white ring-emerald-600'
                                    : 'bg-white text-emerald-700 ring-emerald-200 hover:bg-emerald-50'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="has-penetration"
                                  value={v}
                                  checked={(hasPen ? 'yes' : 'no') === v}
                                  onChange={() => {
                                    if (v === 'no') {
                                      // Detaching also clears any prior link so the
                                      // saved state matches the UI choice.
                                      setEditingPin({ ...editingPin, has_penetration: 'no', linked_pipe_pin_id: undefined });
                                    } else {
                                      setEditingPin({ ...editingPin, has_penetration: 'yes' });
                                    }
                                  }}
                                  className="hidden"
                                />
                                {v.toUpperCase()}
                              </label>
                            ))}
                          </div>
                        </div>
                        {hasPen && (<>
                          {/* Iter229y — Conflict banner. Shown when
                              this Backtray's linked Penetration is
                              ALSO referenced by another Backtray.
                              The dropdown also annotates the
                              conflict on a per-option basis. */}
                          {conflictingFlashings.length > 0 && (
                            <div
                              data-testid="annot-edit-backtray-link-conflict"
                              className="bg-rose-50 ring-1 ring-rose-300 rounded p-1.5 text-[11px] text-rose-800 flex items-start gap-2"
                            >
                              <span className="text-rose-600 font-bold">⚠</span>
                              <div className="flex-1">
                                <div className="font-bold">
                                  {linked ? refLabel(linked) : 'This Penetration'} is also linked to{' '}
                                  <strong>{conflictingFlashings.map(flashRefLabel).join(', ')}</strong>.
                                </div>
                                <div className="text-rose-700 text-[10px]">
                                  A Penetration can only be covered by one Backtray. Resolve to detach the others.
                                </div>
                              </div>
                              <button
                                type="button"
                                data-testid="annot-edit-backtray-link-conflict-resolve"
                                onClick={resolveConflictKeepThis}
                                className="px-2 py-0.5 text-[10px] font-bold text-white bg-rose-600 hover:bg-rose-700 rounded whitespace-nowrap"
                                title="Detach the other Backtray flashing(s) and keep this one"
                              >Keep this · detach others</button>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <select
                              data-testid="annot-edit-backtray-link-select"
                              value={linkedId}
                              onChange={e => handlePickPenetration(e.target.value || '')}
                              className="flex-1 px-2 py-1.5 border border-emerald-200 rounded text-sm bg-white"
                            >
                              <option value="">— Pick an existing Penetration —</option>
                              {candidates.map(p => {
                                const others = otherFlashingsByPenId[p.id] || [];
                                const conflict = others.length > 0 ? ` ⚠ also linked to ${others.map(flashRefLabel).join(', ')}` : '';
                                return (
                                  <option key={p.id} value={p.id}>{refLabel(p)}{conflict}</option>
                                );
                              })}
                            </select>
                            <button
                              type="button"
                              data-testid="annot-edit-backtray-link-create"
                              onClick={() => setShowInlinePenetrationCreate(v => !v)}
                              className={`px-2 py-1.5 text-[10px] font-bold rounded whitespace-nowrap ${
                                showInlinePenetrationCreate
                                  ? 'bg-slate-500 hover:bg-slate-600 text-white'
                                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                              }`}
                              title={`Drop a new ${seedKind === 'flue' ? 'Flue' : 'Pipe'} Penetration pin here and link it`}
                            >{showInlinePenetrationCreate ? 'Close' : '+ Create new Penetration'}</button>
                          </div>
                          {/* Iter229v — Inline cascade form. Lets the
                              operator configure Kind / Usage / Ø /
                              Dektite for the new Penetration right
                              here, then a single Save creates the pin
                              fully-populated and links it to this
                              Backtray. */}
                          {showInlinePenetrationCreate && (
                            <PenetrationInlineCreate
                              api={api}
                              defaultKind={seedKind}
                              roofProfile={roofProfile}
                              roofType={roofType}
                              onSave={(cascadeFields) => createAndLink(cascadeFields)}
                              onCancel={() => setShowInlinePenetrationCreate(false)}
                            />
                          )}
                          {linked ? (
                            <>
                              <div className="text-[10px] text-emerald-700">
                                ✓ Linked to <strong>{refLabel(linked)}</strong>. Open that pin to set Kind / Usage / Dektite.
                              </div>
                              {/* Iter242 — Pipe Ø shortcut. Edit the
                                  linked Penetration's `outlet_diameter_mm`
                                  directly from the Flashing dialog so
                                  operators don't have to bounce back to
                                  the Penetration pin for a Ø change. */}
                              {(() => {
                                const dia = Number(linked.outlet_diameter_mm) || 0;
                                const presets = TYPE_LOOKUP['Penetration']?.diameters || [];
                                const updateDia = (mm) => {
                                  const newDia = Number(mm) || 0;
                                  save(pins.map(p => p.id === linked.id ? { ...p, outlet_diameter_mm: newDia } : p));
                                };
                                return (
                                  <div className="flex items-center gap-2" data-testid="annot-edit-backtray-link-dia">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 whitespace-nowrap">Pipe Ø</span>
                                    <select
                                      data-testid="annot-edit-backtray-link-dia-preset"
                                      value={presets.includes(dia) ? String(dia) : (dia ? '__custom__' : '')}
                                      onChange={e => {
                                        const v = e.target.value;
                                        if (v === '__custom__') return;  // free input handles it
                                        updateDia(v);
                                      }}
                                      className="px-2 py-1 border border-emerald-200 rounded text-xs bg-white font-mono-num"
                                    >
                                      <option value="">— mm —</option>
                                      {presets.map(d => <option key={d} value={d}>{d} mm</option>)}
                                      <option value="__custom__">Custom…</option>
                                    </select>
                                    <input
                                      type="number" min={0} step={1}
                                      data-testid="annot-edit-backtray-link-dia-input"
                                      value={dia || ''}
                                      onFocus={(e) => e.target.select()}
                                      onChange={e => updateDia(e.target.value)}
                                      className="w-20 px-2 py-1 border border-emerald-200 rounded text-xs font-mono-num"
                                      placeholder="mm"
                                    />
                                    <span className="text-[10px] text-emerald-600 italic flex-1">
                                      writes through to {refLabel(linked)}
                                    </span>
                                  </div>
                                );
                              })()}
                            </>
                          ) : candidates.length === 0 ? (
                            <div className="text-[10px] text-amber-700 italic">
                              No Penetration pins on this diagram yet — use "+ Create new Penetration" to drop one.
                            </div>
                          ) : null}
                        </>)}
                        {!hasPen && (
                          <div className="text-[10px] text-slate-500 italic">
                            Backtray will be sized + ordered without a paired penetration.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {/* "Same as" chips (Flashing-specific subset). Stays
                      below the row so multi-line chip rails don't
                      stretch the grid. */}
                  {(() => {
                    const flashingSources = ['Barge', 'Length'];
                    const entries = flashingSources
                      .flatMap(k => (lengthsByType[k] || []).map(e => ({ ...e, type: k })))
                      .filter(e => e.length > 0)
                      .filter((e, i, arr) => arr.findIndex(x => Math.abs(x.length - e.length) < 0.001) === i);
                    if (entries.length === 0) return null;
                    return (
                      <div data-testid="annot-edit-copy-chips" className="flex items-center gap-1 flex-wrap">
                        <span className="text-[10px] text-slate-400 mr-1">Same as:</span>
                        {entries.map(e => (
                          <button
                            key={`f-${e.ref}-${e.type}`}
                            type="button"
                            onClick={() => setEditingPin({ ...editingPin, length_m: e.length })}
                            className="px-1.5 py-0.5 text-[10px] font-mono-num font-semibold bg-[#FBE9D8] hover:bg-[#F5B884] text-[#A04000] rounded ring-1 ring-[#F5B884]"
                            title={`Copy ${e.length} m from ${e.ref} (${e.type})`}
                          >{e.ref} · {e.length}m</button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
              {TYPE_LOOKUP[editingPin.type]?.position ? (
                /* POSITION pin — diameter + (Outlet-only) shape instead of length */
                <>
                {/* Iter229t — unified Pin.Penetration dialog (replaces
                    the old multi-block Pipe/Flue UI). Pre-existing
                    Pipe / Flue pins flow through here too via
                    render-time normalisation. The legacy code below
                    is gated to `!isPenetration` (Outlet / LeakArea
                    only) until those flows are folded in later. */}
                {(editingPin.type === 'Penetration') ? (
                  <PenetrationEditPanel
                    api={api}
                    pin={editingPin}
                    setPin={setEditingPin}
                    pins={pins}
                    save={save}
                    genId={genId}
                    roofType={roofType}
                    roofProfile={roofProfile}
                    flashingDescriptions={flashingDescriptions}
                    TYPE_LOOKUP={TYPE_LOOKUP}
                  />
                ) : (<>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                      {editingPin.type} Diameter <span className="text-slate-400 font-normal">(mm)</span>
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {(TYPE_LOOKUP[editingPin.type]?.diameters || OUTLET_DIAMETERS).map(d => (
                        <label key={d} data-testid={`annot-edit-outlet-dia-${d}`}
                               className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded border cursor-pointer text-xs ${(editingPin.outlet_diameter_mm || TYPE_LOOKUP[editingPin.type]?.defaultDia || 80) === d ? 'bg-[#FBE9D8] border-[#D35400] text-[#A04000] font-bold' : 'bg-white border-slate-200 text-slate-600'}`}
                               style={{ minWidth: 56 }}>
                          <input type="radio" name="outlet-dia"
                                 checked={(editingPin.outlet_diameter_mm || TYPE_LOOKUP[editingPin.type]?.defaultDia || 80) === d}
                                 onChange={() => setEditingPin({ ...editingPin, outlet_diameter_mm: d })}
                                 className="hidden" />
                          {d} mm
                        </label>
                      ))}
                      {/* Pipe + Flue accept arbitrary diameters — show a free input alongside the presets. */}
                      {TYPE_LOOKUP[editingPin.type]?.freeDia && (
                        <div className="flex items-center gap-1 px-2 py-1 rounded border border-dashed border-slate-300 bg-white">
                          <input
                            data-testid="annot-edit-outlet-dia-custom"
                            type="number" min={5} max={500} step={1}
                            value={editingPin.outlet_diameter_mm || ''}
                            onChange={e => setEditingPin({ ...editingPin, outlet_diameter_mm: parseInt(e.target.value, 10) || 0 })}
                            placeholder="Other"
                            className="w-16 text-xs text-center bg-transparent focus:outline-none font-mono-num"
                          />
                          <span className="text-[10px] text-slate-400">mm</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Round / Square is meaningful for downpipe Outlets only.
                      Pipe + Flue penetrations are always round. */}
                  {editingPin.type === 'Outlet' && (
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Outlet Shape</label>
                      <div className="flex gap-2">
                        {OUTLET_SHAPES.map(s => (
                          <label key={s} data-testid={`annot-edit-outlet-shape-${s}`}
                                 className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded border cursor-pointer text-xs ${(editingPin.outlet_shape || 'Round') === s ? 'bg-[#FBE9D8] border-[#D35400] text-[#A04000] font-bold' : 'bg-white border-slate-200 text-slate-600'}`}>
                            <input type="radio" name="outlet-shape"
                                   checked={(editingPin.outlet_shape || 'Round') === s}
                                   onChange={() => setEditingPin({ ...editingPin, outlet_shape: s })}
                                   className="hidden" />
                            <span style={{ width: 8, height: 8, background: '#EAB308', borderRadius: s === 'Square' ? '1px' : '50%' }} />
                            {s}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* PIPE USAGE + auto-suggested flashing — only for Pipe + Flue
                      pins. Picking a usage queries the backend for a
                      pre-set flashing template (matched by usage +
                      diameter + the roof's sheet profile if known) and
                      shows a one-click "Use this flashing" suggestion. */}
                  {(editingPin.type === 'Pipe' || editingPin.type === 'Flue') && (
                    <PipeUsageFlashingBlock
                      api={api}
                      pin={editingPin}
                      setPin={setEditingPin}
                      sheetProfileHint={roofProfile}
                      roofType={roofType}
                    />
                  )}
                  {/* Iter229q — Backtray link mirror. From a Pipe / Flue
                      pin's perspective, surface any Backtray flashing
                      already linking back to it (`linked_pipe_pin_id`
                      === this pin's id). Operator can pick a different
                      Backtray, clear, or drop a brand new one
                      pre-linked. The link lives ONLY on the flashing
                      side so we keep a single source of truth. */}
                  {(editingPin.type === 'Pipe' || editingPin.type === 'Flue') && (() => {
                    const wantDesc = editingPin.type === 'Flue' ? 'Flue Backtray' : 'Pipe Backtray';
                    const eligible = pins.filter(p =>
                      p.type === 'Flashing'
                      && p.flashing_type === 'Backtray'
                      && p.flashing_description === wantDesc
                    );
                    const linked = eligible.find(p => p.linked_pipe_pin_id === editingPin.id) || null;
                    const refLabel = (p) => {
                      const refMatch = /^([A-Z]+\d+)/.exec(p.label || '') || [];
                      const idx = pins.filter(x => x.type === 'Flashing').findIndex(x => x.id === p.id);
                      const tag = `F${idx + 1}`;
                      const len = p.length_m ? ` · ${p.length_m}m` : '';
                      return `${refMatch[1] || tag}${len}`;
                    };
                    const pickBacktray = (backtrayId) => {
                      if (!backtrayId) {
                        // Clear — detach any existing back-tray pointing here.
                        if (linked) {
                          const next = pins.map(p => p.id === linked.id
                            ? { ...p, linked_pipe_pin_id: undefined }
                            : p
                          );
                          save(next);
                        }
                        return;
                      }
                      // Reassign: point the chosen back-tray here, AND
                      // detach the previous one if any.
                      const next = pins.map(p => {
                        if (p.id === backtrayId) {
                          return { ...p, linked_pipe_pin_id: editingPin.id };
                        }
                        if (linked && p.id === linked.id) {
                          return { ...p, linked_pipe_pin_id: undefined };
                        }
                        return p;
                      });
                      save(next);
                    };
                    const createAndLink = () => {
                      const newPin = {
                        id: genId(),
                        type: 'Flashing',
                        flashing_type: 'Backtray',
                        flashing_description: wantDesc,
                        x_pct: editingPin.x_pct,
                        y_pct: editingPin.y_pct,
                        length_m: 0,
                        linked_pipe_pin_id: editingPin.id,
                        color: TYPE_LOOKUP['Flashing']?.color,
                      };
                      // Detach any pre-existing link before adding.
                      const base = linked
                        ? pins.map(p => p.id === linked.id ? { ...p, linked_pipe_pin_id: undefined } : p)
                        : pins;
                      save([...base, newPin]);
                    };
                    return (
                      <div className="bg-emerald-50 ring-1 ring-emerald-200 rounded p-2 space-y-1.5 text-xs" data-testid="annot-edit-pipe-backtray-link">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">
                            Linked {wantDesc}
                            <span className="text-emerald-600 font-normal normal-case ml-1">— the backtray over this {editingPin.type}</span>
                          </span>
                          {linked && (
                            <button
                              type="button"
                              data-testid="annot-edit-pipe-backtray-link-clear"
                              onClick={() => pickBacktray('')}
                              className="text-emerald-700 underline text-[10px]"
                              title="Detach this Backtray link"
                            >clear</button>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <select
                            data-testid="annot-edit-pipe-backtray-link-select"
                            value={linked?.id || ''}
                            onChange={e => pickBacktray(e.target.value)}
                            className="flex-1 px-2 py-1.5 border border-emerald-200 rounded text-sm bg-white"
                          >
                            <option value="">— Pick a {wantDesc} —</option>
                            {eligible.map(p => (
                              <option key={p.id} value={p.id}>{refLabel(p)}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            data-testid="annot-edit-pipe-backtray-link-create"
                            onClick={createAndLink}
                            className="px-2 py-1.5 text-[10px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded whitespace-nowrap"
                            title={`Drop a new ${wantDesc} pin at this location and link it`}
                          >+ Create new Backtray</button>
                        </div>
                        {linked ? (
                          <div className="text-[10px] text-emerald-700">
                            ✓ Linked to <strong>{refLabel(linked)}</strong>
                          </div>
                        ) : eligible.length === 0 ? (
                          <div className="text-[10px] text-amber-700 italic">
                            No {wantDesc} pins yet — use "+ Create new Backtray" to drop one.
                          </div>
                        ) : null}
                      </div>
                    );
                  })()}
                  {/* LINKED FLASHING (generic) — available on position
                      pins where a flashing template is meaningful
                      (Pipe / Flue). Iter229k — explicitly excluded for
                      Outlet pins per operator request: a downpipe
                      outlet doesn't get a flashing template wrapped
                      around it. The block also won't render for LeakArea
                      markers (no flashing for a leak pin). */}
                  {editingPin.type !== 'Outlet' && editingPin.type !== 'LeakArea' && (
                    <LinkedFlashingBlock
                      api={api}
                      pin={editingPin}
                      setPin={setEditingPin}
                      roofType={roofType}
                      roofProfile={roofProfile}
                    />
                  )}
                  {/* Iter229h — Flashing Pre-order / On-site Measure
                      toggle (mirrors the Barge Starter/Finish role).
                      Pre-order rolls into the supplier PO with the
                      initial delivery; On-site Measure waits until the
                      roof is fitted and the final dimension is taken.
                      Iter229k — hidden for Outlet / LeakArea pins
                      because they don't carry a linked flashing
                      template, so the toggle has nothing to gate. */}
                  {editingPin.type !== 'Outlet' && editingPin.type !== 'LeakArea' && (<>
                  <div
                    className="flex items-center justify-between gap-1 text-[10px] bg-slate-50 rounded ring-1 ring-slate-200 px-2 py-1"
                    data-testid="annot-edit-flashing-role-toggle"
                  >
                    <span className="font-bold uppercase tracking-[0.18em] text-slate-700">When ordered</span>
                    <div className="inline-flex rounded ring-1 ring-slate-300 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setEditingPin({ ...editingPin, flashing_role: 'pre_order' })}
                        data-testid="annot-edit-flashing-role-preorder"
                        className={`px-2 py-0.5 font-semibold ${(editingPin.flashing_role || 'pre_order') === 'pre_order'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-white text-emerald-800 hover:bg-emerald-50'}`}
                        title="Pre-order with initial delivery — standard dimensions known upfront"
                      >Pre-order</button>
                      <button
                        type="button"
                        onClick={() => setEditingPin({ ...editingPin, flashing_role: 'on_site_measure' })}
                        data-testid="annot-edit-flashing-role-onsite"
                        className={`px-2 py-0.5 font-semibold border-l border-slate-300 ${editingPin.flashing_role === 'on_site_measure'
                          ? 'bg-amber-500 text-white'
                          : 'bg-white text-amber-700 hover:bg-amber-50'}`}
                        title="Measure once roof is fitted — order after install"
                      >On-site Measure</button>
                    </div>
                  </div>
                  {editingPin.flashing_role === 'on_site_measure' && (
                    <div
                      data-testid="annot-edit-flashing-onsite-warning"
                      className="text-[10px] bg-amber-50 ring-1 ring-amber-200 text-amber-800 rounded px-2 py-1 leading-tight"
                    >
                      ⏳ <strong>On-site measure</strong> — measure final dimension after the roof is fitted, then update folds before ordering.
                    </div>
                  )}
                  </>)}
                  {/* AQUASEAL / DEKTITE + BACKTRAY block — only for Pipe + Flue
                      penetrations (Outlets are downpipe outlets). The dropdown is
                      sourced from `component_rate_tables.aquaseal`; selecting a
                      type preloads rivets/silicone% from the size band — the
                      operator can override per pin. */}
                  {(editingPin.type === 'Pipe' || editingPin.type === 'Flue') && (
                    <div className="pt-2 mt-2 border-t border-dashed border-slate-200 space-y-2">
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">
                        Aquaseal / Dektite
                        <span className="text-slate-400 font-normal normal-case ml-1">— select sized to cover the {editingPin.outlet_diameter_mm || '?'} mm penetration</span>
                      </label>
                      {(() => {
                        const dia = parseInt(editingPin.outlet_diameter_mm, 10) || 0;
                        const suggest = !editingPin.aquaseal_name && dia > 0
                          ? aquasealTypes.find(a => {
                              const r = parseAquasealRange(a.name);
                              return r && dia >= r[0] && dia <= r[1];
                            })
                          : null;
                        const selected = editingPin.aquaseal_name
                          ? aquasealTypes.find(a => a.name === editingPin.aquaseal_name)
                          : null;
                        return (
                          <>
                            <select
                              data-testid="annot-edit-aquaseal-type"
                              value={editingPin.aquaseal_name || ''}
                              onChange={e => {
                                const name = e.target.value;
                                const a = aquasealTypes.find(x => x.name === name);
                                const r = a ? parseAquasealRange(a.name) : null;
                                const isLead = a && /lead/i.test((a.name || '') + ' ' + (a.materials_desc || ''));
                                const defaults = AQUASEAL_DEFAULTS(r ? r[1] : null, isLead);
                                setEditingPin({
                                  ...editingPin,
                                  aquaseal_name: name || undefined,
                                  rivets_qty:   editingPin.rivets_qty   ?? defaults.rivets_qty,
                                  silicone_pct: editingPin.silicone_pct ?? defaults.silicone_pct,
                                });
                              }}
                              className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm bg-white"
                            >
                              <option value="">— No Aquaseal —</option>
                              {aquasealTypes.map(a => (
                                <option key={a._id} value={a.name}>{a.name}</option>
                              ))}
                            </select>
                            {!editingPin.aquaseal_name && suggest && (
                              <button
                                type="button"
                                data-testid="annot-edit-aquaseal-suggest"
                                onClick={() => {
                                  const r = parseAquasealRange(suggest.name);
                                  const isLead = /lead/i.test((suggest.name || '') + ' ' + (suggest.materials_desc || ''));
                                  const defaults = AQUASEAL_DEFAULTS(r ? r[1] : null, isLead);
                                  setEditingPin({ ...editingPin, aquaseal_name: suggest.name, rivets_qty: defaults.rivets_qty, silicone_pct: defaults.silicone_pct });
                                }}
                                className="text-[11px] underline text-emerald-700 hover:text-emerald-900"
                              >Suggested: {suggest.name} ↗</button>
                            )}
                            {selected && (
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Rivets</label>
                                  <input
                                    data-testid="annot-edit-aquaseal-rivets"
                                    type="number" min={0} step={1}
                                    value={editingPin.rivets_qty ?? ''}
                                    onChange={e => setEditingPin({ ...editingPin, rivets_qty: e.target.value === '' ? undefined : parseInt(e.target.value, 10) || 0 })}
                                    className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm font-mono-num"
                                    placeholder="qty"
                                  />
                                </div>
                                <div>
                                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Silicone</label>
                                  <div className="flex items-center gap-1">
                                    <input
                                      data-testid="annot-edit-aquaseal-silicone"
                                      type="number" min={0} max={100} step={5}
                                      value={editingPin.silicone_pct ?? ''}
                                      onChange={e => setEditingPin({ ...editingPin, silicone_pct: e.target.value === '' ? undefined : parseFloat(e.target.value) || 0 })}
                                      className="flex-1 px-2 py-1.5 border border-slate-200 rounded text-sm font-mono-num"
                                      placeholder="% of tube"
                                    />
                                    <span className="text-[10px] text-slate-400">% tube</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}

                      {/* BACKTRAY FLASHING — long-run iron roofs only. Hidden
                          when the roof profile is clearly a tile profile. */}
                      {(() => {
                        const isTile = /tile|concrete|decramastic|clay/i.test(roofProfile || '');
                        if (isTile) return null;
                        const dia = parseInt(editingPin.outlet_diameter_mm, 10) || 0;
                        const profile = (roofProfile || '').trim();
                        const lookup = profile
                          ? backtrayRows.find(r => r.profile.toLowerCase() === profile.toLowerCase() && r.pipe_dia_mm === dia)
                          : null;
                        const known = !!lookup;
                        const widthMm = known ? lookup.width_mm : editingPin.backtray_width_mm;
                        return (
                          <div className="pt-2 mt-2 border-t border-dashed border-slate-200 space-y-2">
                            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">
                              Backtray flashing
                              <span className="text-slate-400 font-normal normal-case ml-1">
                                — {profile ? `${profile} profile` : 'set roof profile to auto-derive width'}
                              </span>
                            </label>
                            {known && (
                              <div data-testid="annot-edit-backtray-derived" className="text-[11px] bg-emerald-50 ring-1 ring-emerald-200 rounded px-2 py-1">
                                <strong>Width: {lookup.width_mm} mm</strong> ·
                                fold pattern <span className="font-mono">{lookup.fold_mm}/{lookup.width_mm - 2 * lookup.fold_mm}/{lookup.fold_mm} mm</span>
                                {lookup.notes && <span className="text-emerald-700 ml-1">· {lookup.notes}</span>}
                              </div>
                            )}
                            {!known && profile && dia > 0 && (
                              <div data-testid="annot-edit-backtray-unknown" className="space-y-1.5 text-[11px] bg-amber-50 ring-1 ring-amber-200 rounded px-2 py-1.5">
                                <p className="text-amber-800">
                                  No backtray width recorded for <strong>{profile} × {dia} mm</strong>. Measure on-site then add it below — saves time on the next job.
                                </p>
                                <BacktrayInlineAdd
                                  profile={profile}
                                  pipe_dia_mm={dia}
                                  api={api}
                                  onSaved={reloadBacktrayRows}
                                />
                              </div>
                            )}
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Length to top of roof (m)</label>
                              <input
                                data-testid="annot-edit-backtray-length"
                                type="number" min={0} step={0.1}
                                value={editingPin.backtray_length_m ?? ''}
                                onChange={e => setEditingPin({
                                  ...editingPin,
                                  backtray_length_m: e.target.value === '' ? undefined : parseFloat(e.target.value) || 0,
                                  backtray_width_mm: widthMm || undefined,
                                })}
                                className="w-32 px-2 py-1.5 border border-slate-200 rounded text-sm font-mono-num"
                                placeholder="e.g. 2.4"
                              />
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* OUTLET → optional "new downpipe install" sub-block.
                      Tick the box to flag this outlet for a new downpipe;
                      pick the storey count to drive the pipe length
                      auto-calc (3 m per storey default, can be overridden
                      per-pin for non-standard heights). The diameter of
                      the downpipe always matches the outlet's diameter so
                      we don't duplicate that field here. */}
                  {editingPin.type === 'Outlet' && (
                    <div className="pt-2 mt-2 border-t border-dashed border-slate-200">
                      <label className="flex items-start gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          data-testid="annot-edit-downpipe-install"
                          checked={!!editingPin.downpipe_install}
                          onChange={e => {
                            const checked = e.target.checked;
                            setEditingPin(p => ({
                              ...p,
                              downpipe_install: checked,
                              downpipe_stories: checked ? (p.downpipe_stories || 1) : undefined,
                              downpipe_pipe_m:  checked ? (p.downpipe_pipe_m  || undefined) : undefined,
                            }));
                          }}
                          className="mt-0.5 accent-[#15803D]"
                        />
                        <span>
                          <strong className="text-[11px] uppercase tracking-wider text-slate-700">Install new downpipe</strong>
                          <span className="block text-[10px] text-slate-400">Matches outlet shape + diameter · adds 2 bends + 500 mm junction + pipe to ground.</span>
                        </span>
                      </label>
                      {editingPin.downpipe_install && (() => {
                        // Iter229m — surface the shape + diameter the
                        // downpipe will inherit from the outlet so the
                        // installer can see at-a-glance what bend / bracket
                        // kit they need. Bends + brackets MUST match the
                        // pipe profile (a Round bracket won't clamp a
                        // Square downpipe and vice-versa).
                        const dpShape = editingPin.outlet_shape || 'Round';
                        const dpDia   = parseInt(editingPin.outlet_diameter_mm, 10)
                                      || TYPE_LOOKUP[editingPin.type]?.defaultDia
                                      || 0;
                        return (
                        <div className="mt-2 space-y-2 pl-6">
                          <div
                            data-testid="annot-edit-downpipe-match-chip"
                            className="inline-flex items-center gap-2 text-[10px] bg-emerald-50 ring-1 ring-emerald-200 text-emerald-900 rounded px-2 py-1"
                            title="Downpipe + bends + brackets all inherit the outlet's shape + diameter"
                          >
                            <span
                              style={{
                                width: 10, height: 10, background: '#EAB308',
                                borderRadius: dpShape === 'Square' ? '1px' : '50%',
                              }}
                            />
                            <span className="font-semibold uppercase tracking-wide">{dpShape}</span>
                            <span className="font-mono-num">Ø {dpDia || '?'} mm</span>
                            <span className="text-emerald-700/80 italic">— bends + brackets match</span>
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Storey count</label>
                            <div className="flex gap-2">
                              {DOWNPIPE_STORIES.map(s => (
                                <label
                                  key={s.value}
                                  data-testid={`annot-edit-downpipe-stories-${s.value}`}
                                  className={`flex-1 flex flex-col items-center px-2 py-1.5 rounded border cursor-pointer text-xs ${(editingPin.downpipe_stories || 1) === s.value ? 'bg-[#DCFCE7] border-[#15803D] text-[#14532D] font-bold' : 'bg-white border-slate-200 text-slate-600'}`}
                                >
                                  <input type="radio" name="dp-stories"
                                         checked={(editingPin.downpipe_stories || 1) === s.value}
                                         onChange={() => setEditingPin({ ...editingPin, downpipe_stories: s.value, downpipe_pipe_m: undefined })}
                                         className="hidden" />
                                  <span>{s.label}</span>
                                  <span className="text-[9px] font-mono opacity-70">{s.default_m} m</span>
                                </label>
                              ))}
                            </div>
                          </div>
                          {/* Optional per-pin length override — surfaced as
                              an editable field that defaults to the storey
                              presets but can be tweaked for unusual heights. */}
                          {(() => {
                            const stories = editingPin.downpipe_stories || 1;
                            const preset = (DOWNPIPE_STORIES.find(s => s.value === stories) || {}).default_m || 3;
                            const effective = editingPin.downpipe_pipe_m ?? preset;
                            return (
                              <div>
                                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                                  Pipe length <span className="text-slate-400 font-normal">(m) — override for tall / short runs</span>
                                </label>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number" min={0.5} max={30} step={0.5}
                                    data-testid="annot-edit-downpipe-length"
                                    value={editingPin.downpipe_pipe_m ?? ''}
                                    placeholder={`${preset} (preset)`}
                                    onChange={e => setEditingPin({ ...editingPin, downpipe_pipe_m: e.target.value === '' ? undefined : parseFloat(e.target.value) || 0 })}
                                    className="w-24 px-2 py-1.5 border border-slate-200 rounded text-sm font-mono-num"
                                  />
                                  <span className="text-[11px] text-slate-500">
                                    Will use <strong className="font-mono-num text-slate-700">{effective} m</strong> of pipe + 2 bends + 500 mm junction
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                          {/* Iter229k — Downpipe material + bracket
                              selectors. Outlets carry the downpipe's
                              material (PVC vs the iron family) so the
                              builder can quote pipe + fittings at the
                              right rate, and a bracket type so the
                              installer knows which fixing kit to
                              order alongside it. */}
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                                Downpipe material
                              </label>
                              <select
                                data-testid="annot-edit-downpipe-material"
                                value={editingPin.downpipe_material || ''}
                                onChange={e => setEditingPin({
                                  ...editingPin,
                                  downpipe_material: e.target.value || undefined,
                                })}
                                className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm bg-white"
                              >
                                <option value="">— Pick material —</option>
                                <optgroup label="PVC">
                                  {DOWNPIPE_MATERIALS.filter(m => m.family === 'pvc').map(m => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                  ))}
                                </optgroup>
                                <optgroup label="Iron">
                                  {DOWNPIPE_MATERIALS.filter(m => m.family === 'iron').map(m => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                  ))}
                                </optgroup>
                              </select>
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                                Bracket type
                              </label>
                              <select
                                data-testid="annot-edit-downpipe-bracket"
                                value={editingPin.downpipe_bracket || ''}
                                onChange={e => setEditingPin({
                                  ...editingPin,
                                  downpipe_bracket: e.target.value || undefined,
                                })}
                                className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm bg-white"
                              >
                                <option value="">— Pick bracket —</option>
                                {DOWNPIPE_BRACKETS.map(b => (
                                  <option key={b.value} value={b.value}>{b.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                        );
                      })()}
                    </div>
                  )}
                </>)}
                </>
              ) : (<>
              {editingPin.type === 'Face' ? (
                /* Iter211 — Face pin custom panel.
                 * Face m² = Face Length × Face Width. Operator picks
                 * length from existing Length / Barge pins (the runs
                 * parallel to a face's eaves) and width from existing
                 * Spouting / Ridge pins (the runs perpendicular). The
                 * area auto-fills L × W and stays read-only unless the
                 * operator explicitly hits Override (odd-shaped face). */
                (() => {
                  const lengthSources = ['Length', 'Barge'];
                  const widthSources  = ['Spouting', 'Ridge'];
                  // Build "Same as" chip data — one entry per distinct
                  // length from the allowed source types.
                  const chipsFrom = (sources) => {
                    const out = [];
                    sources.forEach(t => (lengthsByType[t] || []).forEach(e => {
                      if (e.length <= 0) return;
                      if (out.some(o => Math.abs(o.length - e.length) < 0.001)) return;
                      out.push({ ...e, type: t });
                    }));
                    return out;
                  };
                  const lengthChips = chipsFrom(lengthSources);
                  const widthChips  = chipsFrom(widthSources);
                  // Iter211 — sibling Face pins on the diagram that have any
                  // dimension recorded. One-click copy of L / W / Area
                  // so cookie-cutter faces (e.g. mirrored gable ends) can
                  // be filled in seconds. Excludes the pin we're editing.
                  const refMapForFaces = refMap || {};
                  const otherFacePins = pins
                    .filter(p => p.type === 'Face' && p.id !== editingPin.id)
                    .map(p => ({
                      id: p.id,
                      ref: refMapForFaces[p.id] || `FA?`,
                      L: parseFloat(p.face_length_m) || 0,
                      W: parseFloat(p.face_width_m)  || 0,
                      area: parseFloat(p.length_m)   || 0,
                      override: !!p.face_area_override,
                    }))
                    .filter(p => p.L > 0 || p.W > 0 || p.area > 0);
                  const copyFromFace = (src) => {
                    // Iter211 — if the source has no L/W but has an
                    // area, copy as a manual override so the operator
                    // sees the area immediately. Otherwise just mirror
                    // L/W and let the live L×W formula fill the area.
                    const hasLW = src.L > 0 && src.W > 0;
                    setEditingPin({
                      ...editingPin,
                      face_length_m: src.L || 0,
                      face_width_m:  src.W || 0,
                      length_m:      src.area || (hasLW ? +(src.L * src.W).toFixed(2) : 0),
                      face_area_override: src.override || (!hasLW && src.area > 0),
                    });
                  };
                  const faceL = parseFloat(editingPin.face_length_m) || 0;
                  const faceW = parseFloat(editingPin.face_width_m)  || 0;
                  const computed = faceL > 0 && faceW > 0
                    ? +(faceL * faceW).toFixed(2)
                    : 0;
                  const currentArea = parseFloat(editingPin.length_m) || 0;
                  const isOverride = !!editingPin.face_area_override
                    || (computed > 0 && currentArea > 0 && Math.abs(currentArea - computed) > 0.005);
                  // When L AND W are both set, length_m mirrors L × W
                  // unless override is explicitly on. The mirror is
                  // applied here in render via setEditingPin in change
                  // handlers below — we don't want a useEffect side-
                  // effect inside the dialog (keeps render pure).
                  const applyComputed = (nextL, nextW) => {
                    const nl = parseFloat(nextL) || 0;
                    const nw = parseFloat(nextW) || 0;
                    const patch = { face_length_m: nextL, face_width_m: nextW };
                    if (!isOverride && nl > 0 && nw > 0) {
                      patch.length_m = +(nl * nw).toFixed(2);
                    } else if (!isOverride && (nl === 0 || nw === 0)) {
                      // Clearing either dimension wipes the auto area
                      // (manual override is preserved by the guard).
                      patch.length_m = 0;
                    }
                    setEditingPin({ ...editingPin, ...patch });
                  };
                  return (
                    <div data-testid="annot-edit-face-panel" className="space-y-3">
                      {/* Iter211 — Copy from another Face pin. Common case:
                          a roof has several mirrored / cookie-cutter
                          faces of the same dimensions. Showing this at
                          the top of the panel saves the operator from
                          re-typing identical L/W values. */}
                      {otherFacePins.length > 0 && (
                        <div data-testid="annot-edit-face-copy-from" className="rounded p-2 bg-slate-50 ring-1 ring-slate-200">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">
                              Copy from another Face
                            </span>
                            <span className="text-[10px] text-slate-400">{otherFacePins.length} other{otherFacePins.length === 1 ? '' : 's'}</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {otherFacePins.map(f => {
                              const labelArea = f.area > 0 ? `${f.area.toFixed(2)}m²` : '—';
                              const dims = (f.L > 0 && f.W > 0)
                                ? `${f.L.toFixed(1)}×${f.W.toFixed(1)}`
                                : (f.L > 0 ? `L=${f.L.toFixed(1)}` : (f.W > 0 ? `W=${f.W.toFixed(1)}` : ''));
                              return (
                                <button
                                  key={`copy-${f.id}`}
                                  type="button"
                                  data-testid={`annot-edit-face-copy-${f.ref}`}
                                  onClick={() => copyFromFace(f)}
                                  className="px-2 py-1 text-[10px] font-mono-num font-semibold bg-white hover:bg-amber-50 text-amber-900 rounded ring-1 ring-amber-200 hover:ring-amber-400 transition-colors"
                                  title={`Copy L=${f.L.toFixed(2)}m · W=${f.W.toFixed(2)}m · Area=${labelArea} from ${f.ref}${f.override ? ' (manual override)' : ''}`}
                                >
                                  <span className="font-bold">{f.ref}</span>
                                  {dims && <span className="text-slate-500 font-normal"> · {dims}</span>}
                                  <span className="text-slate-400 font-normal"> · {labelArea}</span>
                                  {f.override && <span className="text-amber-600 ml-1" title="Manual override">✏️</span>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {/* Face Length */}
                      <div>
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                          Face Length (m)
                        </label>
                        <input
                          type="number" min={0} step={0.1}
                          data-testid="annot-edit-face-length"
                          autoFocus
                          value={editingPin.face_length_m === 0 || editingPin.face_length_m === '0' ? '' : (editingPin.face_length_m ?? '')}
                          onFocus={(e) => e.target.select()}
                          onChange={e => applyComputed(e.target.value, editingPin.face_width_m)}
                          className="w-full px-3 py-2 border border-slate-200 rounded text-sm font-mono-num"
                          placeholder="e.g. 8.4"
                        />
                        {lengthChips.length > 0 && (
                          <div data-testid="annot-edit-face-length-chips" className="mt-1.5 flex items-center gap-1 flex-wrap">
                            <span className="text-[10px] text-slate-400 mr-1">Same as:</span>
                            {lengthChips.map(e => (
                              <button
                                key={`fl-${e.type}-${e.ref}`}
                                type="button"
                                onClick={() => applyComputed(e.length, editingPin.face_width_m)}
                                className="px-1.5 py-0.5 text-[10px] font-mono-num font-semibold bg-[#FEF3C7] hover:bg-[#FDE68A] text-[#92400E] rounded ring-1 ring-[#FCD34D]"
                                title={`Copy ${e.length} m from ${e.ref} (${e.type})`}
                              >{e.ref} · {e.length}m</button>
                            ))}
                          </div>
                        )}
                        {lengthChips.length === 0 && (
                          <p className="text-[10px] text-slate-400 mt-1 italic">
                            Tip — drop a Length or Barge pin first to see "Same as" shortcuts here.
                          </p>
                        )}
                      </div>
                      {/* Face Width */}
                      <div>
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                          Face Width (m)
                        </label>
                        <input
                          type="number" min={0} step={0.1}
                          data-testid="annot-edit-face-width"
                          value={editingPin.face_width_m === 0 || editingPin.face_width_m === '0' ? '' : (editingPin.face_width_m ?? '')}
                          onFocus={(e) => e.target.select()}
                          onChange={e => applyComputed(editingPin.face_length_m, e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded text-sm font-mono-num"
                          placeholder="e.g. 5.2"
                        />
                        {widthChips.length > 0 && (
                          <div data-testid="annot-edit-face-width-chips" className="mt-1.5 flex items-center gap-1 flex-wrap">
                            <span className="text-[10px] text-slate-400 mr-1">Same as:</span>
                            {widthChips.map(e => (
                              <button
                                key={`fw-${e.type}-${e.ref}`}
                                type="button"
                                onClick={() => applyComputed(editingPin.face_length_m, e.length)}
                                className="px-1.5 py-0.5 text-[10px] font-mono-num font-semibold bg-[#DBEAFE] hover:bg-[#BFDBFE] text-[#1E40AF] rounded ring-1 ring-[#93C5FD]"
                                title={`Copy ${e.length} m from ${e.ref} (${e.type})`}
                              >{e.ref} · {e.length}m</button>
                            ))}
                          </div>
                        )}
                        {widthChips.length === 0 && (
                          <p className="text-[10px] text-slate-400 mt-1 italic">
                            Tip — drop a Spouting or Ridge pin first to see "Same as" shortcuts here.
                          </p>
                        )}
                      </div>
                      {/* Area (m²) — read-only by default, override-toggle reveals an editable input */}
                      <div className="bg-amber-50/60 ring-1 ring-amber-200 rounded p-2">
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-[10px] font-bold uppercase tracking-wider text-amber-900">
                            Area (m²)
                          </label>
                          {!isOverride ? (
                            <button
                              type="button"
                              data-testid="annot-edit-face-area-override-on"
                              onClick={() => setEditingPin({ ...editingPin, face_area_override: true })}
                              className="text-[10px] font-bold text-amber-700 hover:text-amber-900 underline"
                              title="Manually type a custom Area (e.g. triangle or odd-shaped face)"
                            >✏️ Override</button>
                          ) : (
                            <button
                              type="button"
                              data-testid="annot-edit-face-area-reset"
                              onClick={() => {
                                const nl = parseFloat(editingPin.face_length_m) || 0;
                                const nw = parseFloat(editingPin.face_width_m)  || 0;
                                setEditingPin({
                                  ...editingPin,
                                  face_area_override: false,
                                  length_m: (nl > 0 && nw > 0) ? +(nl * nw).toFixed(2) : 0,
                                });
                              }}
                              className="text-[10px] font-bold text-amber-700 hover:text-amber-900 underline"
                              title="Discard manual area, return to Length × Width"
                            >↻ Reset to L × W</button>
                          )}
                        </div>
                        {isOverride ? (
                          <>
                            <input
                              type="number" min={0} step={0.1}
                              data-testid="annot-edit-face-area-manual"
                              value={editingPin.length_m === 0 || editingPin.length_m === '0' ? '' : (editingPin.length_m ?? '')}
                              onFocus={(e) => e.target.select()}
                              onChange={e => setEditingPin({ ...editingPin, length_m: e.target.value, face_area_override: true })}
                              className="w-full px-3 py-2 border border-amber-300 rounded text-sm font-mono-num bg-white"
                              placeholder="e.g. 18.7"
                            />
                            {/* Phase 1 (2026-02) — divergence hint when L×W ≠
                                manual m² beyond 2 %. Helps operators spot
                                typos (e.g. transposed digits) without
                                blocking irregular-face entries. */}
                            {computed > 0 && currentArea > 0 && (() => {
                              const pctDiff = Math.abs(currentArea - computed) / computed;
                              if (pctDiff <= 0.02) return null;
                              return (
                                <div data-testid="annot-edit-face-area-divergence" className="mt-1.5 text-[10px] text-amber-800 bg-amber-100/70 ring-1 ring-amber-200 rounded px-2 py-1">
                                  <span className="font-semibold">Heads up:</span> L × W would be <span className="font-mono-num font-semibold">{computed.toFixed(2)} m²</span> — that's {(pctDiff * 100).toFixed(0)}% off your manual entry. Keep it if the face is irregular; correct L or W if it's a typo.
                                </div>
                              );
                            })()}
                          </>
                        ) : (
                          <div
                            data-testid="annot-edit-face-area-computed"
                            className="px-3 py-2 bg-white rounded border border-amber-200 font-mono-num text-base font-bold text-amber-900"
                          >
                            {computed > 0 ? (
                              <>
                                {faceL.toFixed(2)} <span className="text-slate-400 font-normal">×</span> {faceW.toFixed(2)} <span className="text-slate-400 font-normal">=</span> {computed.toFixed(2)} m²
                              </>
                            ) : currentArea > 0 ? (
                              /* Iter211 — handles the copy-from-area-only case:
                                 a sibling Face pin had a saved area but no L/W,
                                 so we still show the operator what's on this
                                 pin (otherwise the placeholder is misleading). */
                              <>{currentArea.toFixed(2)} m²</>
                            ) : (
                              <span className="text-slate-400 font-normal italic text-xs">Enter Length and Width above…</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()
              ) : editingPin.type === 'Flashing' ? (
                /* Iter229o — Flashing pins handle Length + Label inside
                   the custom 2-col Flashing block above; skip the
                   generic Length input here so we don't duplicate. */
                null
              ) : PITCHED_PIN_TYPES.includes(editingPin.type) ? (
                <PlanPitchedFields
                  kind={editingPin.type} pitchDeg={pitchDeg} testId="annot-edit-length"
                  plan={editingPin.plan_length_m} pitched={editingPin.length_m} source={editingPin.length_source}
                  autoFocusPitched
                  onChange={({ plan, pitched, source }) => setEditingPin({
                    ...editingPin, length_m: pitched, plan_length_m: plan, length_source: source, pitch_deg_used: pitchDeg,
                  })}
                />
              ) : (
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                  {unitForPin(editingPin) === 'm²' ? 'Area (m²)' : 'Length (m)'}
                </label>
                <input
                  type="number" min={0} step={0.1}
                  data-testid="annot-edit-length"
                  autoFocus
                  value={editingPin.length_m === 0 || editingPin.length_m === '0' ? '' : editingPin.length_m}
                  onFocus={(e) => e.target.select()}
                  onChange={e => setEditingPin({ ...editingPin, length_m: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded text-sm font-mono-num"
                  placeholder={unitForPin(editingPin) === 'm²' ? 'e.g. 24.5' : 'e.g. 5.2'}
                />
                {/* Copy-from-existing chips — surface every distinct length
                    already on the diagram for THIS pin's type, plus any
                    other type. Often a Valley is the same as 2 small
                    Ridges, so we show same-type lengths first then
                    cross-type.
                    Iter229l — Flashing pins are special-cased: a
                    flashing's length only ever reuses a Barge run or a
                    Length (manual) measurement (e.g. a Backtray that
                    spans the same distance as a Barge sheet). Ridges /
                    Valleys / Spoutings are filtered out so the chip
                    rail isn't polluted with irrelevant numbers. */}
                {(() => {
                  if (editingPin.type === 'Flashing') {
                    const flashingSources = ['Barge', 'Length'];
                    const entries = flashingSources
                      .flatMap(k => (lengthsByType[k] || []).map(e => ({ ...e, type: k })))
                      .filter(e => e.length > 0)
                      // De-dupe identical lengths so two 4.54 m barges
                      // collapse to one chip rather than four.
                      .filter((e, i, arr) => arr.findIndex(x => Math.abs(x.length - e.length) < 0.001) === i);
                    if (entries.length === 0) return null;
                    return (
                      <div data-testid="annot-edit-copy-chips" className="mt-1.5 flex items-center gap-1 flex-wrap">
                        <span className="text-[10px] text-slate-400 mr-1">Same as:</span>
                        {entries.map(e => (
                          <button
                            key={`f-${e.ref}-${e.type}`}
                            type="button"
                            onClick={() => setEditingPin({ ...editingPin, length_m: e.length })}
                            className="px-1.5 py-0.5 text-[10px] font-mono-num font-semibold bg-[#FBE9D8] hover:bg-[#F5B884] text-[#A04000] rounded ring-1 ring-[#F5B884]"
                            title={`Copy ${e.length} m from ${e.ref} (${e.type})`}
                          >{e.ref} · {e.length}m</button>
                        ))}
                      </div>
                    );
                  }
                  const sameType = (lengthsByType[editingPin.type] || []).filter(e => e.length > 0);
                  const others = Object.entries(lengthsByType)
                    .filter(([k]) => k !== editingPin.type)
                    .flatMap(([k, arr]) => arr.map(e => ({ ...e, type: k })))
                    .filter(e => e.length > 0)
                    .filter(e => !sameType.some(s => Math.abs(s.length - e.length) < 0.001));
                  if (sameType.length === 0 && others.length === 0) return null;
                  return (
                    <div data-testid="annot-edit-copy-chips" className="mt-1.5 flex items-center gap-1 flex-wrap">
                      <span className="text-[10px] text-slate-400 mr-1">Same as:</span>
                      {sameType.map(e => (
                        <button
                          key={`s-${e.ref}`}
                          type="button"
                          onClick={() => setEditingPin({ ...editingPin, length_m: e.length })}
                          className="px-1.5 py-0.5 text-[10px] font-mono-num font-semibold bg-[#FBE9D8] hover:bg-[#F5B884] text-[#A04000] rounded ring-1 ring-[#F5B884]"
                          title={`Copy ${e.length} m from ${e.ref}`}
                        >{e.ref} · {e.length}m</button>
                      ))}
                      {others.length > 0 && sameType.length > 0 && <span className="text-slate-300 mx-1">·</span>}
                      {others.map(e => (
                        <button
                          key={`o-${e.ref}-${e.type}`}
                          type="button"
                          onClick={() => setEditingPin({ ...editingPin, length_m: e.length })}
                          className="px-1.5 py-0.5 text-[10px] font-mono-num font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded ring-1 ring-slate-200"
                          title={`Copy ${e.length} m from ${e.ref} (${e.type})`}
                        >{e.ref} · {e.length}m</button>
                      ))}
                    </div>
                  );
                })()}
              </div>
              )}
              {/* RIDGE → bidirectional Ridge-Cap calculator (tile/concrete/
                  clay) OR pieces-with-lap calculator (iron — Iter228).
                  - Tile / Concrete / Clay: pick a cap type, enter EITHER
                    length OR cap count; the other auto-fills. Approximate
                    length warning surfaces when count was the input.
                  - Iron: pick RoundTop Std / Wide, enter ridge length, and
                    the dialog auto-derives how many sticks to order using
                    the 9 m mill max + 200 mm per-piece lap rule. The "cap
                    count" input is hidden — iron isn't capped, it's cut. */}
              {isRidging(editingPin.type) && (() => {
                const cap = RIDGE_CAP_LOOKUP[editingPin.ridge_cap_type];
                const isIron = cap?.klass === 'iron';
                const fieldLabel = isIron ? 'Ridge type' : 'Ridge Cap type';
                const skipLabel  = isIron ? '— no ridge (skip) —' : '— no cap (skip) —';
                return (
                <div className="pt-2 mt-2 border-t border-dashed border-slate-200 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <label
                      data-testid="annot-edit-ridge-field-label"
                      className="text-[10px] font-bold uppercase tracking-wider text-slate-500"
                    >{fieldLabel}</label>
                    <span
                      data-testid="annot-edit-ridgecap-roof-label"
                      className="text-[9px] text-slate-400"
                      title={`Options narrowed by roof type: ${roofType || 'unknown'}`}
                    >{ridgeRoofLabel(roofType)}</span>
                  </div>
                  <select
                    data-testid="annot-edit-ridgecap-type"
                    value={editingPin.ridge_cap_type || ''}
                    onChange={e => setEditingPin({ ...editingPin, ridge_cap_type: e.target.value || undefined })}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm bg-white"
                  >
                    <option value="">{skipLabel}</option>
                    {getRidgeOptionsForRoof(roofType).map(c => (
                      <option key={c.name} value={c.name}>
                        {c.name}{c.klass === 'iron' ? '' : ` (${c.length_mm} mm)`}
                      </option>
                    ))}
                    {/* Iter227 — preserve a legacy cap value (saved before
                        the roof-type cascade existed, or imported from a
                        different roof type) so the operator's prior pick
                        isn't silently dropped. Rendered as a disabled-style
                        option labelled "(legacy)". Operator can leave it
                        OR click the ↻ reset chip below to pick the roof-
                        appropriate default. */}
                    {editingPin.ridge_cap_type &&
                     !getRidgeOptionsForRoof(roofType).some(c => c.name === editingPin.ridge_cap_type) && (
                      <option value={editingPin.ridge_cap_type}>
                        {editingPin.ridge_cap_type} (legacy · {ridgeRoofLabel(roofType)} mismatch)
                      </option>
                    )}
                  </select>
                  {/* Iter227 — Stale cap reset chip. Surfaces when the
                      pin's saved cap_type doesn't fit the current roof type
                      (e.g. a pin imported with "Standard Concrete" on an
                      Iron roof). One click swaps to the roof default. */}
                  {editingPin.ridge_cap_type &&
                   !getRidgeOptionsForRoof(roofType).some(c => c.name === editingPin.ridge_cap_type) && (() => {
                    const fallback = defaultRidgeCapForRoof(roofType);
                    if (!fallback || fallback === editingPin.ridge_cap_type) return null;
                    return (
                      <button
                        type="button"
                        data-testid="annot-edit-ridgecap-reset"
                        onClick={() => setEditingPin({ ...editingPin, ridge_cap_type: fallback })}
                        className="text-[10px] font-semibold px-2 py-1 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded ring-1 ring-amber-200 inline-flex items-center gap-1"
                        title={`Replace stale cap value with "${fallback}" (recommended for ${ridgeRoofLabel(roofType)})`}
                      >
                        ↻ Reset to {fallback}
                      </button>
                    );
                  })()}
                  {editingPin.ridge_cap_type && (() => {
                    const capM = (cap?.length_mm || 420) / 1000;
                    const lenNum  = parseFloat(editingPin.length_m) || 0;
                    const cntNum  = parseInt(editingPin.ridge_cap_count, 10) || 0;
                    const derivedCaps = lenNum > 0 ? Math.ceil(lenNum / capM) : 0;
                    const derivedLen  = cntNum > 0 ? +(cntNum * capM).toFixed(2) : 0;
                    // Warning: length came from cap count (operator typed a
                    // count, so the metres are derived/approximate).
                    const lengthFromCount = cntNum > 0;
                    // Iron — compute the per-piece breakdown with the 9 m
                    // mill max + 200 mm lap rule. Display-only (not stored
                    // on the pin); recomputed on every length change.
                    const iron = isIron ? calcIronRidgePieces(lenNum) : null;
                    return (
                      <>
                        <div className={isIron ? '' : 'grid grid-cols-2 gap-2'}>
                          <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Ridge length (m)</label>
                            <div className="flex items-center gap-1">
                              <input
                                type="number" min={0} step={0.1}
                                data-testid="annot-edit-ridgecap-length"
                                value={editingPin.length_m === 0 || editingPin.length_m === '0' ? '' : (editingPin.length_m || '')}
                                onChange={e => setEditingPin({
                                  ...editingPin,
                                  length_m: e.target.value,
                                  // entering a length REPLACES any previously-derived cap count
                                  ridge_cap_count: undefined,
                                })}
                                className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm font-mono-num"
                                placeholder="e.g. 6.2"
                              />
                            </div>
                          </div>
                          {/* Cap count input — tile/concrete/clay only.
                              Iron uses the derived pieces readout below. */}
                          {!isIron && (
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">OR Cap count</label>
                              <div className="flex items-center gap-1">
                                <input
                                  type="number" min={0} step={1}
                                  data-testid="annot-edit-ridgecap-count"
                                  value={editingPin.ridge_cap_count ?? ''}
                                  onChange={e => {
                                    const v = e.target.value === '' ? undefined : (parseInt(e.target.value, 10) || 0);
                                    setEditingPin({
                                      ...editingPin,
                                      ridge_cap_count: v,
                                      // entering a count derives an approximate length
                                      length_m: v ? +(v * capM).toFixed(2) : editingPin.length_m,
                                    });
                                  }}
                                  className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm font-mono-num"
                                  placeholder="e.g. 15"
                                />
                                <span className="text-[10px] text-slate-400 whitespace-nowrap">caps</span>
                              </div>
                            </div>
                          )}
                        </div>
                        {/* Live derivation readout */}
                        {isIron ? (
                          <div
                            data-testid="annot-edit-ridge-iron-readout"
                            className="text-[11px] text-slate-700 bg-slate-50 ring-1 ring-slate-200 rounded px-2 py-1.5 space-y-1"
                          >
                            <div className="flex items-center justify-between gap-2 text-slate-500 text-[10px]">
                              <span>Max <strong className="font-mono-num text-slate-700">{IRON_RIDGE_MAX_M.toFixed(1)} m</strong> per length · <strong className="font-mono-num text-slate-700">{(IRON_RIDGE_LAP_M * 1000).toFixed(0)} mm</strong> lap allowance per piece</span>
                            </div>
                            {lenNum > 0 && iron && iron.pieces > 0 ? (
                              <div className="flex items-center justify-between gap-2">
                                <span data-testid="annot-edit-ridge-iron-formula" className="font-mono-num">
                                  {iron.pieces === 1 ? (
                                    <>1 piece × {iron.stock_each.toFixed(3)} m <span className="text-slate-400">(no lap)</span></>
                                  ) : (
                                    <>{iron.pieces} pieces × <strong>{iron.stock_each.toFixed(3)} m</strong> <span className="text-slate-400">({iron.per_piece.toFixed(3)} m + {IRON_RIDGE_LAP_M.toFixed(2)} m lap)</span></>
                                  )}
                                </span>
                                <span className="text-emerald-700 font-semibold font-mono-num">
                                  ≈ {iron.total_stock.toFixed(2)} m stock
                                </span>
                              </div>
                            ) : (
                              <div className="text-slate-400 italic">enter ridge length to derive pieces</div>
                            )}
                          </div>
                        ) : (
                          <div data-testid="annot-edit-ridgecap-readout" className="text-[11px] text-slate-600 flex items-center justify-between bg-slate-50 rounded px-2 py-1">
                            <span>
                              @ <strong className="font-mono-num">{cap?.length_mm} mm</strong>/cap
                            </span>
                            <span className="font-mono-num">
                              {lenNum > 0 && `≈ ${derivedCaps} cap${derivedCaps === 1 ? '' : 's'}`}
                              {lenNum === 0 && cntNum > 0 && `≈ ${derivedLen} m`}
                              {lenNum === 0 && cntNum === 0 && <span className="text-slate-300">— enter length or count</span>}
                            </span>
                          </div>
                        )}
                        {/* Std 420mm reference — concrete-tile rate tables
                            traditionally cost by 420 mm cap units even when
                            larger caps are installed. Only show when the
                            selected cap differs from the std size AND a
                            length is known. Iter227 — gated to concrete /
                            clay options only (Iron / Tin Tile use per-metre
                            pricing so the std-420 ref is meaningless). */}
                        {(cap?.klass === 'concrete' || cap?.klass === 'clay') && (cap?.length_mm !== STD_RIDGE_CAP_MM) && (lenNum > 0 || (cntNum > 0 && capM > 0)) && (() => {
                          const lenForStd = lenNum > 0 ? lenNum : (cntNum * capM);
                          const stdCount = Math.ceil(lenForStd / (STD_RIDGE_CAP_MM / 1000));
                          return (
                            <div data-testid="annot-edit-ridgecap-std420" className="text-[11px] text-indigo-700 flex items-center justify-between bg-indigo-50 ring-1 ring-indigo-200 rounded px-2 py-1">
                              <span>Std quoting unit @ <strong className="font-mono-num">{STD_RIDGE_CAP_MM} mm</strong></span>
                              <span className="font-mono-num">≈ {stdCount} cap{stdCount === 1 ? '' : 's'} ({lenForStd.toFixed(2)} m)</span>
                            </div>
                          );
                        })()}
                        {lengthFromCount && (cap?.klass === 'concrete' || cap?.klass === 'clay') && (
                          <div data-testid="annot-edit-ridgecap-verify-warning" className="text-[11px] text-amber-800 bg-amber-50 ring-1 ring-amber-200 rounded px-2 py-1.5 flex gap-1.5 items-start">
                            <span className="text-amber-600">⚠</span>
                            <span><strong>Verify on-site:</strong> length derived from cap count is approximate. Sufficient for concrete-tile quoting but confirm metres before ordering materials.</span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                );
              })()}
              {/* Iter229o — Flashing pins render Label inside the
                  custom 2-col Flashing block above, so this generic
                  Label section skips itself for Flashing. */}
              {editingPin.type !== 'Flashing' && (
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Label</label>
                {/* Dropdown of preset sides + an "Other…" path that flips to a
                    free-text input so unique callouts still flow. */}
                <select
                  data-testid="annot-edit-label-preset"
                  value={LABEL_PRESETS.includes(editingPin.label) ? editingPin.label : (editingPin.label ? '__OTHER__' : '')}
                  onChange={e => {
                    const v = e.target.value;
                    if (v === '__OTHER__') {
                      setEditingPin({ ...editingPin, label: editingPin.label && !LABEL_PRESETS.includes(editingPin.label) ? editingPin.label : ' ' });
                    } else {
                      setEditingPin({ ...editingPin, label: v });
                    }
                  }}
                  className="w-full px-3 py-2 border border-slate-200 rounded text-sm bg-white mb-1.5"
                >
                  <option value="">— Pick a side —</option>
                  {LABEL_PRESETS.map(p => <option key={p} value={p}>{p}</option>)}
                  <option value="__OTHER__">Other / custom…</option>
                </select>
                {(editingPin.label && !LABEL_PRESETS.includes(editingPin.label)) && (
                  <input
                    data-testid="annot-edit-label"
                    value={editingPin.label.trim() === '' ? '' : editingPin.label}
                    onChange={e => setEditingPin({ ...editingPin, label: e.target.value })}
                    onFocus={(e) => e.target.select()}
                    className="w-full px-3 py-2 border border-slate-200 rounded text-sm"
                    placeholder='e.g. "Above bay window"'
                    autoFocus
                  />
                )}
              </div>
              )}
              {/* Iter211 — LinkedFlashingBlock removed from length/area
                  pin edits (Ridge / Hip / Spouting / Length / Face) per
                  operator request. Flashing template linkage still
                  lives on POSITION pins (Pipe / Flue / Outlet) where
                  it's actually used.
                  Iter229 / Iter229d — restored for Valley AND Barge
                  pins so the operator can pick a preset template
                  (5Fold Valley, Starter Barge TRib, etc.) and have it
                  cascade to the Components form + supplier order PDF.
                  Iter229j — extended to Flashing pins too. The pin's
                  [Flashing Kind] (`flashing_type`) cascades into the
                  picker as a hard category filter via `hardCategoryFilter`
                  in LinkedFlashingBlock, so e.g. a Flashing pin with
                  Kind=Apron only sees Apron-category templates. */}
              {(editingPin.type === 'Valley' || editingPin.type === 'Barge' || editingPin.type === 'Flashing') && (
                <LinkedFlashingBlock
                  api={api}
                  pin={editingPin}
                  setPin={setEditingPin}
                  roofType={roofType}
                  roofProfile={roofProfile}
                />
              )}
              </>)}
            </div>
            <footer className="px-4 py-3 border-t border-slate-200 flex items-center justify-between gap-2 bg-slate-50">
              <button
                data-testid="annot-edit-delete"
                onClick={() => handleEditDelete(editingPin.id)}
                className="flex items-center gap-1 text-xs px-3 py-1.5 border border-red-200 text-red-600 rounded hover:bg-red-50"
              ><Trash2 size={11} /> Delete</button>
              <div className="flex gap-2">
                <button
                  data-testid="annot-edit-cancel"
                  onClick={() => {
                    // For a freshly-dropped pin that the user is bailing out of,
                    // remove it from the diagram + backend (otherwise an empty
                    // length=0 pin would sit on the diagram forever).
                    if (editingPin._isFresh && (!parseFloat(editingPin.length_m) && !editingPin.label)) {
                      handleEditDelete(editingPin.id);
                    } else {
                      setEditingPin(null);
                    }
                  }}
                  className="text-xs px-3 py-1.5 border border-slate-200 rounded"
                >Cancel</button>
                <button
                  data-testid="annot-edit-save"
                  disabled={!!editingPin._dimensions_required && !editingPin.dimensions_confirmed}
                  onClick={() => handleEditSave({
                    ...editingPin,
                    length_m: parseFloat(editingPin.length_m) || 0,
                    // Iter211 — persist Face dimensions as numbers so the
                    // running roof-m² rollup and "Same as" chips read them
                    // back cleanly.
                    ...(editingPin.type === 'Face' ? {
                      face_length_m: parseFloat(editingPin.face_length_m) || 0,
                      face_width_m:  parseFloat(editingPin.face_width_m)  || 0,
                      // Phase 1 (2026-02) — persist the canonical R3
                      // alias `sheet_length_m` (same value as
                      // face_length_m for full-height faces). Backend
                      // reads either name via `_appraisal_diagram_dims`.
                      sheet_length_m: parseFloat(editingPin.face_length_m) || 0,
                      // Persist the override flag so the dialog re-opens
                      // in the same mode + the Translator knows this
                      // pin's m² is operator-authoritative.
                      face_area_override: !!editingPin.face_area_override,
                    } : {}),
                    label: (editingPin.label || '').trim(),
                    _isFresh: undefined,
                    _dimensions_required: undefined,
                  })}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 bg-[#D35400] hover:bg-[#A04000] text-white rounded font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                ><Save size={11} /> Save pin</button>
              </div>
            </footer>
          </div>
        </div>
      )}
      {/* Iter229o — Flashing Description admin pop-out. Mounts as a
          modal overlay (`fixed inset-0`) so it sits above the pin
          edit dialog without unmounting it — closing the admin pop
          returns the operator to their in-progress pin. */}
      {showDescAdmin && (
        <FlashingDescriptionAdminDialog
          api={api}
          onClose={() => setShowDescAdmin(false)}
          onChanged={reloadFlashingDescriptions}
        />
      )}
    </div>
  );
}




// Iter230b — ValleyFoldEditor + FoldCrossSectionPreview have been
// extracted to ./annotator/FoldEditor.js. Re-exported here so existing
// consumers (PenetrationEditPanel.js imports ValleyFoldEditor from
// './RoofDiagramAnnotator') continue to work without changes.
export { ValleyFoldEditor, FoldCrossSectionPreview } from './annotator/FoldEditor';
